<template>
  <div class="helper-tool" :class="{ 'helper-tool--article': Boolean(activeGuide) }">
    <header class="helper-tool__header">
      <div class="helper-tool__header-glow helper-tool__header-glow--one" aria-hidden="true"></div>
      <div class="helper-tool__header-glow helper-tool__header-glow--two" aria-hidden="true"></div>

      <div class="helper-tool__header-content">
        <div class="helper-tool__eyebrow">
          <v-icon size="18" color="#7ee5a6">mdi-lifebuoy</v-icon>
          <span>Mitarbeiter-Hilfe</span>
        </div>

        <div class="helper-tool__header-row">
          <div>
            <h1 ref="pageTitle" class="helper-tool__title" tabindex="-1">
              {{ activeGuide ? activeGuide.title : "Hilfe & Anleitungen" }}
            </h1>
            <p class="helper-tool__subtitle">
              {{ activeGuide ? activeGuide.summary : "Finde schnell die richtige Funktion und erledige deinen nächsten Schritt direkt in Roody." }}
            </p>
          </div>

          <div class="helper-tool__header-actions">
            <v-btn v-if="activeGuide" class="helper-tool__header-button" outlined color="white" @click="goToOverview">
              <v-icon left>mdi-view-grid-outline</v-icon>
              Alle Anleitungen
            </v-btn>
            <v-btn class="helper-tool__header-button" text color="white" @click="goBackToDashboard">
              <v-icon left>mdi-arrow-left</v-icon>
              Dashboard
            </v-btn>
          </div>
        </div>
      </div>
    </header>

    <main class="helper-tool__main">
      <v-card v-if="!hasAnyHelpAccess" class="helper-tool__state-card" flat>
        <div class="helper-tool__state-icon">
          <v-icon size="34" color="#7ee5a6">mdi-shield-account-outline</v-icon>
        </div>
        <h2>Für dieses Profil sind keine Hilfeinhalte verfügbar</h2>
        <p>Das HelperTool zeigt nur Funktionen, für die dein aktuelles Profil eine Freigabe besitzt.</p>
        <v-btn color="primary" depressed @click="goBackToDashboard">Zum Dashboard</v-btn>
      </v-card>

      <v-card v-else-if="isDeniedGuide" class="helper-tool__state-card" flat>
        <div class="helper-tool__state-icon">
          <v-icon size="34" color="#f5c66a">mdi-lock-outline</v-icon>
        </div>
        <h2>Diese Anleitung ist für dein Profil nicht verfügbar</h2>
        <p>In der Übersicht findest du alle Inhalte, die zu deinen aktuellen Funktionsrechten passen.</p>
        <v-btn color="primary" depressed @click="goToOverview">Erlaubte Anleitungen anzeigen</v-btn>
      </v-card>

      <v-card v-else-if="isMissingGuide" class="helper-tool__state-card" flat>
        <div class="helper-tool__state-icon">
          <v-icon size="34" color="#7ee5a6">mdi-file-search-outline</v-icon>
        </div>
        <h2>Anleitung nicht gefunden</h2>
        <p>Der Link ist möglicherweise veraltet. Über die Suche findest du die aktuelle Anleitung.</p>
        <v-btn color="primary" depressed @click="goToOverview">Zur Suche</v-btn>
      </v-card>

      <article v-else-if="activeGuide" class="helper-tool__article">
        <nav class="helper-tool__breadcrumb" aria-label="Breadcrumb">
          <button type="button" @click="goToOverview">Hilfe</button>
          <v-icon size="16" color="#7c8580">mdi-chevron-right</v-icon>
          <span>{{ categoryLabel(activeGuide.category) }}</span>
        </nav>

        <section class="helper-tool__article-layout">
          <div class="helper-tool__article-body">
            <div class="helper-tool__meta-row">
              <span class="helper-tool__meta-pill"><v-icon size="16">mdi-clock-outline</v-icon>{{ activeGuide.duration }}</span>
              <span class="helper-tool__meta-pill"><v-icon size="16">mdi-source-branch-check</v-icon>Code-Stand {{ activeGuide.lastReviewed }}</span>
              <span class="helper-tool__meta-pill"><v-icon size="16">mdi-account-lock-outline</v-icon>Interne Anleitung</span>
            </div>

            <section class="helper-tool__where-card" aria-labelledby="where-title">
              <div class="helper-tool__section-icon"><v-icon color="#7ee5a6">mdi-map-marker-path</v-icon></div>
              <div>
                <p id="where-title" class="helper-tool__section-label">Wo finde ich das?</p>
                <div class="helper-tool__path" aria-label="Navigationspfad">
                  <template v-for="(part, index) in activeGuide.where">
                    <span :key="`where-${index}`">{{ part }}</span>
                    <v-icon v-if="index < activeGuide.where.length - 1" :key="`where-icon-${index}`" size="15" color="#7c8580">
                      mdi-chevron-right
                    </v-icon>
                  </template>
                </div>
              </div>
            </section>

            <section v-if="activeGuide.prerequisites.length" class="helper-tool__content-section" aria-labelledby="prerequisites-title">
              <p id="prerequisites-title" class="helper-tool__section-kicker">Bevor du startest</p>
              <ul class="helper-tool__check-list">
                <li v-for="item in activeGuide.prerequisites" :key="item">
                  <v-icon size="18" color="#7ee5a6">mdi-check-circle-outline</v-icon>
                  <span>{{ item }}</span>
                </li>
              </ul>
            </section>

            <section class="helper-tool__content-section" aria-labelledby="steps-title">
              <div class="helper-tool__section-heading">
                <div>
                  <p class="helper-tool__section-kicker">Schritt für Schritt</p>
                  <h2 id="steps-title">So gehst du vor</h2>
                </div>
                <span class="helper-tool__step-count">{{ visibleSteps(activeGuide).length }} Schritte</span>
              </div>

              <ol class="helper-tool__steps">
                <li
                  v-for="(step, index) in visibleSteps(activeGuide)"
                  :key="`${activeGuide.slug}-step-${index}`"
                  :id="step.id || null"
                  :tabindex="step.id ? -1 : null">
                  <div class="helper-tool__step-number">{{ index + 1 }}</div>
                  <div class="helper-tool__step-copy">
                    <h3>{{ step.title }}</h3>
                    <p>{{ step.text }}</p>
                    <div v-if="step.tip" class="helper-tool__tip">
                      <v-icon size="17" color="#7ee5a6">mdi-lightbulb-on-outline</v-icon>
                      <span>{{ step.tip }}</span>
                    </div>
                  </div>
                </li>
              </ol>
            </section>

            <section v-if="visibleVisuals(activeGuide).length" class="helper-tool__visual-section" aria-labelledby="visual-preview-title">
              <div class="helper-tool__visual-heading">
                <div>
                  <p class="helper-tool__section-kicker">Visueller Ablauf</p>
                  <h2 id="visual-preview-title">So sieht es in der App aus</h2>
                  <p>Statische Nachbildung mit Beispieldaten – zum Einprägen, nicht zum Ausführen.</p>
                </div>
                <span class="helper-tool__visual-safety"
                  ><v-icon size="16" color="#7ee5a6">mdi-shield-check-outline</v-icon> Sichere Vorschau · keine Aktion</span
                >
              </div>

              <div class="helper-tool__visual-track" :aria-label="`${visibleVisuals(activeGuide).length} visuelle Schritte`">
                <article
                  v-for="(screen, screenIndex) in visibleVisuals(activeGuide)"
                  :key="`${activeGuide.slug}-visual-${screenIndex}`"
                  class="helper-tool__mock-device"
                  :class="[
                    `helper-tool__mock-device--${screen.device || 'phone'}`,
                    `helper-tool__mock-device--${screen.type || 'overview'}`,
                    { 'helper-tool__mock-device--didactic': screen.fidelity === 'didactic' },
                  ]">
                  <div class="helper-tool__mock-browserbar" aria-hidden="true">
                    <div class="helper-tool__mock-dots"><span></span><span></span><span></span></div>
                    <span class="helper-tool__mock-browser-title">{{ screen.fidelity === "didactic" ? "Orientierung" : "roody Vorschau" }}</span>
                    <span class="helper-tool__mock-step">{{ screenIndex + 1 }}/{{ visibleVisuals(activeGuide).length }}</span>
                  </div>

                  <div class="helper-tool__mock-app">
                    <div v-if="screen.path && screen.path.length" class="helper-tool__mock-path">
                      <template v-for="(part, pathIndex) in screen.path">
                        <span :key="`visual-path-${screenIndex}-${pathIndex}`">{{ part }}</span>
                        <v-icon v-if="pathIndex < screen.path.length - 1" :key="`visual-path-icon-${screenIndex}-${pathIndex}`" size="13">
                          mdi-chevron-right
                        </v-icon>
                      </template>
                    </div>

                    <div class="helper-tool__mock-titlebar">
                      <div class="helper-tool__mock-title-icon">
                        <v-icon size="22" color="#7ee5a6">{{ screen.icon }}</v-icon>
                      </div>
                      <div>
                        <span>{{ screen.eyebrow }}</span>
                        <strong>{{ screen.title }}</strong>
                        <small v-if="screen.subtitle">{{ screen.subtitle }}</small>
                      </div>
                      <span
                        v-if="screen.status"
                        class="helper-tool__mock-status"
                        :class="`helper-tool__mock-status--${screen.status.tone || 'neutral'}`">
                        {{ screen.status.label }}
                      </span>
                    </div>

                    <div v-if="screen.tabs.length" class="helper-tool__mock-tabs">
                      <span v-for="tab in screen.tabs" :key="tab.label" :class="{ 'helper-tool__mock-tab--active': tab.active }">
                        <v-icon v-if="tab.icon" size="15">{{ tab.icon }}</v-icon>
                        {{ tab.label }}
                        <small v-if="tab.count !== undefined">{{ tab.count }}</small>
                      </span>
                    </div>

                    <div v-if="screen.search" class="helper-tool__mock-search" :class="{ 'helper-tool__mock-spotlight': screen.search.spotlight }">
                      <v-icon size="18" color="#8e9992">mdi-magnify</v-icon>
                      <span :class="{ 'helper-tool__mock-placeholder': !screen.search.value }">{{
                        screen.search.value || screen.search.placeholder
                      }}</span>
                      <v-icon v-if="screen.search.value" size="16" color="#8e9992">mdi-close-circle</v-icon>
                      <span v-if="screen.search.spotlight" class="helper-tool__mock-pointer"
                        ><v-icon size="14">mdi-cursor-default-click</v-icon>{{ screen.search.hint || "Hier suchen" }}</span
                      >
                    </div>

                    <div v-if="screen.notice" class="helper-tool__mock-notice" :class="`helper-tool__mock-notice--${screen.notice.tone || 'info'}`">
                      <v-icon size="18">{{ screen.notice.icon || "mdi-information-outline" }}</v-icon>
                      <div>
                        <strong v-if="screen.notice.title">{{ screen.notice.title }}</strong
                        ><span>{{ screen.notice.text }}</span>
                      </div>
                    </div>

                    <div v-if="screen.progress" class="helper-tool__mock-progress">
                      <div>
                        <strong>{{ screen.progress.label }}</strong
                        ><span>{{ screen.progress.caption }}</span>
                      </div>
                      <div class="helper-tool__mock-progress-track"><span :style="{ width: `${screen.progress.value}%` }"></span></div>
                      <small>{{ screen.progress.value }}%</small>
                    </div>

                    <div v-if="screen.metrics.length" class="helper-tool__mock-metrics">
                      <div
                        v-for="metric in screen.metrics"
                        :key="`${metric.label}-${metric.value}`"
                        :class="`helper-tool__mock-metric--${metric.tone || 'neutral'}`">
                        <span>{{ metric.label }}</span>
                        <strong>{{ metric.value }}</strong>
                        <small v-if="metric.detail">{{ metric.detail }}</small>
                      </div>
                    </div>

                    <div v-if="screen.sectionLabel" class="helper-tool__mock-section-label">
                      <span>{{ screen.sectionLabel }}</span>
                      <small v-if="screen.sectionMeta">{{ screen.sectionMeta }}</small>
                    </div>

                    <div v-if="screen.fields.length" class="helper-tool__mock-fields">
                      <div
                        v-for="field in screen.fields"
                        :key="field.label"
                        class="helper-tool__mock-field"
                        :class="{ 'helper-tool__mock-field--wide': field.wide, 'helper-tool__mock-spotlight': field.spotlight }">
                        <span>{{ field.label }}</span>
                        <div>
                          <v-icon v-if="field.icon" size="17" color="#8e9992">{{ field.icon }}</v-icon>
                          <strong :class="{ 'helper-tool__mock-placeholder': !field.value }">{{ field.value || field.placeholder }}</strong>
                          <v-icon v-if="field.select" size="17" color="#8e9992">mdi-chevron-down</v-icon>
                        </div>
                        <span v-if="field.spotlight" class="helper-tool__mock-pointer"
                          ><v-icon size="14">mdi-cursor-default-click</v-icon>{{ field.hint || "Hier auswählen" }}</span
                        >
                      </div>
                    </div>

                    <div v-if="screen.chips.length" class="helper-tool__mock-chips">
                      <span
                        v-for="chip in screen.chips"
                        :key="chip.label"
                        :class="[
                          `helper-tool__mock-chip--${chip.tone || 'neutral'}`,
                          {
                            'helper-tool__mock-chip--active': chip.active,
                            'helper-tool__mock-chip--switch': chip.switch,
                            'helper-tool__mock-spotlight': chip.spotlight,
                          },
                        ]">
                        <v-icon v-if="chip.icon" size="14">{{ chip.icon }}</v-icon
                        >{{ chip.label }}
                        <small v-if="chip.value">{{ chip.value }}</small>
                        <span v-if="chip.spotlight" class="helper-tool__mock-pointer"
                          ><v-icon size="14">mdi-cursor-default-click</v-icon>{{ chip.hint || "Auswählen" }}</span
                        >
                      </span>
                    </div>

                    <div v-if="screen.calendar" class="helper-tool__mock-calendar">
                      <div class="helper-tool__mock-calendar-days">
                        <span
                          v-for="day in screen.calendar.days"
                          :key="`${day.label}-${day.date}`"
                          :class="{ 'helper-tool__mock-calendar-day--active': day.active }">
                          <small>{{ day.label }}</small
                          ><strong>{{ day.date }}</strong>
                        </span>
                      </div>
                      <div class="helper-tool__mock-calendar-body">
                        <div class="helper-tool__mock-time-axis">
                          <span v-for="time in screen.calendar.times" :key="time">{{ time }}</span>
                        </div>
                        <div class="helper-tool__mock-blocks">
                          <div
                            v-for="block in screen.calendar.blocks"
                            :key="`${block.time}-${block.title}`"
                            :class="[`helper-tool__mock-block--${block.tone || 'green'}`, { 'helper-tool__mock-spotlight': block.spotlight }]">
                            <small>{{ block.time }}</small
                            ><strong>{{ block.title }}</strong
                            ><span v-if="block.meta">{{ block.meta }}</span>
                            <span v-if="block.spotlight" class="helper-tool__mock-pointer"
                              ><v-icon size="14">mdi-cursor-default-click</v-icon>{{ block.hint || "Öffnen" }}</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="screen.rows.length" class="helper-tool__mock-rows">
                      <div
                        v-for="row in screen.rows"
                        :key="`${row.title}-${row.subtitle}`"
                        class="helper-tool__mock-row"
                        :class="{ 'helper-tool__mock-spotlight': row.spotlight, 'helper-tool__mock-row--checked': row.checked }">
                        <div class="helper-tool__mock-row-icon">
                          <v-icon size="19" :color="row.checked ? '#7ee5a6' : '#c8d0cb'">{{ row.checked ? "mdi-check-circle" : row.icon }}</v-icon>
                        </div>
                        <div>
                          <strong>{{ row.title }}</strong
                          ><span v-if="row.subtitle">{{ row.subtitle }}</span
                          ><small v-if="row.meta">{{ row.meta }}</small>
                        </div>
                        <span v-if="row.actions && row.actions.length" class="helper-tool__mock-row-actions">
                          <v-icon v-for="rowAction in row.actions" :key="rowAction" size="17">{{ rowAction }}</v-icon>
                        </span>
                        <span v-else-if="row.status" :class="`helper-tool__mock-row-status--${row.tone || 'neutral'}`">{{ row.status }}</span>
                        <v-icon v-else size="18" color="#7c8580">mdi-chevron-right</v-icon>
                        <span v-if="row.spotlight" class="helper-tool__mock-pointer"
                          ><v-icon size="14">mdi-cursor-default-click</v-icon>{{ row.hint || "Hier öffnen" }}</span
                        >
                      </div>
                    </div>

                    <div v-if="screen.bars.length" class="helper-tool__mock-bars">
                      <div v-for="bar in screen.bars" :key="bar.label">
                        <span>{{ bar.label }}</span>
                        <div><i :style="{ width: `${bar.value}%` }" :class="`helper-tool__mock-bar--${bar.tone || 'green'}`"></i></div>
                        <strong>{{ bar.display || `${bar.value}%` }}</strong>
                      </div>
                    </div>

                    <div v-if="visiblePreviewActions(screen).length" class="helper-tool__mock-actions">
                      <div
                        v-for="action in visiblePreviewActions(screen)"
                        :key="action.label"
                        class="helper-tool__mock-button"
                        :class="[
                          `helper-tool__mock-button--${action.tone || 'secondary'}`,
                          {
                            'helper-tool__mock-spotlight': action.spotlight,
                            'helper-tool__mock-button--block': action.block,
                            'helper-tool__mock-button--disabled': action.disabled,
                          },
                        ]">
                        <v-icon v-if="action.icon" size="17">{{ action.icon }}</v-icon>
                        <span>{{ action.label }}</span>
                        <span v-if="action.spotlight" class="helper-tool__mock-pointer"
                          ><v-icon size="14">mdi-cursor-default-click</v-icon>{{ action.hint || "Hier tippen" }}</span
                        >
                      </div>
                    </div>

                    <p v-if="screen.caption" class="helper-tool__mock-caption">
                      <v-icon size="15" color="#7ee5a6">mdi-lightbulb-on-outline</v-icon>{{ screen.caption }}
                    </p>
                  </div>

                  <footer class="helper-tool__mock-footer">
                    <span class="helper-tool__mock-sequence"><i></i> Schritt {{ screenIndex + 1 }}</span>
                    <strong>{{ screen.stepLabel }}</strong>
                    <small>{{ screen.fidelity === "didactic" ? "Wegweiser" : "App-Nachbildung" }}</small>
                  </footer>
                </article>
              </div>

              <p class="helper-tool__visual-scroll-hint">
                <v-icon size="17">mdi-gesture-swipe-horizontal</v-icon> Auf kleinen Bildschirmen seitlich durch den Ablauf wischen.
              </p>
            </section>

            <div v-else class="helper-tool__screenshot-placeholder" role="note">
              <div class="helper-tool__screenshot-icon"><v-icon size="31" color="#7ee5a6">mdi-shield-eye-outline</v-icon></div>
              <div>
                <strong>Keine weitere Vorschau für dieses Profil</strong
                ><span>Die Anleitung zeigt nur Teilbereiche, die mit deinen aktuellen Funktionsrechten übereinstimmen.</span>
              </div>
            </div>

            <section class="helper-tool__result-card" aria-labelledby="result-title">
              <v-icon size="27" color="#7ee5a6">mdi-check-bold</v-icon>
              <div>
                <p id="result-title" class="helper-tool__section-label">Daran erkennst du, dass es geklappt hat</p>
                <p>{{ activeGuide.resultCheck }}</p>
              </div>
            </section>

            <section v-if="visibleTroubleshooting(activeGuide).length" class="helper-tool__content-section" aria-labelledby="problems-title">
              <p class="helper-tool__section-kicker">Wenn etwas nicht klappt</p>
              <h2 id="problems-title">Häufige Probleme</h2>
              <v-expansion-panels class="helper-tool__problems" accordion flat>
                <v-expansion-panel v-for="item in visibleTroubleshooting(activeGuide)" :key="item.problem">
                  <v-expansion-panel-header>{{ item.problem }}</v-expansion-panel-header>
                  <v-expansion-panel-content>{{ item.solution }}</v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </section>

            <section v-if="relatedGuides.length" class="helper-tool__content-section" aria-labelledby="related-title">
              <p class="helper-tool__section-kicker">Das könnte als Nächstes helfen</p>
              <h2 id="related-title">Verwandte Anleitungen</h2>
              <div class="helper-tool__related-grid">
                <button
                  v-for="guide in relatedGuides"
                  :key="guide.slug"
                  type="button"
                  class="helper-tool__related-card"
                  @click="openGuide(guide.slug)">
                  <span>{{ categoryLabel(guide.category) }}</span>
                  <strong>{{ guide.title }}</strong>
                  <v-icon size="20" color="#7ee5a6">mdi-arrow-right</v-icon>
                </button>
              </div>
            </section>
          </div>

          <aside class="helper-tool__article-aside" aria-label="Artikelaktionen">
            <v-card class="helper-tool__action-card" flat>
              <p class="helper-tool__section-label">Direkt weiterarbeiten</p>
              <v-btn
                v-if="visibleGuideTarget(activeGuide)"
                block
                color="primary"
                depressed
                large
                @click="openAppTarget(visibleGuideTarget(activeGuide))">
                Funktion öffnen
                <v-icon right>mdi-open-in-new</v-icon>
              </v-btn>
              <v-btn block text color="white" class="mt-2" @click="copyGuideLink">
                <v-icon left>mdi-link-variant</v-icon>
                Link kopieren
              </v-btn>
            </v-card>
          </aside>
        </section>
      </article>

      <template v-else>
        <section class="helper-tool__search-panel" aria-labelledby="search-title">
          <div class="helper-tool__search-copy">
            <p class="helper-tool__section-kicker">Schnelle Hilfe im Arbeitsalltag</p>
            <h2 id="search-title">Was möchtest du erledigen?</h2>
          </div>
          <v-text-field
            ref="helpSearch"
            v-model="searchQuery"
            class="helper-tool__search"
            filled
            rounded
            clearable
            hide-details
            color="primary"
            prepend-inner-icon="mdi-magnify"
            placeholder="Zum Beispiel: Ferien, Termin verschieben oder Trainingsplan"
            aria-label="Hilfe durchsuchen"></v-text-field>
          <div class="helper-tool__search-stats" aria-live="polite">
            <span>{{ filteredGuides.length }} Anleitungen</span>
            <span>{{ filteredFunctions.length }} Funktionen</span>
            <span>für dein Profil</span>
            <span>Code-Entwurf</span>
          </div>
        </section>

        <section class="helper-tool__category-section" aria-label="Hilfebereiche">
          <v-chip-group v-model="selectedCategory" class="helper-tool__category-chips" mandatory active-class="helper-tool__category-chip--active">
            <v-chip value="all" filter>
              Alle
              <span class="helper-tool__chip-count">{{ authorizedGuides.length + authorizedFunctions.length }}</span>
            </v-chip>
            <v-chip v-for="category in availableCategories" :key="category.id" :value="category.id" filter>
              {{ category.label }}
              <span class="helper-tool__chip-count">{{ category.count }}</span>
            </v-chip>
          </v-chip-group>
        </section>

        <section v-if="!hasSearchOrCategoryFilter && featuredGuides.length" class="helper-tool__overview-section" aria-labelledby="featured-title">
          <div class="helper-tool__overview-heading">
            <div>
              <p class="helper-tool__section-kicker">Direkt loslegen</p>
              <h2 id="featured-title">Häufige Aufgaben</h2>
            </div>
          </div>
          <div class="helper-tool__featured-grid">
            <button v-for="guide in featuredGuides" :key="guide.slug" type="button" class="helper-tool__featured-card" @click="openGuide(guide.slug)">
              <div class="helper-tool__featured-icon">
                <v-icon color="#7ee5a6">{{ categoryIcon(guide.category) }}</v-icon>
              </div>
              <span>{{ categoryLabel(guide.category) }}</span>
              <strong>{{ guide.title }}</strong>
              <p>{{ guide.summary }}</p>
              <div class="helper-tool__card-link">Anleitung öffnen <v-icon size="18" color="#7ee5a6">mdi-arrow-right</v-icon></div>
            </button>
          </div>
        </section>

        <section v-if="filteredGuides.length" class="helper-tool__overview-section" aria-labelledby="guides-title">
          <div class="helper-tool__overview-heading">
            <div>
              <p class="helper-tool__section-kicker">Schritt für Schritt</p>
              <h2 id="guides-title">{{ searchQuery ? "Passende Anleitungen" : "Alle Anleitungen" }}</h2>
            </div>
            <span>{{ filteredGuides.length }} Treffer</span>
          </div>
          <div class="helper-tool__guide-grid">
            <button v-for="guide in filteredGuides" :key="guide.slug" type="button" class="helper-tool__guide-card" @click="openGuide(guide.slug)">
              <div class="helper-tool__guide-card-top">
                <span class="helper-tool__guide-category"
                  ><v-icon size="17" color="#7ee5a6">{{ categoryIcon(guide.category) }}</v-icon
                  >{{ categoryLabel(guide.category) }}</span
                >
                <span>{{ guide.duration }}</span>
              </div>
              <strong>{{ guide.title }}</strong>
              <p>{{ guide.summary }}</p>
              <div class="helper-tool__card-link">Anleitung öffnen <v-icon size="18" color="#7ee5a6">mdi-arrow-right</v-icon></div>
            </button>
          </div>
        </section>

        <section v-if="filteredFunctions.length" class="helper-tool__overview-section" aria-labelledby="functions-title">
          <div class="helper-tool__overview-heading">
            <div>
              <p class="helper-tool__section-kicker">Wo finde ich was?</p>
              <h2 id="functions-title">Funktionsverzeichnis</h2>
            </div>
            <span>{{ filteredFunctions.length }} Funktionen</span>
          </div>
          <div class="helper-tool__function-list">
            <div v-for="item in filteredFunctions" :key="item.slug" class="helper-tool__function-row">
              <div class="helper-tool__function-icon">
                <v-icon color="#7ee5a6">{{ categoryIcon(item.category) }}</v-icon>
              </div>
              <div class="helper-tool__function-copy">
                <span>{{ categoryLabel(item.category) }}</span>
                <strong>{{ item.title }}</strong>
                <p>{{ item.summary }}</p>
                <div class="helper-tool__function-path">{{ item.where.join(" › ") }}</div>
              </div>
              <div class="helper-tool__function-actions">
                <v-btn v-if="item.guideSlug && authorizedGuideSlugs.includes(item.guideSlug)" text color="white" @click="openGuide(item.guideSlug)">
                  Anleitung
                </v-btn>
                <v-btn v-if="item.appTarget" icon color="primary" :aria-label="`${item.title} öffnen`" @click="openAppTarget(item.appTarget)">
                  <v-icon>mdi-open-in-new</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </section>

        <v-card v-if="!filteredGuides.length && !filteredFunctions.length" class="helper-tool__state-card helper-tool__state-card--inline" flat>
          <div class="helper-tool__state-icon"><v-icon size="34" color="#7ee5a6">mdi-magnify-close</v-icon></div>
          <h2>Keine passenden Treffer</h2>
          <p>Versuche einen kürzeren Begriff oder zeige wieder alle Bereiche an.</p>
          <v-btn color="primary" depressed @click="resetFilters">Filter zurücksetzen</v-btn>
        </v-card>
      </template>
    </main>
  </div>
</template>

<script>
import { mapState } from "vuex"
import userService from "@/services/userService"

const REVIEW_DATE = "12.08.2026"
const TRAINER_ROLES = ["616616c4c659c875cad63525", "61661791c659c875cad63527"]

const CATEGORY_DEFINITIONS = [
  { id: "orientation", label: "Orientierung", icon: "mdi-compass-outline" },
  { id: "workday", label: "Arbeitsalltag", icon: "mdi-weather-sunny" },
  { id: "customers", label: "Kunden", icon: "mdi-account-heart-outline" },
  { id: "calendar", label: "Kalender", icon: "mdi-calendar-month-outline" },
  { id: "training", label: "Training", icon: "mdi-dumbbell" },
  { id: "time", label: "Zeiterfassung", icon: "mdi-timer-outline" },
  { id: "planning", label: "Arbeitsplanung", icon: "mdi-calendar-edit" },
  { id: "documents", label: "Dokumente", icon: "mdi-file-document-outline" },
  { id: "group-fitness", label: "Group Fitness", icon: "mdi-account-group-outline" },
  { id: "studio", label: "Studio", icon: "mdi-office-building-outline" },
  { id: "statistics", label: "Statistiken", icon: "mdi-chart-box-outline" },
  { id: "marketing", label: "Marketing", icon: "mdi-bullhorn-outline" },
  { id: "tools", label: "Weitere Werkzeuge", icon: "mdi-toolbox-outline" },
]

const DOCUMENT_RIGHTS = [
  "d_fetchAllDocs",
  "d_fetchDocs",
  "d_manageJobApplications",
  "d_fetchJobApplications",
  "d_v_healthcare",
  "cc_view",
  "cc_viewAll",
  "d_c_timestops",
  "d_v_timestops",
  "d_fetchAccidentProtocols",
  "d_manageAccidentProtocols",
  "d_fetchPersonalRequests",
  "d_managePersonalRequests",
]

const GROUP_FITNESS_RIGHTS = [
  "gf_c_create",
  "gf_c_edit",
  "gf_c_delete",
  "gf_c_plan",
  "gf_r_viewAll",
  "gf_r_threestars",
  "gf_cp_instructor",
  "gf_c_signupRange",
  "r_edit",
  "l_edit",
  "a_add",
  "a_edit",
  "a_delete",
]

const STATISTICS_RIGHTS = [
  "stat_viewstudio",
  "stat_admin",
  "stat_gf",
  "stat_gf_stud",
  "stat_leads",
  "stat_employee",
  "stat_coo",
  "stats_cl_admin",
  "stats_transactions",
  "stat_planningControlling",
  "stats_blackFriday",
]

const STAFF_RIGHTS = [
  "tt_employeeManage",
  "tt_management",
  "tt_office",
  "tt_eventsAdmin",
  "g_studioadmin",
  "sm_create",
  "sm_appointment_management",
  "g_employeemanager",
  "live_checkins",
  "u_search",
  "tp_create",
  "tp_viewAll",
  "tp_feedback",
  "mtt_create_workouts",
  "todo_fetchAndEdit",
  "ab_view",
  "ab_create",
  "ab_edit",
  "ab_delete",
  "c_create_customer",
  "c_open_contracts",
  "c_create",
  "reservations_fetch",
  "reservations_createType",
  "g_daycare",
  "g_websitecontent",
  "g_websitecontentstudioadmin",
  "af_downloadList",
  "af_view",
  "af_edit",
  "af_settingsEdit",
  "af_leadSettings",
  "af_cost",
  "m_downloadList",
  "m_signupUser",
  "m_viewSubs",
  "m_createSubs",
  "m_createMembercard",
  "payment_create",
  "payment_overview",
  "shop_manage",
  "shop_purchase_view",
  "nutrition_admin",
  "sebdomspecial",
  "ex_create",
  "ex_edit",
  "ex_delete",
  "ex_transfer",
  "tac_accessDevices",
  "mk_news",
  "mk_popup",
  "mk_qrcode",
  "mk_campaigns",
  "mk_slider",
  "mk_push",
  "mk_push_deactivated",
  "mk_hyroxPFT",
  "mk_screenBill",
  "mk_emailsignature",
  "d_fetchMarketingStock",
  "d_fetchMarketingShop",
  "d_manageMarketingShop",
  ...DOCUMENT_RIGHTS,
  ...GROUP_FITNESS_RIGHTS,
  ...STATISTICS_RIGHTS,
]

const anyRight = (...rights) => [{ anyRights: rights }]
const allGroups = (...groups) => groups
const staffAccess = [{ anyRights: STAFF_RIGHTS, anyRoles: TRAINER_ROLES }]
const customerAction = (...rights) => allGroups({ anyRights: ["u_search"] }, { anyRights: rights })
const step = (title, text, tip = "", accessGroups = null, id = null) => ({ title, text, tip, accessGroups, id })
const problem = (problemText, solution, accessGroups = null) => ({ problem: problemText, solution, accessGroups })
const previewAction = (label, tone = "secondary", icon = "", spotlight = false, hint = "", accessGroups = null) => ({
  label,
  tone,
  icon,
  spotlight,
  hint,
  accessGroups,
})
const previewField = (label, value, options = {}) => ({ label, value, placeholder: "Auswählen", ...options })
const previewRow = (title, subtitle = "", options = {}) => ({ title, subtitle, icon: "mdi-circle-small", ...options })
const previewMetric = (label, value, detail = "", tone = "neutral") => ({ label, value, detail, tone })
const previewScreen = (title, options = {}) => ({
  eyebrow: "Roody Arbeitsbereich",
  subtitle: "",
  icon: "mdi-view-dashboard-outline",
  device: "phone",
  type: "overview",
  fidelity: "app",
  accessGroups: null,
  stepLabel: title,
  path: [],
  status: null,
  tabs: [],
  search: null,
  notice: null,
  progress: null,
  metrics: [],
  sectionLabel: "",
  sectionMeta: "",
  fields: [],
  chips: [],
  calendar: null,
  rows: [],
  bars: [],
  actions: [],
  caption: "",
  ...options,
  title: options.title || title,
})

const guide = (definition) => ({
  duration: "3 Min.",
  prerequisites: [],
  troubleshooting: [],
  relatedGuides: [],
  visuals: [],
  lastReviewed: REVIEW_DATE,
  ...definition,
})

const functionEntry = (definition) => definition

const HELP_GUIDES = [
  guide({
    slug: "dashboard-navigation",
    title: "Dashboard und Navigation verstehen",
    summary: "Finde deine freigegebenen Bereiche, Schnellzugriffe und Funktionen ohne lange Wege.",
    category: "orientation",
    aliases: ["menü", "sidebar", "navigation", "wo finde ich", "schnellzugriff"],
    accessGroups: staffAccess,
    appTarget: { name: "dashboardHome" },
    where: ["App", "Dashboard"],
    duration: "2 Min.",
    featured: true,
    visuals: [
      previewScreen("Dashboard-Menü öffnen", {
        device: "tablet",
        eyebrow: "Dashboard",
        icon: "mdi-menu",
        path: ["App", "Dashboard"],
        search: { placeholder: "Navigation durchsuchen", spotlight: true, hint: "Funktion eingeben" },
        sectionLabel: "Bereiche",
        rows: [
          previewRow("Home", "Persönliche Übersicht", { icon: "mdi-home-outline", status: "Aktiv", tone: "success" }),
          previewRow("Zeiterfassung", "Eigene Zeit und Anträge", { icon: "mdi-timer-outline" }),
          previewRow("Arbeitsplanung", "Planung erstellen und prüfen", { icon: "mdi-calendar-edit" }),
          previewRow("Benutzersuche", "Kunden und Kundenakten", { icon: "mdi-account-search-outline" }),
        ],
        caption: "Auf dem Smartphone öffnet derselbe Menübutton den Dashboard-Drawer.",
        stepLabel: "Menü und Suche",
      }),
      previewScreen("Schnellzugriffe bearbeiten", {
        device: "tablet",
        type: "list-detail",
        eyebrow: "Dashboard · Übersicht",
        icon: "mdi-lightning-bolt-outline",
        title: "Schnellzugriffe bearbeiten",
        sectionLabel: "Schnellzugriff hinzufügen",
        search: { placeholder: "Bereich suchen", value: "Zeiterfassung", spotlight: true, hint: "Bereich finden" },
        rows: [
          previewRow("Zeiterfassung", "Ausgewählt", { icon: "mdi-timer-outline", status: "Ausgewählt", tone: "success", checked: true }),
          previewRow("Arbeitskalender", "Noch nicht ausgewählt", { icon: "mdi-calendar-outline", spotlight: true, hint: "Hinzufügen" }),
        ],
        actions: [
          previewAction("Leeren", "secondary"),
          previewAction("Abbrechen", "secondary"),
          previewAction("Speichern", "primary", "mdi-content-save", true, "Auswahl sichern"),
        ],
        stepLabel: "Schnellzugriffe wählen",
      }),
    ],
    prerequisites: ["Du bist mit deinem persönlichen Mitarbeiterprofil angemeldet."],
    steps: [
      step(
        "Dashboard öffnen",
        "Öffne in der App den Bereich „Dashboard“. Auf dem Smartphone findest du ihn in der unteren Navigation, sofern dein Profil dafür freigegeben ist.",
      ),
      step("Navigation aufrufen", "Nutze links die Dashboard-Navigation. Auf kleinen Bildschirmen öffnest du sie über den Menübutton."),
      step(
        "Bereich suchen",
        "Gib den Namen einer Funktion in „Navigation durchsuchen“ ein. Die Suche berücksichtigt nur Bereiche, die dein Profil verwenden darf.",
      ),
      step("Ziel öffnen", "Wähle den passenden Treffer. Kontrolliere anschließend Überschrift und Inhalt der geöffneten Seite."),
      step("Zur App zurückkehren", "Mit „Zurück zur App“ am Ende der Navigation wechselst du wieder in die normale App-Ansicht."),
    ],
    resultCheck: "Die gewünschte Dashboard-Seite ist geöffnet und ihre Überschrift beziehungsweise URL passt zu deiner Aufgabe.",
    troubleshooting: [
      problem(
        "Eine Funktion fehlt in der Navigation",
        "Dashboard-Einträge werden anhand deiner Rechte gefiltert. Suche zuerst nach dem Begriff und lasse anschließend dein Funktionsrecht prüfen.",
      ),
      problem("Die Navigation ist auf dem Smartphone nicht sichtbar", "Öffne das temporäre Menü über den Menübutton der Dashboard-Seite."),
    ],
    relatedGuides: ["fehlende-funktion", "heutiger-einsatz"],
  }),
  guide({
    slug: "fehlende-funktion",
    title: "Fehlende Funktion oder fehlendes Recht einordnen",
    summary: "Prüfe Kontext, Filter und Berechtigung, wenn ein erwarteter Button oder Menüpunkt fehlt.",
    category: "orientation",
    aliases: ["kein zugriff", "berechtigung", "recht fehlt", "button fehlt", "menü fehlt"],
    accessGroups: staffAccess,
    appTarget: { name: "dashboardHome" },
    where: ["Dashboard", "Betroffener Bereich"],
    duration: "3 Min.",
    visuals: [
      previewScreen("Kontext zuerst kontrollieren", {
        device: "tablet",
        type: "form",
        eyebrow: "Dashboard · Filter",
        icon: "mdi-filter-cog-outline",
        fields: [
          previewField("Zeitraum", "August 2026", { icon: "mdi-calendar-range", select: true, spotlight: true, hint: "Zeitraum prüfen" }),
          previewField("Studios", "Zürich · Beispiel", { icon: "mdi-map-marker-multiple-outline", select: true }),
        ],
        chips: [
          { label: "alle Studios anwählen", icon: "mdi-checkbox-multiple-marked-outline" },
          { label: "alle Studios abwählen", icon: "mdi-checkbox-multiple-blank-outline" },
        ],
        caption: "Viele Dashboard-Inhalte ändern sich mit Zeitraum und Studioauswahl.",
        stepLabel: "Kontext prüfen",
      }),
      previewScreen("Navigation ohne Treffer", {
        type: "navigation",
        eyebrow: "Dashboard · Suchergebnisse",
        icon: "mdi-magnify-close",
        title: "Suchergebnisse",
        search: { placeholder: "Navigation durchsuchen", value: "Beispielfunktion", spotlight: true, hint: "Suchbegriff prüfen" },
        notice: { tone: "info", icon: "mdi-text-search", title: "Nichts gefunden", text: "Versuche einen anderen Begriff." },
        caption: "Bleibt die Funktion auch mit einem treffenden Begriff unsichtbar, sollte das konkrete Funktionsrecht geprüft werden.",
        stepLabel: "Suche und Recht einordnen",
      }),
    ],
    prerequisites: ["Du weißt ungefähr, welche Aufgabe du erledigen möchtest."],
    steps: [
      step(
        "Kontext prüfen",
        "Kontrolliere das ausgewählte Studio, Datum und gegebenenfalls den geöffneten Kunden. Manche Aktionen erscheinen erst im passenden Kontext.",
      ),
      step("Navigation durchsuchen", "Suche im Dashboard nach dem Funktionsnamen oder einem verwandten Begriff."),
      step(
        "Darstellung vergleichen",
        "Prüfe, ob die Funktion auf Mobilgeräten in einem Menü, Tab oder Dialog liegt und nicht direkt als Button angezeigt wird.",
      ),
      step(
        "Filter zurücksetzen",
        "Setze auf der Zielseite Studio-, Datums-, Status- oder Suchfilter zurück, wenn nur Daten statt der gesamten Funktion fehlen.",
      ),
      step(
        "Freigabe prüfen lassen",
        "Fehlt die Aktion weiterhin, nenne Centerleitung oder Administration die konkrete Funktion und deinen aktuellen Studio-Kontext.",
      ),
    ],
    resultCheck: "Die Funktion ist im richtigen Kontext sichtbar oder die fehlende Freigabe kann eindeutig benannt werden.",
    troubleshooting: [
      problem(
        "Die Seite ist da, aber die Liste bleibt leer",
        "Eine leere Liste deutet häufig auf Datum, Studio oder Filter hin und nicht automatisch auf ein fehlendes Recht.",
      ),
      problem(
        "Ein Direktlink öffnet die Startseite",
        "Der Router leitet bei fehlenden Routenrechten zurück. Lasse das Recht der konkreten Zielseite prüfen.",
      ),
    ],
    relatedGuides: ["dashboard-navigation"],
  }),
  guide({
    slug: "arbeitszeit-erfassen",
    title: "Arbeitszeit starten, pausieren und beenden",
    summary: "Erfasse deinen Arbeitstag korrekt mit Start, Pause, Weiter und Check-out.",
    category: "time",
    aliases: ["stempeln", "check in", "check out", "pause", "arbeitsbeginn", "arbeitsende"],
    accessGroups: anyRight("tt_employeeManage"),
    appTarget: { name: "timeTracker" },
    where: ["Startseite", "Zeiterfassung", "Heute"],
    duration: "2 Min.",
    featured: true,
    visuals: [
      previewScreen("Arbeitszeit starten", {
        eyebrow: "Heute",
        icon: "mdi-timer-outline",
        type: "overview",
        tabs: [
          { label: "Zeiterfassung", icon: "mdi-clock-outline", active: true },
          { label: "Meine Planung", icon: "mdi-calendar-account" },
        ],
        metrics: [previewMetric("Heute", "0.00h", "Noch nicht gestartet")],
        chips: [
          { label: "Check-in", icon: "mdi-login" },
          { label: "Check-out", icon: "mdi-logout" },
          { label: "Pause", icon: "mdi-coffee" },
        ],
        actions: [previewAction("Starten", "success", "mdi-power", true, "Arbeitsbeginn")],
        caption: "Nach dem Start wechselt die Statusanzeige sofort auf laufend.",
        stepLabel: "Starten",
      }),
      previewScreen("Aktive Zeiterfassung", {
        type: "status-confirm",
        eyebrow: "ZEITERFASSUNG",
        icon: "mdi-timer-sand",
        status: { label: "Aktiv", tone: "success" },
        metrics: [previewMetric("Arbeitszeit", "07:31", "Beispiel", "success"), previewMetric("Pause", "00:31", "Beispiel", "warning")],
        actions: [
          previewAction("Check-Out", "danger", "mdi-stop", true, "Arbeit beenden"),
          previewAction("Pause", "warning", "mdi-coffee"),
          previewAction("Rauchen", "secondary", "mdi-smoking"),
        ],
        stepLabel: "Aktiv arbeiten",
      }),
      previewScreen("Laufende Pause", {
        type: "status-confirm",
        eyebrow: "ZEITERFASSUNG",
        icon: "mdi-coffee-outline",
        status: { label: "Pause", tone: "warning" },
        notice: { tone: "warning", icon: "mdi-information-outline", text: "Während der Pause ist der Check-Out deaktiviert." },
        actions: [
          previewAction("Weiter", "success", "mdi-play", true, "Arbeit fortsetzen"),
          { ...previewAction("Check-Out", "secondary", "mdi-stop"), disabled: true },
        ],
        stepLabel: "Pause beenden",
      }),
    ],
    prerequisites: ["Das richtige Studio ist ausgewählt.", "Du bearbeitest den aktuellen Tag."],
    steps: [
      step("Arbeit starten", "Öffne die heutige Zeiterfassung und tippe auf „Starten“. Die laufende Arbeitszeit wird danach angezeigt."),
      step("Pause beginnen", "Tippe beim Verlassen des Arbeitsplatzes auf „Pause“. Die Pausenzeit läuft getrennt von der Arbeitszeit."),
      step("Arbeit fortsetzen", "Beende eine laufende Pause mit „Weiter“, bevor du später den Arbeitstag abschließt."),
      step("Arbeitstag beenden", "Tippe am Ende des Einsatzes auf „Stop“ beziehungsweise Check-out und kontrolliere die erfasste Endzeit."),
    ],
    resultCheck: "Bei laufender Arbeit wird der aktive Status angezeigt; nach dem Check-out sind Endzeit und Tagesgesamtzeit sichtbar.",
    troubleshooting: [
      problem("Der Stop-Button ist nicht verfügbar", "Beende zuerst eine noch laufende Pause mit „Weiter“ und versuche den Check-out danach erneut."),
      problem(
        "Eine Zeit aus einem früheren Tag ist falsch",
        "Ändere vergangene Einträge nicht über die heutigen Buttons, sondern über den Korrekturmodus.",
      ),
    ],
    relatedGuides: ["zeitkorrektur", "monat-einreichen", "heutiger-einsatz"],
  }),
  guide({
    slug: "heutiger-einsatz",
    title: "Heutige Schichten, Termine und Ämtli finden",
    summary: "Sieh auf einen Blick, was heute geplant ist, und öffne bei Bedarf den Arbeitskalender.",
    category: "workday",
    aliases: ["mein tag", "meine planung", "schicht", "termin", "ämtli", "aufgabe"],
    accessGroups: anyRight("tt_employeeManage"),
    appTarget: { name: "workCalendar" },
    where: ["Startseite", "Meine Planung", "Tag"],
    duration: "2 Min.",
    featured: true,
    visuals: [
      previewScreen("Meine Planung öffnen", {
        device: "tablet",
        eyebrow: "Mitarbeiter-Startseite",
        icon: "mdi-calendar-account",
        title: "Heute",
        tabs: [
          { label: "Zeiterfassung", icon: "mdi-clock-outline" },
          { label: "Meine Planung", icon: "mdi-calendar-account", active: true },
        ],
        chips: [{ label: "Tag", active: true }, { label: "Monat" }],
        actions: [previewAction("Vorheriger Tag", "secondary", "mdi-chevron-left"), previewAction("Nächster Tag", "secondary", "mdi-chevron-right")],
        sectionLabel: "Dienstag, 12. August · Beispiel",
        rows: [
          previewRow("SCHICHTEN", "08:00–12:00 Arbeit · 12:00–13:00 Pause · 13:00–17:00 Arbeit", { icon: "mdi-briefcase-clock" }),
          previewRow("TERMINE", "Anamnese · Anna Keller · Beispiel · 10:30–11:00", {
            icon: "mdi-calendar-clock",
            status: "2 Termine",
            tone: "success",
          }),
          previewRow("ÄMTLI", "Getränkestation prüfen · Beispiel · Checkliste 2/4", {
            icon: "mdi-clipboard-check-outline",
            status: "Offen",
            tone: "warning",
            spotlight: true,
            hint: "Details öffnen",
          }),
        ],
        actions: [previewAction("Zum Kalender", "primary", "mdi-arrow-right", true, "Alle Details")],
        stepLabel: "Einsatz prüfen",
      }),
      previewScreen("Freien Tag erkennen", {
        eyebrow: "Meine Planung · Heute",
        icon: "mdi-party-popper",
        title: "Frei!",
        notice: { tone: "success", icon: "mdi-emoticon-happy-outline", text: "An diesem Tag ist nichts geplant" },
        actions: [previewAction("Zum Kalender", "secondary", "mdi-arrow-right")],
        stepLabel: "Leerzustand einordnen",
      }),
    ],
    prerequisites: ["Dein Arbeitsplan wurde für den gewählten Monat freigegeben."],
    steps: [
      step("Planung öffnen", "Wechsle auf der Mitarbeiter-Startseite zu „Meine Planung“ und wähle die Tagesansicht."),
      step("Datum kontrollieren", "Prüfe das angezeigte Datum und wechsle bei Bedarf zum gewünschten Tag."),
      step("Tagesinhalte lesen", "Kontrolliere Schichten, Kundentermine und zugewiesene Ämtli beziehungsweise Aufgaben."),
      step("Details im Kalender öffnen", "Nutze „Zum Kalender“, wenn du Zeitblöcke, weitere Mitarbeitende oder Details bearbeiten musst."),
    ],
    resultCheck: "Das richtige Datum ist gewählt und alle für dich sichtbaren Schichten, Termine und Aufgaben sind geprüft.",
    troubleshooting: [
      problem(
        "Der Tag ist leer, obwohl du einen Einsatz erwartest",
        "Kontrolliere Datum und Studio. Ein noch nicht freigegebener Arbeitsplan ist in der persönlichen Planung möglicherweise noch nicht sichtbar.",
      ),
      problem(
        "Im Kalender sehe ich andere Inhalte als auf der Startseite",
        "Prüfe im Arbeitskalender erneut Studio, Datum und den aktiven Mitarbeiterfokus.",
      ),
    ],
    relatedGuides: ["arbeitszeit-erfassen", "aufgaben-aemtli", "termin-erstellen"],
  }),
  guide({
    slug: "ferien-wuensche",
    title: "Ferien-, Frei- und Schichtwünsche einreichen",
    summary: "Erfasse deinen gewünschten Zeitraum oder Einsatz und verfolge den Entscheidungsstatus.",
    category: "time",
    aliases: ["ferien", "urlaub", "freiwunsch", "schichtwunsch", "einsatzwunsch"],
    accessGroups: anyRight("tt_employeeManage"),
    appTarget: { name: "timeTracker" },
    where: ["Zeiterfassung", "Schnellaktionen", "Ferien, Freiwunsch oder Schichtwunsch"],
    duration: "4 Min.",
    visuals: [
      previewScreen("Ferienantrag prüfen", {
        type: "stepper",
        eyebrow: "Ferienplanung",
        icon: "mdi-palm-tree",
        tabs: [{ label: "Meine Anträge" }, { label: "Neuer Antrag", active: true }],
        progress: { label: "Startdatum → Enddatum → Tage anpassen & Senden", caption: "Schritt 3 von 3", value: 100 },
        fields: [
          previewField("Startdatum", "18.08.2026", { icon: "mdi-calendar-start" }),
          previewField("Enddatum", "22.08.2026", { icon: "mdi-calendar-end" }),
        ],
        metrics: [previewMetric("RESTSALDO (TAGE)", "12.0", "Beispiel", "success"), previewMetric("STUNDEN", "96:00", "Beispiel")],
        notice: {
          tone: "warning",
          icon: "mdi-account-group-outline",
          text: "2 Kollegen haben in diesem Zeitraum ebenfalls Ferien beantragt. · Beispiel",
        },
        actions: [previewAction("Berechnen", "secondary"), previewAction("Beantragen", "primary", "mdi-send", true, "Antrag senden")],
        stepLabel: "Ferien beantragen",
      }),
      previewScreen("Freiwunsch erfassen", {
        type: "stepper",
        eyebrow: "Freiwünsche",
        icon: "mdi-calendar-heart",
        tabs: [{ label: "Meine Wünsche" }, { label: "Neuer Antrag", active: true }],
        progress: { label: "Datum wählen → Zeitraum → Bemerkung", caption: "Schritt 2 von 3", value: 67 },
        fields: [
          previewField("Datum wählen", "28.08.2026", { icon: "mdi-calendar", spotlight: true, hint: "Tag wählen" }),
          previewField("Bemerkung (Optional)", "Privater Termin · Beispiel", { icon: "mdi-message-text-outline", wide: true }),
        ],
        chips: [{ label: "Ganzer Tag", active: true, tone: "success" }, { label: "Vormittag" }, { label: "Nachmittag" }],
        actions: [previewAction("Absenden (1)", "primary", "mdi-send", true, "Wunsch senden")],
        stepLabel: "Freiwunsch senden",
      }),
      previewScreen("Schichtwunsch erfassen", {
        type: "stepper",
        eyebrow: "Schichtwünsche",
        icon: "mdi-clock-start",
        tabs: [{ label: "Meine Wünsche" }, { label: "Neuer Wunsch", active: true }],
        progress: { label: "Datum wählen → Schichtart wünschen", caption: "Schritt 2 von 2", value: 100 },
        fields: [previewField("Datum wählen", "05.09.2026", { icon: "mdi-calendar" })],
        chips: [
          { label: "Morgen", icon: "mdi-weather-sunset-up", active: true, tone: "success" },
          { label: "Zwischen", icon: "mdi-white-balance-sunny" },
          { label: "Spät", icon: "mdi-weather-night" },
        ],
        actions: [previewAction("Wunsch absenden", "primary", "mdi-send", true, "Schichtwunsch senden")],
        stepLabel: "Schicht wünschen",
      }),
    ],
    prerequisites: ["Du kennst Datum beziehungsweise Zeitraum deines Wunsches."],
    steps: [
      step("Wunschtyp wählen", "Öffne Ferien, Freiwunsch oder Schichtwunsch und starte einen neuen Antrag."),
      step(
        "Datum festlegen",
        "Wähle bei Ferien den Zeitraum, bei Frei den Tag beziehungsweise Tagesabschnitt und beim Schichtwunsch den gewünschten Einsatzbereich.",
      ),
      step("Angaben kontrollieren", "Prüfe berechnete Ferientage, gewählten Tagesabschnitt oder die Schichtauswahl Morgen, Zwischen oder Spät."),
      step("Kommentar ergänzen", "Ergänze bei Bedarf einen kurzen Hinweis, der den Wunsch für die prüfende Person verständlich macht."),
      step("Wunsch absenden", "Sende den Antrag ab und kontrolliere anschließend den Status in der Übersicht."),
    ],
    resultCheck: "Der Wunsch erscheint mit dem Status „Offen“ und wechselt später auf genehmigt oder abgelehnt.",
    troubleshooting: [
      problem("Die Anzahl der Ferientage wirkt falsch", "Kontrolliere Start, Ende und die angezeigten Arbeitstage, bevor du den Antrag absendest."),
      problem(
        "Ein offener Wunsch soll nicht mehr gelten",
        "Prüfe beim offenen Eintrag, ob eine Stornieren-Aktion verfügbar ist. Bereits bearbeitete Wünsche müssen über die zuständige Leitung geklärt werden.",
      ),
    ],
    relatedGuides: ["monat-einreichen", "krankmeldung"],
  }),
  guide({
    slug: "krankmeldung",
    title: "Krankheit melden und Arztzeugnis hochladen",
    summary: "Bestätige eine erfasste Abwesenheit und reiche bei Bedarf ein Arztzeugnis ein.",
    category: "time",
    aliases: ["krank", "krankmeldung", "arztzeugnis", "aub", "abwesenheit"],
    accessGroups: [{ anyRights: ["tt_employeeManage", "tt_management", "g_studioadmin"] }],
    appTarget: { name: "timeTracker" },
    where: ["Zeiterfassung", "Abwesenheit"],
    duration: "4 Min.",
    visuals: [
      previewScreen("Abwesenheit erfassen", {
        device: "tablet",
        type: "stepper",
        accessGroups: anyRight("tt_management", "g_studioadmin"),
        eyebrow: "Zeiterfassung · Abwesenheit",
        icon: "mdi-account-alert-outline",
        title: "Abwesenheit erfassen",
        tabs: [
          { label: "Abwesenheit erfassen", icon: "mdi-plus-circle-outline", active: true },
          { label: "Einträge verwalten", icon: "mdi-file-document-edit-outline" },
        ],
        progress: { label: "Auswahl → Typ auswählen → Tage auswählen", caption: "Schritt 3 von 3", value: 100 },
        fields: [
          previewField("Mitarbeiter", "Marco Meier · Beispiel", { icon: "mdi-account-outline", select: true }),
          previewField("Abwesenheitstyp auswählen", "Krank", { icon: "mdi-medical-bag", select: true, spotlight: true, hint: "Typ wählen" }),
          previewField("Datum", "12.08.2026", { icon: "mdi-calendar" }),
          previewField("Stunden", "8.4", { icon: "mdi-clock-outline" }),
        ],
        chips: [{ label: "Mitarbeiter-Freigabe erforderlich (z.B. für Arztzeugnis)", active: true, icon: "mdi-account-check-outline" }],
        actions: [previewAction("Speichern", "primary", "mdi-content-save", true, "Vorgang anlegen")],
        stepLabel: "Leitung erfasst",
      }),
      previewScreen("Krankmeldung beantworten", {
        type: "form",
        accessGroups: anyRight("tt_employeeManage"),
        eyebrow: "Abwesenheit bestätigen",
        icon: "mdi-file-document-alert-outline",
        title: "Abwesenheit bestätigen",
        notice: {
          tone: "warning",
          icon: "mdi-alert-circle",
          title: "Ausstehende Krankmeldung / Abwesenheit",
          text: "Krank · 12. August · Beispieldaten",
        },
        chips: [
          { label: "Ja, ich war abwesend", icon: "mdi-check", active: true, tone: "success", spotlight: true, hint: "Bestätigen" },
          { label: "Nein", icon: "mdi-close", tone: "danger" },
        ],
        fields: [
          previewField("Arztzeugnis hochladen (AUB - optional)", "Datei auswählen (Bild oder PDF)", { icon: "mdi-paperclip", wide: true }),
          previewField("Kommentar (optional)", "", { icon: "mdi-message-text-outline", placeholder: "Kommentar eingeben", wide: true }),
        ],
        actions: [previewAction("Senden", "primary", "mdi-send", true, "Antwort senden")],
        stepLabel: "Mitarbeiter antwortet",
      }),
    ],
    prerequisites: ["Die Abwesenheit wurde durch eine verantwortliche Person angelegt oder ist dir zur Bestätigung zugewiesen."],
    steps: [
      step(
        "Abwesenheit anlegen",
        "Öffne als verantwortliche Person die Abwesenheiten, wähle Mitarbeitenden, Typ und Zeitraum und aktiviere bei Bedarf die Mitarbeiter-Freigabe.",
        "Im aktuellen Ablauf wird die Krankmeldung zuerst durch die zuständige Leitung erfasst.",
        [{ anyRights: ["tt_management", "g_studioadmin"] }],
      ),
      step(
        "Hinweis öffnen",
        "Öffne als Mitarbeiter den Hinweis zur ausstehenden Krankmeldung beziehungsweise Abwesenheit.",
        "",
        anyRight("tt_employeeManage"),
      ),
      step("Angaben beantworten", "Prüfe Zeitraum und Typ und bestätige oder lehne die erfassten Angaben ab.", "", anyRight("tt_employeeManage")),
      step(
        "Arztzeugnis hinzufügen",
        "Lade, falls erforderlich, ein gut lesbares Bild oder PDF des Arztzeugnisses hoch.",
        "Keine echten Dokumente in Test- oder Schulungskonten verwenden.",
        anyRight("tt_employeeManage"),
      ),
      step("Antwort senden", "Sende die Bestätigung samt Datei ab und kontrolliere den neuen Status.", "", anyRight("tt_employeeManage")),
    ],
    resultCheck: "Die Abwesenheit ist gespeichert; eine erforderliche Antwort beziehungsweise Datei ist in der Verwaltung sichtbar.",
    troubleshooting: [
      problem(
        "Als Mitarbeiter finde ich keinen Button „Krank melden“",
        "Der aktuelle Ablauf startet über eine durch die zuständige Leitung erfasste Abwesenheit, die du anschließend bestätigst.",
      ),
      problem(
        "Die Datei lässt sich nicht hochladen",
        "Verwende ein unterstütztes Bild- oder PDF-Format und achte auf eine stabile Verbindung. Versuche es anschließend erneut.",
      ),
    ],
    relatedGuides: ["ferien-wuensche", "zeiten-pruefen"],
  }),
  guide({
    slug: "zeitkorrektur",
    title: "Stempelzeiten korrigieren lassen",
    summary: "Ergänze oder korrigiere einen vergangenen Zeiteintrag und reiche ihn zur Prüfung ein.",
    category: "time",
    aliases: ["korrektur", "stempelzeit falsch", "fehlender eintrag", "zeit ändern"],
    accessGroups: anyRight("tt_employeeManage"),
    appTarget: { name: "timeTracker" },
    where: ["Dashboard", "Zeiterfassung", "Tag", "Korrektur"],
    duration: "4 Min.",
    visuals: [
      previewScreen("Korrekturen öffnen", {
        eyebrow: "Korrekturen",
        icon: "mdi-playlist-edit",
        chips: [{ label: "10.08.2026", icon: "mdi-calendar", active: true, tone: "success" }],
        rows: [
          previewRow("08:14 · Check-In", "Zürich · Beispiel", { icon: "mdi-login" }),
          previewRow("17:06 · Check-Out", "Zürich · Beispiel", {
            icon: "mdi-pencil-circle",
            status: "Wartet auf Prüfung",
            tone: "warning",
            spotlight: true,
            hint: "Eintrag öffnen",
          }),
        ],
        actions: [previewAction("Fehlenden Eintrag hinzufügen", "secondary", "mdi-plus-circle-outline", true, "Neuen Eintrag")],
        stepLabel: "Eintrag wählen",
      }),
      previewScreen("Eintrag bearbeiten", {
        type: "form",
        eyebrow: "Korrekturen",
        icon: "mdi-clock-edit-outline",
        title: "Neuer Eintrag",
        fields: [
          previewField("Uhrzeit", "17:06", { icon: "mdi-clock-outline", spotlight: true, hint: "24h-Zeit wählen" }),
          previewField("Typ auswählen", "Check-Out (Ende)", { icon: "mdi-logout", select: true }),
        ],
        actions: [previewAction("Abbrechen", "secondary"), previewAction("Speichern", "primary", "mdi-content-save", true, "Korrektur einreichen")],
        caption: "Nach dem Speichern erscheint der Eintrag orange als „Wartet auf Prüfung“.",
        stepLabel: "Eintrag speichern",
      }),
    ],
    prerequisites: ["Der betreffende Monat wurde noch nicht endgültig abgeschlossen."],
    steps: [
      step("Tag auswählen", "Öffne die Tagesansicht und wechsle zu dem vergangenen Datum mit dem fehlerhaften Eintrag."),
      step("Korrekturmodus öffnen", "Aktiviere die Korrektur und wähle den vorhandenen Eintrag oder „Fehlenden Eintrag hinzufügen“."),
      step("Zeiten erfassen", "Trage Beginn und Ende beziehungsweise Check-in und Check-out korrekt ein."),
      step("Begründung ergänzen", "Beschreibe kurz und sachlich, weshalb die Änderung erforderlich ist."),
      step("Zur Prüfung senden", "Speichere die Korrektur und kontrolliere den offenen Prüfstatus."),
    ],
    resultCheck: "Die Korrektur erscheint als offen beziehungsweise „Wartet auf Prüfung“ und verändert die offizielle Zeit erst nach Freigabe.",
    troubleshooting: [
      problem(
        "Der Tag lässt sich nicht mehr korrigieren",
        "Nach persönlicher Einreichung oder offiziellem Monatsabschluss sind Änderungen gesperrt. Wende dich an die Zeitverantwortlichen.",
      ),
      problem("Die neue Zeit ist noch nicht in der Bilanz", "Offene Korrekturen müssen zunächst in der Management-Übersicht entschieden werden."),
    ],
    relatedGuides: ["arbeitszeit-erfassen", "monat-einreichen", "zeiten-pruefen"],
  }),
  guide({
    slug: "monat-einreichen",
    title: "Persönlichen Monat prüfen und einreichen",
    summary: "Kontrolliere Zeiten und offene Punkte, bevor du deinen Monat verbindlich zur Prüfung sendest.",
    category: "time",
    aliases: ["monatsabschluss", "monat abschliessen", "einreichen", "zeitmonat"],
    accessGroups: anyRight("tt_employeeManage"),
    appTarget: { name: "timeTracker" },
    where: ["Dashboard", "Zeiterfassung", "Monat"],
    duration: "5 Min.",
    featured: true,
    visuals: [
      previewScreen("Monatswerte kontrollieren", {
        device: "tablet",
        eyebrow: "Zeiterfassung · Monat",
        icon: "mdi-calendar-month-outline",
        fields: [previewField("Monat", "August 2026", { icon: "mdi-calendar", select: true })],
        metrics: [
          previewMetric("IST-STUNDEN", "174.7 h", "Beispiel", "success"),
          previewMetric("SOLL (80%)", "176.0 h", "Beispiel"),
          previewMetric("DIFFERENZ", "−1.3 h", "Beispiel", "warning"),
          previewMetric("ÜBERSTUNDEN SALDO", "+4.2 h", "Beispiel", "success"),
        ],
        actions: [previewAction("Monat Abschliessen", "primary", "mdi-check-circle-outline", true, "Dialog öffnen")],
        stepLabel: "Monat prüfen",
      }),
      previewScreen("Monat verbindlich einreichen", {
        type: "status-confirm",
        eyebrow: "Monat abschliessen?",
        icon: "mdi-file-document-check-outline",
        title: "August 2026 · Beispiel",
        metrics: [previewMetric("Gearbeitet", "174.7 h"), previewMetric("Differenz", "−1.3 h", "Beispiel", "warning")],
        notice: {
          tone: "warning",
          icon: "mdi-alert-outline",
          title: "Nachträgliche Korrekturen sind nicht möglich.",
          text: "Prüfe alle Werte vor der Einreichung.",
        },
        actions: [previewAction("Abbrechen", "secondary"), previewAction("Einreichen", "primary", "mdi-check-all", true, "Verbindlich senden")],
        stepLabel: "Einreichen",
      }),
      previewScreen("Einreichungsstatus erkennen", {
        type: "status-confirm",
        eyebrow: "Zeiterfassung · August 2026",
        icon: "mdi-file-clock-outline",
        notice: { tone: "warning", icon: "mdi-information", title: "Eingereicht", text: "Wartet auf Prüfung." },
        chips: [
          { label: "Bereits eingereicht", icon: "mdi-lock", tone: "warning" },
          { label: "Monat abgeschlossen", icon: "mdi-check-circle", tone: "success" },
        ],
        caption: "Nach der Freigabe zeigt die App „Monat abgeschlossen“ und „Genehmigt durch Centerleitung.“",
        stepLabel: "Status verfolgen",
      }),
    ],
    prerequisites: ["Alle Arbeitstage des Monats sind beendet.", "Fehlende Stempelzeiten wurden als Korrektur eingereicht."],
    steps: [
      step("Studio und Monat prüfen", "Kontrolliere oben das ausgewählte Studio und den abzuschließenden Monat."),
      step("Monatswerte kontrollieren", "Prüfe Arbeitszeit, Soll, Differenz, Abwesenheiten, Pausen und offene Korrekturen."),
      step("Offene Punkte bereinigen", "Reiche fehlende Korrekturen ein und kläre auffällige Tage, bevor du fortfährst."),
      step("Monat abschließen", "Wähle „Monat Abschliessen“ und lies den Bestätigungsdialog vollständig."),
      step("Verbindlich einreichen", "Bestätige die Einreichung. Danach wartet der Monat auf die offizielle Prüfung."),
    ],
    resultCheck:
      "Der Monat zeigt „Eingereicht“ und darunter „Wartet auf Prüfung.“ Erst nach Freigabe durch die verantwortliche Stelle gilt er als abgeschlossen.",
    troubleshooting: [
      problem(
        "Nach dem Einreichen fällt ein Fehler auf",
        "Die persönliche Einreichung sperrt nachträgliche Korrekturen. Melde den konkreten Tag an die prüfende Stelle.",
      ),
      problem("Der Monat kann nicht eingereicht werden", "Kontrolliere offene Korrekturen, laufende Arbeitszeiten und Hinweise im Abschlussdialog."),
    ],
    relatedGuides: ["zeitkorrektur", "monatsabschluss-pruefen"],
  }),
  guide({
    slug: "kunde-suchen",
    title: "Kunden suchen und Kundenakte öffnen",
    summary: "Finde den richtigen Kunden und öffne seine Betreuung, Verträge oder Trainingsdaten.",
    category: "customers",
    aliases: ["kundensuche", "benutzersuche", "mitglied suchen", "kundenakte", "profil"],
    accessGroups: anyRight("u_search"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Dashboard", "Benutzersuche"],
    duration: "2 Min.",
    featured: true,
    visuals: [
      previewScreen("Kunden suchen", {
        device: "tablet",
        eyebrow: "KUNDEN & BENUTZER",
        icon: "mdi-account-search-outline",
        path: ["Dashboard", "Benutzersuche"],
        search: { placeholder: "Suche", value: "Anna Keller", spotlight: true, hint: "Name oder E-Mail" },
        sectionLabel: "DIREKTSUCHE",
        sectionMeta: "Wen möchtest du öffnen?",
        rows: [
          previewRow("Anna Keller (anna.keller@beispiel.ch)", "Beispieldaten", {
            icon: "mdi-account-circle-outline",
            spotlight: true,
            hint: "Kundenakte öffnen",
          }),
          previewRow("Anna Keller-Müller (anna.mueller@beispiel.ch)", "Beispieldaten", { icon: "mdi-account-circle-outline" }),
        ],
        caption: "Beispieldaten – in der echten Suche immer Name und E-Mail vergleichen.",
        stepLabel: "Treffer auswählen",
      }),
      previewScreen("Kundenakte öffnen", {
        device: "tablet",
        eyebrow: "KUNDENAKTE",
        icon: "mdi-account-heart-outline",
        status: { label: "E-Mail verifiziert", tone: "success" },
        title: "Anna Keller · Beispiel",
        tabs: [
          { label: "Betreuung", active: true },
          { label: "Training" },
          { label: "Stammdaten" },
          { label: "Verträge" },
          { label: "Interaktionen" },
          { label: "11-Tage-Karte" },
        ],
        actions: [
          previewAction("Termin planen", "primary", "mdi-calendar-plus", true, "Schnellaktion", anyRight("sm_appointment_management")),
          previewAction("Trainingspläne", "secondary", "mdi-dumbbell", false, "", anyRight("tp_create")),
          previewAction("Interaktion", "secondary", "mdi-message-plus-outline", false, "", anyRight("tp_create")),
          previewAction("Anamnese", "secondary", "mdi-clipboard-pulse-outline", false, "", anyRight("ab_create", "ab_edit")),
          previewAction(
            "Verträge & Pakete",
            "secondary",
            "mdi-card-account-details-outline",
            false,
            "",
            anyRight("c_create_customer", "c_open_contracts"),
          ),
        ],
        stepLabel: "Bereich wählen",
      }),
    ],
    prerequisites: ["Du hast mindestens Name oder E-Mail-Adresse des Kunden."],
    steps: [
      step("Benutzersuche öffnen", "Öffne im Dashboard die Benutzersuche."),
      step("Suchbegriff eingeben", "Suche nach Name oder E-Mail-Adresse und warte auf die Trefferliste."),
      step("Treffer vergleichen", "Vergleiche Name und weitere sichtbare Merkmale, bevor du eine Person auswählst."),
      step("Kundenakte öffnen", "Öffne den passenden Treffer und kontrolliere den Namen in der Kundenakte."),
      step("Bereich wählen", "Wechsle je nach Aufgabe zu Betreuung, Training, Stammdaten, Verträgen, Interaktionen oder 11-Tage-Karte."),
    ],
    resultCheck: "Die richtige Kundenakte ist geöffnet und der benötigte Bereich kann ausgewählt werden.",
    troubleshooting: [
      problem(
        "Es erscheint kein Treffer",
        "Prüfe die Schreibweise, suche alternativ nach der E-Mail-Adresse und kontrolliere den aktuellen Studio-Kontext.",
      ),
      problem("Die Benutzersuche fehlt im Dashboard", "Für den Einstieg wird das Recht zur Kundensuche benötigt. Lasse dein Profil prüfen."),
    ],
    relatedGuides: ["kundeninteraktion", "anamnese", "trainingsplan-erstellen", "vertraege-pakete"],
  }),
  guide({
    slug: "trainerpoint-live",
    title: "Trainerpoint Live und Check-in-Pool verwenden",
    summary: "Sieh, wer im Center ist, wer heute ausgecheckt hat und welche Kunden Betreuung benötigen.",
    category: "calendar",
    aliases: ["tp live", "checkin pool", "live fläche", "im center", "ausgecheckt"],
    accessGroups: anyRight("live_checkins"),
    appTarget: { name: "workCalendar" },
    where: ["Arbeitskalender", "Trainerpoint Live"],
    duration: "3 Min.",
    featured: true,
    visuals: [
      previewScreen("Live-Ansicht wählen", {
        device: "tablet",
        eyebrow: "TRAINERPOINT LIVE",
        icon: "mdi-account-multiple-outline",
        title: "Kunden im Center",
        tabs: [
          { label: "Im Center", icon: "mdi-account-multiple-outline", active: true, count: 8 },
          { label: "Heute ausgecheckt", icon: "mdi-logout", count: 21 },
          { label: "Empfehlungen", icon: "mdi-account-alert-outline", count: 3 },
          { label: "Termin bald", icon: "mdi-calendar-clock-outline", count: 2 },
          { label: "Ohne Folgetermin", icon: "mdi-calendar-question-outline", count: 4 },
        ],
        search: { placeholder: "Name oder E-Mail eingeben", spotlight: true, hint: "Person direkt suchen" },
        caption: "Auf dem Smartphone werden zuerst „Im Center“ und „Heute ausgecheckt“ gezeigt.",
        stepLabel: "Live-Pool filtern",
      }),
      previewScreen("Kundenkarte bearbeiten", {
        eyebrow: "IM CENTER",
        icon: "mdi-account-circle-outline",
        title: "Marco Meier · Beispiel",
        status: { label: "Seit 42 Min.", tone: "success" },
        metrics: [
          previewMetric("Check-in", "17:12", "Heute", "success"),
          previewMetric("Trainingsplan", "Aktuell", "vor 8 Tagen"),
          previewMetric("Anamnese", "Vorhanden", "vor 3 Monaten"),
        ],
        actions: [
          previewAction("Termin planen", "primary", "mdi-calendar-plus", true, "Terminart wählen", anyRight("sm_appointment_management")),
          previewAction(
            "Interaktion",
            "secondary",
            "mdi-message-text-plus-outline",
            false,
            "",
            allGroups({ anyRights: ["u_search"] }, { anyRights: ["tp_create"] }),
          ),
        ],
        stepLabel: "Aktion starten",
      }),
      previewScreen("Terminart auswählen", {
        eyebrow: "TERMIN PLANEN",
        icon: "mdi-calendar-plus",
        title: "Was möchtest du für Marco eintragen?",
        search: { placeholder: "Terminart suchen" },
        rows: [
          previewRow("Anamnese", "30 Minuten", { icon: "mdi-calendar", spotlight: true, hint: "Terminart wählen" }),
          previewRow("Trainingsplan", "45 Minuten", { icon: "mdi-calendar" }),
          previewRow("Betreuungsgespräch", "30 Minuten", { icon: "mdi-calendar" }),
        ],
        stepLabel: "Terminart wählen",
      }),
    ],
    prerequisites: ["Das richtige Studio und der heutige Kalendertag sind ausgewählt."],
    steps: [
      step("Trainerpoint Live öffnen", "Öffne im Arbeitskalender das Personen- beziehungsweise Trainerpoint-Live-Symbol."),
      step(
        "Live-Ansicht wählen",
        "Wechsle zwischen „Im Center“ und „Heute ausgecheckt“. Auf Mobilgeräten stehen zunächst diese beiden Kernansichten zur Verfügung.",
      ),
      step("Kunden direkt suchen", "Nutze die Suche nach Name oder E-Mail, wenn die Person nicht in der aktuellen Check-in-Liste erscheint."),
      step("Kundenkarte prüfen", "Kontrolliere Check-in-Zeit, Besuchsdauer und vorhandene Betreuungssignale."),
      step("Passende Aktion öffnen", "Öffne je nach Recht die Kundenakte, einen Termin, eine Interaktion oder einen Trainingsbereich."),
    ],
    resultCheck: "Die aktuelle Kundenkarte ist geöffnet und Check-in- beziehungsweise Betreuungsinformationen passen zum ausgewählten Studio.",
    troubleshooting: [
      problem(
        "Die Live-Daten werden nicht angezeigt",
        "Nutze „Nochmal versuchen“ beziehungsweise aktualisiere die Ansicht und kontrolliere Studio sowie Verbindung.",
      ),
      problem(
        "Aktionen auf einer Kundenkarte sind gesperrt",
        "Für weiterführende Aktionen wird ein verknüpftes Roody-Kundenkonto und das jeweilige Funktionsrecht benötigt.",
      ),
    ],
    relatedGuides: ["kunde-suchen", "checkin-status", "kundeninteraktion", "termin-erstellen"],
  }),
  guide({
    slug: "checkin-status",
    title: "Check-in, Terminabschluss und No-show bearbeiten",
    summary: "Setze den passenden Terminstatus und unterscheide Terminabschluss vom Center-Check-out.",
    category: "calendar",
    aliases: ["erschienen", "nicht gekommen", "no show", "terminstatus", "erledigt", "checkout"],
    accessGroups: anyRight("sm_appointment_management"),
    appTarget: { name: "workCalendar" },
    where: ["Arbeitskalender", "Termin öffnen", "Terminart und Status"],
    duration: "3 Min.",
    visuals: [
      previewScreen("Terminstatus setzen", {
        eyebrow: "Terminart & Status",
        icon: "mdi-calendar-clock",
        title: "Anamnese · Beispieltermin",
        fields: [
          previewField("Termin-Vorlage", "Anamnese", { icon: "mdi-calendar", select: true }),
          previewField("Titel", "Anamnese · Anna Keller", { icon: "mdi-format-title", wide: true }),
          previewField("Status", "Erschienen (Check-in)", {
            icon: "mdi-check-circle",
            select: true,
            wide: true,
            spotlight: true,
            hint: "Status wählen",
          }),
        ],
        chips: [
          { label: "Geplant", icon: "mdi-calendar-clock" },
          { label: "Erschienen (Check-in)", icon: "mdi-check-circle", tone: "success", spotlight: true, hint: "Status wählen" },
          { label: "Erledigt", icon: "mdi-check-circle", tone: "success" },
          { label: "Abgesagt", icon: "mdi-cancel", tone: "danger" },
          { label: "No-Show (Nicht erschienen)", icon: "mdi-eye-off", tone: "warning" },
        ],
        actions: [previewAction("Abbrechen", "secondary"), previewAction("Speichern", "primary", "mdi-content-save", true, "Status übernehmen")],
        stepLabel: "Status speichern",
      }),
      previewScreen("No-show erkennen", {
        eyebrow: "Termin-Verlauf",
        icon: "mdi-account-alert-outline",
        status: { label: "Nicht gekommen", tone: "warning" },
        notice: {
          tone: "warning",
          icon: "mdi-account-alert-outline",
          title: "Aktuell „Nicht gekommen“",
          text: "Für eine neue Planung den Termin gezielt zurück auf „Geplant“ setzen.",
        },
        actions: [
          previewAction("Löschen", "danger", "mdi-trash-can"),
          previewAction("Abbrechen", "secondary"),
          previewAction("Speichern", "primary", "mdi-check", true, "Änderung sichern"),
        ],
        stepLabel: "Verlauf prüfen",
      }),
    ],
    prerequisites: ["Der Kundentermin ist im gewählten Tag sichtbar."],
    steps: [
      step("Termin öffnen", "Öffne den betreffenden Terminblock im Arbeitskalender.", "", anyRight("sm_appointment_management")),
      step("Statusbereich wählen", "Öffne „Terminart & Status“ und prüfe den aktuellen Zustand.", "", anyRight("sm_appointment_management")),
      step(
        "Passenden Status setzen",
        "Wähle Geplant, Erschienen, Erledigt, Abgesagt oder No-Show entsprechend dem tatsächlichen Ablauf.",
        "„Erledigt“ schließt den Termin ab; der Center-Check-out ist ein eigener Live-Vorgang.",
        anyRight("sm_appointment_management"),
      ),
      step(
        "Änderung speichern",
        "Speichere und kontrolliere Farbe beziehungsweise Status des Terminblocks.",
        "",
        anyRight("sm_appointment_management"),
      ),
      step(
        "Live-Status kontrollieren",
        "Nutze Trainerpoint Live, um aktive und heute ausgecheckte Center-Besuche zu prüfen.",
        "",
        anyRight("live_checkins"),
      ),
    ],
    resultCheck:
      "Der Termin zeigt den korrekten Status; aktive beziehungsweise abgeschlossene Center-Besuche erscheinen in der passenden Live-Ansicht.",
    troubleshooting: [
      problem(
        "Ich finde keinen Terminstatus „Check-out“",
        "Der Termin wird mit „Erledigt“ abgeschlossen. Der Center-Check-out wird getrennt im Check-in-System geführt.",
      ),
      problem(
        "Ein No-show soll wieder geplant werden",
        "Öffne den Termin aus der Nicht-gekommen-Ansicht und setze ihn zurück auf den passenden geplanten Status.",
      ),
    ],
    relatedGuides: ["trainerpoint-live", "termin-erstellen", "termine-verwalten"],
  }),
  guide({
    slug: "kundeninteraktion",
    title: "Kundeninteraktion dokumentieren",
    summary: "Halte Gesprächsgrund, Ort und Ergebnis nachvollziehbar in der Kundenakte fest.",
    category: "customers",
    aliases: ["interaktion", "kontakt", "kundengespräch", "notiz", "betreuung dokumentieren"],
    accessGroups: customerAction("tp_create"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte oder Trainerpoint Live", "Interaktion"],
    duration: "4 Min.",
    visuals: [
      previewScreen("Trainer auswählen", {
        eyebrow: "Kundeninteraktion",
        type: "stepper",
        icon: "mdi-face-agent",
        progress: { label: "Schritt 1 von 3", caption: "Trainer · Kunde · Details", value: 34 },
        fields: [
          previewField("Trainer auswählen...", "Max Mustertrainer · Beispiel", {
            icon: "mdi-account-check",
            select: true,
            spotlight: true,
            hint: "Trainer kontrollieren",
          }),
        ],
        chips: [{ label: "Max Mustertrainer · Beispiel", icon: "mdi-account-check", tone: "success", active: true }],
        actions: [
          previewAction("Trainer wechseln", "secondary", "mdi-swap-horizontal"),
          previewAction("Weiter", "primary", "mdi-chevron-right", true, "Zum Kunden"),
        ],
        stepLabel: "Trainer bestätigen",
      }),
      previewScreen("Kunde auswählen", {
        eyebrow: "Kundeninteraktion",
        type: "stepper",
        icon: "mdi-account-search-outline",
        progress: { label: "Schritt 2 von 3", caption: "Trainer · Kunde · Details", value: 67 },
        fields: [
          previewField("Kunde auswählen...", "Anna Keller · Beispiel", {
            icon: "mdi-account-check",
            select: true,
            spotlight: true,
            hint: "Kunde kontrollieren",
          }),
        ],
        chips: [{ label: "Anna Keller · Beispiel", icon: "mdi-account-check", tone: "success", active: true }],
        actions: [
          previewAction("Neuen roody Account erstellen", "secondary", "mdi-account-plus-outline"),
          previewAction("Kunde wechseln", "secondary", "mdi-swap-horizontal"),
          previewAction("Weiter", "primary", "mdi-chevron-right", true, "Zu Details"),
        ],
        stepLabel: "Kunde bestätigen",
      }),
      previewScreen("Gespräch dokumentieren", {
        device: "tablet",
        type: "form",
        eyebrow: "Kundeninteraktion · Details",
        icon: "mdi-message-text-plus-outline",
        fields: [
          previewField("Was wurde besprochen?", "Neuer Plan vereinbaren", {
            icon: "mdi-format-list-bulleted",
            select: true,
            spotlight: true,
            hint: "Grund wählen",
          }),
          previewField("Wo fand die Besprechung statt?", "Trainingsfläche", { icon: "mdi-map-marker-outline", select: true }),
          previewField("Termin abgemacht?", "Ja", { icon: "mdi-calendar-check", select: true }),
          previewField("Datum", "20.08.2026", { icon: "mdi-calendar" }),
          previewField("Uhrzeit", "10:30", { icon: "mdi-clock-outline" }),
          previewField("Welche Pakete wurden besprochen?", "Trainingsbetreuung · Beispiel", {
            icon: "mdi-package-variant",
            select: true,
            wide: true,
          }),
          previewField("Pakete auswählen", "1 Paket gewählt", { icon: "mdi-checkbox-multiple-marked-outline", select: true }),
          previewField("Kurs empfehlen...", "Kein Kurs gewählt", { icon: "mdi-account-group-outline", select: true }),
          previewField("Notizen", "Trainingsplan-Termin für nächste Woche vereinbart.", { icon: "mdi-text-box-outline", wide: true }),
        ],
        chips: [{ label: "Kontaktdaten dürfen mit Allianz geteilt werden", icon: "mdi-toggle-switch", tone: "success", switch: true, active: true }],
        actions: [
          previewAction("Zurück", "secondary", "mdi-chevron-left"),
          previewAction("Speichern", "primary", "mdi-content-save", true, "Interaktion sichern"),
        ],
        caption: "Alle Namen und Inhalte in dieser Vorschau sind synthetische Beispiele.",
        stepLabel: "Details speichern",
      }),
    ],
    prerequisites: ["Die richtige Kundenakte ist ausgewählt.", "Du kennst Anlass und Ergebnis des Kontakts."],
    steps: [
      step("Interaktion öffnen", "Wähle in der Kundenakte oder auf der Trainerpoint-Live-Karte die Aktion „Interaktion“."),
      step("Personen kontrollieren", "Prüfe Kunde und ausführenden Trainer, bevor du Inhalte erfasst."),
      step("Grund und Ort erfassen", "Wähle Gesprächsgrund und Ort des Kontakts aus."),
      step("Notiz ergänzen", "Dokumentiere kurz, sachlich und handlungsorientiert, was besprochen oder vereinbart wurde."),
      step(
        "Zusatzangaben und Speichern",
        "Ergänze bedingte Angaben zu Termin, Kurs oder Paket, falls sie eingeblendet werden, und speichere anschließend.",
      ),
    ],
    resultCheck: "Eine Erfolgsmeldung erscheint und der neue Eintrag ist im Bereich „Interaktionen“ der Kundenakte sichtbar.",
    troubleshooting: [
      problem("Speichern ist nicht möglich", "Prüfe die Pflichtangaben Gesprächsgrund, Ort und Notiz sowie alle zusätzlich eingeblendeten Felder."),
      problem(
        "Die Aktion fehlt in Trainerpoint Live",
        "Prüfe, ob die Karte mit einem Roody-Kundenkonto verknüpft ist, und öffne die Kundenakte alternativ über die Suche.",
      ),
    ],
    relatedGuides: ["kunde-suchen", "trainerpoint-live", "termin-erstellen"],
  }),
  guide({
    slug: "anamnese",
    title: "Anamnese öffnen oder bearbeiten",
    summary: "Finde vorhandene Anamnesen oder erfasse mit dem geführten Formular einen neuen Eintrag.",
    category: "customers",
    aliases: ["anamnesebogen", "gesundheitsfragen", "anamnese erstellen", "anamnese bearbeiten"],
    accessGroups: customerAction("ab_create", "ab_edit"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte", "Anamnese"],
    duration: "5 Min.",
    visuals: [
      previewScreen("Anamnesen verwalten", {
        device: "tablet",
        eyebrow: "GESUNDHEIT & CHECK",
        icon: "mdi-clipboard-pulse-outline",
        title: "Anamnesebögen",
        rows: [
          previewRow("Anamnese vom 05.06.2026", "Erstellt von Max Mustertrainer · Beispiel", { icon: "mdi-file-document-check-outline" }),
          previewRow("Anamnese vom 18.01.2025", "Erstellt von Beispieltrainerin", { icon: "mdi-file-document-outline" }),
        ],
        actions: [
          previewAction("Ansehen", "secondary", "mdi-eye-outline"),
          previewAction("Bearbeiten", "secondary", "mdi-pencil-outline"),
          previewAction("Neue Anamnese", "primary", "mdi-plus", true, "Neu starten"),
          previewAction("Schliessen", "secondary"),
        ],
        stepLabel: "Eintrag wählen",
      }),
      previewScreen("Benutzer-Auswahl", {
        type: "stepper",
        eyebrow: "Anamnesebogen",
        icon: "mdi-account-multiple-check-outline",
        tabs: [{ label: "Benutzer-Auswahl", active: true }, { label: "Änderungsverlauf" }],
        fields: [
          previewField("Visum (Anamnesebogen ersteller)", "Max Mustertrainer · Beispiel", {
            icon: "mdi-account-check",
            select: true,
            spotlight: true,
            hint: "Ersteller prüfen",
          }),
          previewField("Kunde", "Anna Keller · Beispiel", { icon: "mdi-account-heart-outline", select: true }),
        ],
        chips: [
          { label: "Trainer: Max Mustertrainer", icon: "mdi-account-check", active: true, tone: "success" },
          { label: "Kunde: Anna Keller", icon: "mdi-account-check", active: true, tone: "success" },
        ],
        actions: [
          previewAction("Neuen Account erstellen", "secondary", "mdi-account-plus-outline"),
          previewAction("Kunde ändern", "secondary", "mdi-swap-horizontal"),
          previewAction("Weiter", "primary", "mdi-chevron-right", true, "Fragen starten"),
        ],
        stepLabel: "Personen bestätigen",
      }),
      previewScreen("Anamnesebogen abschliessen", {
        eyebrow: "Anamnesebogen",
        icon: "mdi-clipboard-text-outline",
        progress: { label: "Abschluss", caption: "Haftungsausschluss · Unterschriften", value: 88 },
        chips: [
          { label: "Trainer: Max Mustertrainer", icon: "mdi-account-check", active: true },
          { label: "Kunde: Anna Keller", icon: "mdi-account-check", active: true },
        ],
        rows: [
          previewRow("Haftungsausschluss", "Gelesen und bestätigt", { icon: "mdi-check-circle-outline", checked: true }),
          previewRow("Unterschrift Kunde", "Vorhanden", { icon: "mdi-draw", checked: true }),
          previewRow("Unterschrift Trainer", "Noch erforderlich", {
            icon: "mdi-draw",
            status: "Offen",
            tone: "warning",
            spotlight: true,
            hint: "Unterschreiben",
          }),
        ],
        actions: [
          previewAction("Zurück", "secondary", "mdi-chevron-left"),
          previewAction("Abschliessen", "primary", "mdi-chevron-right", true, "Anamnese speichern"),
        ],
        caption: "Die Gesundheitsfragen bleiben dynamisch und werden hier bewusst nicht nachgebildet.",
        stepLabel: "Abschluss prüfen",
      }),
    ],
    prerequisites: ["Die richtige Kundenakte ist geöffnet.", "Gesundheitsangaben werden gemeinsam mit dem Kunden korrekt erfasst."],
    steps: [
      step("Anamnese öffnen", "Wähle in der Kundenakte die Schnellaktion beziehungsweise den Bereich „Anamnese“."),
      step(
        "Vorhandenen Eintrag prüfen",
        "Öffne einen bestehenden Eintrag zum Ansehen oder Bearbeiten, wenn bereits eine passende Anamnese vorliegt.",
      ),
      step("Neue Anamnese starten", "Wähle „Neue Anamnese“, wenn ein neuer Stand dokumentiert werden soll."),
      step(
        "Formular ausfüllen",
        "Arbeite die eingeblendeten Fragen vollständig durch. Das Formular führt abhängig von den Antworten durch die benötigten Angaben.",
      ),
      step("Absenden und kontrollieren", "Speichere die Anamnese und prüfe anschließend Datum und Ersteller in der Liste."),
    ],
    resultCheck: "Der Anamnese-Eintrag erscheint mit aktuellem Datum und Ersteller in der Kundenakte.",
    troubleshooting: [
      problem(
        "Nach dem Speichern öffnet sich eine weitere Empfehlung",
        "Ein Weiterlauf zu passenden GF-Empfehlungen kann Teil des vorgesehenen Ablaufs sein.",
      ),
      problem(
        "Ein benötigtes Feld ist nicht sichtbar",
        "Beantworte die vorherigen Fragen vollständig; weitere Felder können abhängig von den Angaben eingeblendet werden.",
      ),
    ],
    relatedGuides: ["kunde-suchen", "trainingsplan-erstellen", "kundeninteraktion"],
  }),
  guide({
    slug: "trainingsplan-erstellen",
    title: "Trainingsplan erstellen und bearbeiten",
    summary: "Lege für den ausgewählten Kunden einen neuen Plan an und bearbeite Splits und Übungen.",
    category: "training",
    aliases: ["tp erstellen", "workout plan", "split", "übungen", "trainingsplan v2"],
    accessGroups: customerAction("tp_create"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte", "Training", "Neuen Plan erstellen"],
    duration: "7 Min.",
    featured: true,
    visuals: [
      previewScreen("Kunde und Trainer wählen", {
        device: "tablet",
        eyebrow: "Neuer Trainingsplan",
        icon: "mdi-dumbbell",
        title: "Wähle einen Kunden und Trainer aus.",
        fields: [
          previewField("Kunde", "Anna Keller · Beispiel", { icon: "mdi-account-heart-outline", select: true, spotlight: true, hint: "Kunde prüfen" }),
          previewField("Trainer", "Max Mustertrainer", { icon: "mdi-account-check-outline", select: true }),
        ],
        notice: {
          tone: "info",
          icon: "mdi-content-save-edit-outline",
          title: "Gespeicherter Entwurf",
          text: "Ein vorhandener Entwurf kann wiederhergestellt oder verworfen werden.",
        },
        actions: [
          previewAction("Nein, neu starten", "secondary"),
          previewAction("Ja, wiederherstellen", "primary", "mdi-restore", true, "Entwurf laden"),
        ],
        stepLabel: "Personen bestätigen",
      }),
      previewScreen("Trainingsaufteilung wählen", {
        device: "tablet",
        eyebrow: "Planstruktur",
        icon: "mdi-view-grid-plus-outline",
        title: "Wie soll der Plan aufgeteilt sein?",
        rows: [
          previewRow("Ganzkörper-Training", "Ein Trainingstag", { icon: "mdi-dumbbell" }),
          previewRow("Oberkörper-/Unterkörper Split", "Zwei Trainingstage", {
            icon: "mdi-view-split-vertical",
            spotlight: true,
            hint: "Split auswählen",
          }),
          previewRow("Push/Pull/Beine-Split", "Drei Trainingstage", { icon: "mdi-view-grid-outline" }),
          previewRow("Bro-Split", "Mehrere Muskelgruppen-Tage", { icon: "mdi-view-grid-plus-outline" }),
          previewRow("Eigener Split erstellen", "Individuelle Trainingstage", { icon: "mdi-plus-circle-outline" }),
        ],
        actions: [previewAction("Weiter", "primary", "mdi-chevron-right", true, "Zum Builder")],
        stepLabel: "Split bestimmen",
      }),
      previewScreen("Übungen im Builder anordnen", {
        device: "desktop",
        eyebrow: "Trainingsplan-Builder",
        icon: "mdi-dumbbell",
        tabs: [{ label: "Oberkörper", active: true }, { label: "Unterkörper" }, { label: "+", icon: "mdi-plus" }],
        search: { placeholder: "Übung suchen...", value: "Brust", spotlight: true, hint: "Übung finden" },
        sectionLabel: "Verfügbare Übungen",
        sectionMeta: "Oberkörper · 4 Übungen",
        rows: [
          previewRow("Brustpresse", "3 Sätze · 10–12 Wdh.", { icon: "mdi-dumbbell", status: "Geräteeinstellungen gespeichert", tone: "success" }),
          previewRow("Latzug", "3 Sätze · 10–12 Wdh.", { icon: "mdi-dumbbell", status: "Geräteeinstellungen gespeichert", tone: "success" }),
          previewRow("Schulterpresse", "Noch keine Sätze hinterlegt", {
            icon: "mdi-dumbbell",
            status: "Offen",
            tone: "warning",
            spotlight: true,
            hint: "Sätze ergänzen",
          }),
        ],
        actions: [
          previewAction("Filter", "secondary", "mdi-filter-variant"),
          previewAction("Speichern", "primary", "mdi-content-save", true, "Fertigstellen"),
        ],
        stepLabel: "Übungen speichern",
      }),
    ],
    prerequisites: ["Die richtige Kundenakte ist geöffnet.", "Trainingsziel und grobe Aufteilung sind mit dem Kunden geklärt."],
    steps: [
      step("Training öffnen", "Wechsle in der Kundenakte zum Bereich „Training“ und prüfe den bisherigen Planbestand."),
      step("Neuen Plan starten", "Wähle „Neuen Plan erstellen“ und kontrolliere den ausgewählten Kunden."),
      step("Plan strukturieren", "Erfasse die Planangaben und lege die benötigten Splits beziehungsweise Trainingstage an."),
      step("Übungen hinzufügen", "Füge Übungen hinzu und vervollständige die im Builder geforderten Trainingsangaben."),
      step("Speichern", "Speichere den Plan und kehre zur Planübersicht des Kunden zurück."),
      step("Später bearbeiten", "Öffne den Plan über „Bearbeiten“, ändere Inhalte und speichere erneut."),
    ],
    resultCheck: "Der neue oder geänderte Plan erscheint mit Trainer, Datum und Splits in der Trainingsübersicht des Kunden.",
    troubleshooting: [
      problem("Der Plan lässt sich nicht speichern", "Prüfe alle Pflichtfelder des Builders sowie unvollständige Splits oder Übungen."),
      problem(
        "Beim Bearbeiten öffnet sich eine andere Ansicht",
        "Klassische Pläne und Split-Pläne verwenden unterschiedliche Editoren. Wähle die Aktion direkt beim gewünschten Plan.",
      ),
    ],
    relatedGuides: ["kunde-suchen", "trainingsplan-verwalten", "anamnese"],
  }),
  guide({
    slug: "trainingsplan-verwalten",
    title: "Trainingsplan kopieren, drucken, archivieren oder reaktivieren",
    summary: "Verwalte bestehende Kundenpläne und übernimm bei Bedarf frühere Splits.",
    category: "training",
    aliases: ["plan kopieren", "plan drucken", "plan löschen", "wiederherstellen", "splits übernehmen"],
    accessGroups: customerAction("tp_create"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte", "Training", "Planaktionen"],
    duration: "5 Min.",
    visuals: [
      previewScreen("Trainingsplan auswählen", {
        device: "tablet",
        eyebrow: "TRAININGSBETREUUNG",
        icon: "mdi-clipboard-text-outline",
        title: "Trainingspläne",
        chips: [
          { label: "Split-basiert", active: true, tone: "success" },
          { label: "Klassisch" },
          { label: "Gelöschte anzeigen (1)", icon: "mdi-delete-outline", switch: true },
        ],
        rows: [
          previewRow("Oberkörper / Unterkörper", "05.08.2026 · Max Mustertrainer · 2 Splits", {
            icon: "mdi-view-grid-plus-outline",
            status: "Split-basiert",
            tone: "success",
            spotlight: true,
            hint: "Planaktionen",
          }),
          previewRow("Ganzkörper Basis", "10.01.2025 · 8 Übungen", { icon: "mdi-clipboard-text-outline", status: "Klassisch", tone: "neutral" }),
        ],
        actions: [previewAction("Splits übernehmen", "secondary", "mdi-content-copy"), previewAction("Neuen Plan erstellen", "primary", "mdi-plus")],
        stepLabel: "Plan öffnen",
      }),
      previewScreen("Planaktion ausführen", {
        eyebrow: "Split-basierter Plan",
        icon: "mdi-view-grid-plus-outline",
        title: "Oberkörper / Unterkörper",
        status: { label: "Split-basiert", tone: "success" },
        metrics: [previewMetric("Splits", "2"), previewMetric("Übungen", "12"), previewMetric("Erstellt", "05.08.26")],
        actions: [
          previewAction("Kopieren", "secondary", "mdi-content-copy"),
          previewAction("Bearbeiten", "primary", "mdi-pencil-outline", true, "Plan ändern"),
          previewAction("Drucken", "secondary", "mdi-printer-outline"),
          previewAction("Löschen", "danger", "mdi-delete-outline"),
          previewAction("Wiederherstellen", "secondary", "mdi-backup-restore"),
        ],
        caption: "Gelöschte V2-Pläne können über „Gelöschte anzeigen“ wiederhergestellt werden.",
        stepLabel: "Aktion wählen",
      }),
      previewScreen("Split auswählen", {
        type: "status-confirm",
        eyebrow: "Trainingsplan drucken",
        icon: "mdi-printer-outline",
        title: "Split auswählen",
        subtitle: "Welcher Teil des Trainingsplans soll heruntergeladen werden?",
        rows: [
          previewRow("Oberkörper", "6 Übungen", { icon: "mdi-download-outline", spotlight: true, hint: "PDF herunterladen" }),
          previewRow("Unterkörper", "6 Übungen", { icon: "mdi-download-outline" }),
        ],
        actions: [previewAction("Schliessen", "secondary")],
        stepLabel: "PDF-Split wählen",
      }),
    ],
    prerequisites: ["Mindestens ein Trainingsplan ist in der Kundenakte vorhanden."],
    steps: [
      step("Plan auswählen", "Öffne den Bereich „Training“ und wähle die Aktionen beim gewünschten klassischen oder Split-Plan."),
      step("Kopieren oder bearbeiten", "Nutze Kopieren für eine neue Grundlage oder Bearbeiten für Änderungen am bestehenden Plan."),
      step("Drucken", "Wähle beim Drucken den gewünschten Split und starte die PDF-Erstellung beziehungsweise den Download."),
      step("Archivieren", "Wähle Löschen, wenn ein V2-Plan nicht mehr aktiv angezeigt werden soll, und bestätige den Vorgang."),
      step("Wiederherstellen", "Aktiviere „Gelöschte anzeigen“ und reaktiviere den benötigten Plan."),
      step("Splits übernehmen", "Nutze bei einem neuen Plan die Übernahmefunktion, wenn passende Splits aus einem früheren Plan angeboten werden."),
    ],
    resultCheck: "Kopie, Download oder Archivstatus sind in der Planübersicht sichtbar; ein reaktivierter Plan erscheint wieder normal.",
    troubleshooting: [
      problem("Beim Drucken entsteht kein PDF", "Stelle sicher, dass ein druckbarer Split ausgewählt wurde, und erlaube den Download im Browser."),
      problem(
        "Eine Aktion fehlt bei einem alten Plan",
        "Klassische Pläne bieten weniger Aktionen als Split-/V2-Pläne. Öffne nur die für den jeweiligen Plantyp angebotenen Funktionen.",
      ),
    ],
    relatedGuides: ["trainingsplan-erstellen", "kunde-suchen"],
  }),
  guide({
    slug: "termin-erstellen",
    title: "Kundentermin erstellen, bearbeiten oder verschieben",
    summary: "Plane einen Termin aus der Kundenakte oder direkt im Arbeitskalender und passe ihn später an.",
    category: "calendar",
    aliases: ["termin planen", "appointment", "termin verschieben", "termin finder", "kalendertermin"],
    accessGroups: anyRight("sm_appointment_management"),
    appTarget: { name: "workCalendar" },
    where: ["Kundenakte", "Termin planen", "Arbeitskalender"],
    duration: "5 Min.",
    featured: true,
    visuals: [
      previewScreen("Terminzeit festlegen", {
        device: "tablet",
        eyebrow: "Anamnese · Termin",
        icon: "mdi-calendar-plus",
        fields: [
          previewField("Start", "12.08.2026 · 10:30", { icon: "mdi-clock-start", spotlight: true, hint: "Start prüfen" }),
          previewField("Dauer", "30 Min", { icon: "mdi-timer-outline", select: true }),
          previewField("Ende", "11:00", { icon: "mdi-clock-end" }),
          previewField("Mitarbeiter *", "Max Mustertrainer", { icon: "mdi-account-check-outline", select: true, wide: true }),
        ],
        actions: [previewAction("Freie Termine finden (Termin-Finder)", "secondary", "mdi-calendar-search", true, "Freie Zeiten anzeigen")],
        stepLabel: "Zeit und Trainer",
      }),
      previewScreen("Kunde zuordnen", {
        device: "tablet",
        eyebrow: "Termin · Kunde",
        icon: "mdi-account-search",
        sectionLabel: "Kunde *",
        search: { placeholder: "Kunden suchen...", value: "Anna Keller", spotlight: true, hint: "Kunde auswählen" },
        rows: [
          previewRow("Anna Keller · Beispiel", "anna.keller@beispiel.ch", {
            icon: "mdi-account-circle-outline",
            status: "Ausgewählt",
            tone: "success",
          }),
        ],
        chips: [
          { label: "Kunde suchen", icon: "mdi-account-search", active: true },
          { label: "Kein Account", icon: "mdi-account-off" },
          { label: "Zweite Person hinzufügen (+30 Min)", icon: "mdi-account-plus" },
        ],
        stepLabel: "Kunde bestätigen",
      }),
      previewScreen("Termin wiederholen (Serie)", {
        type: "form",
        eyebrow: "Terminserie",
        icon: "mdi-calendar-sync",
        fields: [
          previewField("Frequenz", "Wöchentlich", { icon: "mdi-repeat", select: true }),
          previewField("Intervall", "1 Woche", { icon: "mdi-numeric-1-circle-outline", select: true }),
          previewField("Seriendauer", "Endet nach 8 Terminen", { icon: "mdi-calendar-end", select: true, wide: true }),
        ],
        chips: [
          { label: "Täglich" },
          { label: "Wöchentlich", active: true, tone: "success" },
          { label: "Monatlich" },
          { label: "An folgenden Tagen" },
          { label: "Di", active: true, tone: "success" },
        ],
        actions: [previewAction("Abbrechen", "secondary"), previewAction("Erstellen", "primary", "mdi-check", true, "Termin speichern")],
        stepLabel: "Serie festlegen",
      }),
    ],
    prerequisites: [
      "Der richtige Kunde ist ausgewählt oder du kennst den gewünschten Kalenderslot.",
      "Terminart und zuständiger Mitarbeiter sind geklärt.",
    ],
    steps: [
      step(
        "Terminfluss starten",
        "Wähle in der Kundenakte „Termin planen“ und anschließend die Terminart. Der Kunde wird in den Kalenderdialog übernommen.",
      ),
      step(
        "Zeit und Mitarbeiter prüfen",
        "Kontrolliere Datum, Start, Dauer beziehungsweise Ende und die zuständige Person. Nutze bei Bedarf den Termin-Finder.",
      ),
      step("Details ergänzen", "Prüfe die Kundenzuordnung und ergänze nur notwendige Notizen zum Termin."),
      step("Termin erstellen", "Speichere mit „Erstellen“ und kontrolliere den neuen Block im Arbeitskalender."),
      step("Termin bearbeiten", "Öffne einen bestehenden Terminblock, passe die erlaubten Felder an und speichere erneut."),
      step(
        "Termin verschieben",
        "Wähle über Langdruck beziehungsweise Rechtsklick „Verschieben“ oder nutze den Termin-Finder und bestätige einen freien Zielslot.",
        "",
        null,
        "verschieben",
      ),
    ],
    resultCheck: "Der Terminblock steht beim richtigen Datum, Mitarbeiter, Zeitraum und Kunden.",
    troubleshooting: [
      problem(
        "Der Termin lässt sich nicht erstellen",
        "Kontrolliere Mitarbeiterfreigabe, Terminart, Zeitkollisionen und einen tatsächlich freien Slot.",
      ),
      problem(
        "Die Schnellaktion fehlt in der Kundenakte",
        "Für die Terminverwaltung wird das entsprechende Funktionsrecht benötigt. Öffne den Kalender nur, wenn die Aktion für dein Profil vorgesehen ist.",
      ),
    ],
    relatedGuides: ["kunde-suchen", "termine-verwalten", "checkin-status", "trainerpoint-live"],
  }),
  guide({
    slug: "termine-verwalten",
    title: "Termine suchen, Status ändern und Serien verwalten",
    summary: "Finde vergangene und kommende Termine und verwalte Status oder Wiederholungen kontrolliert.",
    category: "calendar",
    aliases: ["kalender durchsuchen", "serientermin", "terminserie", "wiederholen", "status ändern"],
    accessGroups: anyRight("sm_appointment_management"),
    appTarget: { name: "workCalendar" },
    where: ["Arbeitskalender", "Kalender durchsuchen"],
    duration: "6 Min.",
    visuals: [
      previewScreen("Kalender durchsuchen", {
        device: "tablet",
        eyebrow: "Terminkalender",
        icon: "mdi-calendar-search",
        title: "Kalender durchsuchen",
        chips: [
          { label: "Heute", icon: "mdi-calendar-today", active: true },
          { label: "Gesamter Kalender", icon: "mdi-database-search-outline" },
          { label: "Aktuell & Zukunft", icon: "mdi-calendar-arrow-right", active: true },
          { label: "Vergangenheit", icon: "mdi-history" },
          { label: "Aufgaben & Ämtli", icon: "mdi-clipboard-off-outline", switch: true, value: "Aus" },
        ],
        search: { placeholder: "Kunde, Termin, Mitarbeiter oder Uhrzeit ...", value: "Anna", spotlight: true, hint: "Mindestens 3 Zeichen" },
        rows: [
          previewRow("Anamnese · Anna Keller", "12.08.2026 · 10:30–11:00", {
            icon: "mdi-calendar",
            status: "Geplant",
            tone: "info",
            spotlight: true,
            hint: "Treffer öffnen",
          }),
          previewRow("Trainingsplan · Anna Keller", "19.08.2026 · 16:00–16:45", { icon: "mdi-calendar", status: "Geplant", tone: "info" }),
        ],
        stepLabel: "Treffer finden",
      }),
      previewScreen("Serientermine verwalten", {
        type: "list-detail",
        eyebrow: "Serientermine",
        icon: "mdi-calendar-sync",
        title: "Serientermine verwalten",
        subtitle: "Termin · Max Mustertrainer · 12.08.–30.09.2026 · Beispieldaten",
        rows: [
          previewRow("12.08.2026 · 10:30", "Termin · Max Mustertrainer", {
            icon: "mdi-calendar-outline",
            actions: ["mdi-pencil-outline", "mdi-trash-can-outline"],
          }),
          previewRow("19.08.2026 · 10:30", "Termin · Max Mustertrainer", {
            icon: "mdi-calendar-outline",
            actions: ["mdi-pencil-outline", "mdi-trash-can-outline"],
          }),
        ],
        actions: [
          previewAction("Serie fortführen", "primary", "mdi-calendar-plus", true, "Weitere Termine anlegen"),
          previewAction("Ganze Serie löschen", "danger", "mdi-trash-can-outline"),
        ],
        stepLabel: "Serie bearbeiten",
      }),
    ],
    prerequisites: ["Das richtige Studio ist ausgewählt."],
    steps: [
      step("Kalendersuche öffnen", "Öffne im Arbeitskalender die Lupe „Kalender durchsuchen“."),
      step(
        "Suchbereich festlegen",
        "Suche zuerst am aktuellen Tag. Aktiviere bei Bedarf „Gesamter Kalender“, gib mindestens drei Zeichen ein und wähle Zukunft oder Vergangenheit.",
      ),
      step("Treffer öffnen", "Wähle den passenden Treffer. Der Kalender wechselt zum zugehörigen Datum und öffnet den Eintrag."),
      step("Status bearbeiten", "Öffne „Terminart & Status“, wähle den passenden Zustand und speichere."),
      step(
        "Serie anlegen",
        "Aktiviere beim Erstellen „Termin wiederholen“, definiere Frequenz, Intervall, Wochentage und Ende und prüfe Vorschau sowie Konflikte.",
      ),
      step(
        "Serie verwalten",
        "Öffne über die Serienaktionen einzelne Vorkommen, führe die Serie fort oder lösche bewusst ein Vorkommen, dieses und künftige oder die gesamte Serie.",
      ),
    ],
    resultCheck: "Der Treffer öffnet das richtige Datum; Status und gewünschte Serienvorkommen sind aktualisiert.",
    troubleshooting: [
      problem(
        "Ein Suchtreffer ist nicht mehr vorhanden",
        "Der Eintrag wurde möglicherweise verschoben oder gelöscht. Starte die Suche neu und kontrolliere den Zeitraum.",
      ),
      problem(
        "Beim Löschen einer Serie bin ich unsicher",
        "Prüfe vor Bestätigung genau, ob nur dieser Termin, dieser und künftige Termine oder alle Vorkommen betroffen sein sollen.",
      ),
    ],
    relatedGuides: ["termin-erstellen", "checkin-status", "aufgaben-aemtli"],
  }),
  guide({
    slug: "aufgaben-aemtli",
    title: "Aufgaben und Ämtli im Arbeitskalender bearbeiten",
    summary: "Plane Vorlagen für Mitarbeitende ein und halte Status, Notizen und Checklisten aktuell.",
    category: "calendar",
    aliases: ["ämtli", "aemtli", "aufgabe", "checkliste", "studiotask", "vorlage"],
    accessGroups: [{ anyRights: ["tt_employeeManage", "g_employeemanager", "sm_create"] }],
    appTarget: { name: "workCalendar" },
    where: ["Arbeitskalender", "Vorlagen", "Ämtli oder Aufgabe"],
    duration: "5 Min.",
    visuals: [
      previewScreen("Vorlage auswählen", {
        device: "tablet",
        eyebrow: "Arbeitskalender",
        icon: "mdi-view-dashboard-outline",
        title: "Vorlagen",
        tabs: [
          { label: "Ämtli", icon: "mdi-checkbox-marked-circle-outline", active: true },
          { label: "Aufgabe", icon: "mdi-clipboard-text" },
          { label: "Termin", icon: "mdi-calendar-star" },
        ],
        search: { placeholder: "Suchen…", value: "Getränke", spotlight: true, hint: "Vorlage finden" },
        rows: [
          previewRow("Getränkestation prüfen", "Tägliche Ämtli · Heute eingeplant", {
            icon: "mdi-drag-variant",
            status: "FÄLLIG",
            tone: "warning",
            spotlight: true,
            hint: "Einplanen",
          }),
          previewRow("Kühlschrank auffüllen · Beispiel", "Bei Bedarf · Manuell einfügen", {
            icon: "mdi-drag-variant",
            status: "Ämtli Vorlage",
            tone: "neutral",
          }),
        ],
        stepLabel: "Ämtli finden",
      }),
      previewScreen("Aufgabe dokumentieren", {
        eyebrow: "Aufgabe",
        icon: "mdi-format-list-checks",
        title: "Getränkestation prüfen",
        fields: [
          previewField("Beschreibung & Notiz", "Bestand kontrolliert, Wasser nachgefüllt.", { icon: "mdi-text-box-outline", wide: true }),
          previewField("Neu hinzufügen (Enter drücken)", "", { icon: "mdi-plus", placeholder: "Checklistenpunkt ergänzen", wide: true }),
          previewField("Aufgaben-Status", "Erledigt", { icon: "mdi-check-circle", select: true, wide: true, spotlight: true, hint: "Status setzen" }),
        ],
        sectionLabel: "Checkliste",
        sectionMeta: "2 / 3 Erledigt",
        rows: [
          previewRow("Wasser auffüllen", "", { checked: true }),
          previewRow("Becher nachfüllen", "", { checked: true }),
          previewRow("Kühlschrank reinigen", "", { icon: "mdi-checkbox-blank-circle-outline", spotlight: true, hint: "Punkt abhaken" }),
        ],
        chips: [{ label: "Geplant" }, { label: "Erledigt", icon: "mdi-check-circle", tone: "success", spotlight: true, hint: "Status setzen" }],
        actions: [
          previewAction("Anleitung anzeigen", "secondary", "mdi-information-outline"),
          previewAction("Speichern", "primary", "mdi-content-save", true, "Aufgabe aktualisieren"),
        ],
        stepLabel: "Erledigen",
      }),
    ],
    prerequisites: ["Das richtige Datum ist geöffnet.", "Eine passende Vorlage ist im Vorlagen-Panel verfügbar."],
    steps: [
      step("Vorlage finden", "Öffne das Vorlagen-Panel, wähle Ämtli oder Aufgabe und suche die benötigte Vorlage."),
      step(
        "Zeit und Person wählen",
        "Wähle Datum, Uhrzeit, Dauer und einen freien, berechtigten Mitarbeiter. Auf Desktop kann die Vorlage je nach Typ auf den Zielslot gezogen werden.",
      ),
      step("Eintrag erstellen", "Kontrolliere die Angaben und erstelle den Kalenderblock."),
      step("Arbeit dokumentieren", "Öffne den Block, pflege Notiz und Checkliste und setze den Aufgabenstatus nach Abschluss auf „Erledigt“."),
      step("Ergebnis prüfen", "Kontrolliere, dass Checkliste und Status den tatsächlichen Arbeitsstand zeigen."),
    ],
    resultCheck: "Der Eintrag steht beim richtigen Mitarbeiter und Datum; erledigte Checklistenpunkte und Status sind gespeichert.",
    troubleshooting: [
      problem(
        "Die Vorlage ist ausgegraut",
        "Bereits eingeplante oder nur zu bestimmten Zeiten fällige Vorlagen können gesperrt sein. Prüfe Datum und bestehenden Block.",
      ),
      problem(
        "Keine Person kann ausgewählt werden",
        "Wähle eine andere Zeit oder berechtigte Person, wenn Mitarbeitende nicht eingeplant, beschäftigt oder für die Vorlage eingeschränkt sind.",
      ),
    ],
    relatedGuides: ["heutiger-einsatz", "termine-verwalten", "schichten-verwalten"],
  }),
  guide({
    slug: "vertraege-pakete",
    title: "Verträge, Pakete und Upgrades finden",
    summary: "Ordne Kundenvertrag, Zusatzpakete und Upgrades der richtigen Oberfläche zu.",
    category: "customers",
    aliases: ["vertrag", "paket", "upgrade", "tac vertrag", "roody vertrag"],
    accessGroups: customerAction("c_create_customer", "c_open_contracts"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte", "Verträge und Pakete"],
    duration: "5 Min.",
    visuals: [
      previewScreen("Aktuellen Vertrag prüfen", {
        device: "tablet",
        eyebrow: "AKTUELLER TAC-VERTRAG",
        icon: "mdi-card-account-details-star-outline",
        title: "Premium Mitgliedschaft · Beispiel",
        metrics: [
          previewMetric("Vertragszeitraum", "01.01.–31.12.26"),
          previewMetric("Verbleibend", "142 Tage", "Bis Vertragsende", "success"),
          previewMetric("Studio", "Zürich", "Beispiel"),
        ],
        sectionLabel: "AKTIVE PAKETE & ZUSATZLEISTUNGEN",
        rows: [previewRow("Trainingsbetreuung Plus · Beispiel", "Gültig bis 31.12.2026", { icon: "mdi-check" })],
        actions: [previewAction("Upgrade lösen", "primary", "mdi-trending-up", true, "Upgrade starten")],
        stepLabel: "Vertrag einordnen",
      }),
      previewScreen("Vertragsquelle unterscheiden", {
        device: "tablet",
        eyebrow: "Verträge",
        icon: "mdi-file-document-multiple-outline",
        tabs: [{ label: "roody Verträge", active: true }, { label: "TAC Verträge" }],
        rows: [
          previewRow("Mitgliedschaft · Beispiel", "01.01.–31.12.2026", {
            icon: "mdi-file-document-outline",
            status: "Akzeptiert",
            tone: "success",
            spotlight: true,
            hint: "Vertrag öffnen",
          }),
          previewRow("Upgrade Betreuung", "Erstellt am 05.08.2026", { icon: "mdi-trending-up", status: "Offen", tone: "warning" }),
        ],
        actions: [
          previewAction("Ansehen", "secondary", "mdi-eye"),
          previewAction("Bearbeiten", "secondary", "mdi-pencil"),
          previewAction("Drucken", "secondary", "mdi-printer"),
        ],
        caption: "Vertrags- und Paketnamen sind in dieser Vorschau reine Beispieldaten.",
        stepLabel: "Quelle und Status",
      }),
      previewScreen("TAC-Verträge verwalten", {
        device: "tablet",
        type: "list-detail",
        eyebrow: "TAC Verträge",
        icon: "mdi-card-account-details-outline",
        chips: [{ label: "Abgelaufene Verträge anzeigen", icon: "mdi-toggle-switch-off-outline", switch: true }],
        rows: [previewRow("TAC-Vertrag · Beispiel", "01.01.–31.12.2026", { icon: "mdi-file-document-outline" })],
        actions: [previewAction("Kündigungs-Assistent öffnen", "secondary", "mdi-file-cancel", true, "Assistent starten")],
        stepLabel: "Historie oder Kündigung",
      }),
    ],
    prerequisites: ["Bei kundenbezogenen Vorgängen wurde die richtige Person geöffnet."],
    steps: [
      step(
        "Kundenverträge prüfen",
        "Öffne in der Kundenakte „Verträge“ beziehungsweise „Verträge & Pakete“ und kontrolliere laufenden Vertrag, Zusatzpakete und offene Upgrades.",
        "",
        customerAction("c_create_customer", "c_open_contracts"),
      ),
      step(
        "Vertragsquelle unterscheiden",
        "Wechsle bei Bedarf zwischen Roody- und TAC-Verträgen und blende abgelaufene Einträge nur gezielt ein.",
        "",
        customerAction("c_create_customer", "c_open_contracts"),
      ),
      step(
        "Globale Verträge öffnen",
        "Nutze im Dashboard die Bereiche Übersicht, Offen, Temporär oder Abgelehnt für vertragsübergreifende Arbeit.",
        "",
        [{ anyRights: ["c_create_customer", "c_open_contracts", "c_create"] }],
      ),
      step("Upgrades prüfen", "Öffne die Upgrade-Ansichten Akzeptiert, Offen oder Abgelehnt und kontrolliere Kunde und Status.", "", [
        { anyRights: ["c_create_customer", "c_open_contracts"] },
      ]),
      step("Lead suchen", "Öffne Weiterempfehlungen beziehungsweise Alle Leads und suche nach Name, E-Mail, Telefon, Werber oder Status.", "", [
        { anyRights: ["af_downloadList", "af_view", "af_edit"] },
      ]),
    ],
    resultCheck: "Kundenname, Vertragszeitraum sowie Paket- oder Upgradestatus passen zum gesuchten Vorgang.",
    troubleshooting: [
      problem(
        "Es wird keine laufende Mitgliedschaft angezeigt",
        "Prüfe darunter ältere TAC- und Roody-Verträge und warte den vollständigen Datenabruf ab, bevor du etwas neu erfasst.",
        anyRight("c_create_customer", "c_open_contracts", "c_create"),
      ),
      problem(
        "Ein Bereich fehlt",
        "Verträge, Upgrades und Leads verwenden unterschiedliche Rechte. Nutze nur die für dein Profil sichtbaren Teilbereiche.",
      ),
    ],
    relatedGuides: ["kunde-suchen", "kundeninteraktion"],
  }),
  guide({
    slug: "arbeitsplan-erstellen",
    title: "Monatlichen Arbeitsplan erstellen",
    summary: "Lege einen Planungsmonat an und bereite Mitarbeiter, Pensum und offene Schichten vor.",
    category: "planning",
    aliases: ["schichtplan erstellen", "neuen monat planen", "einsatzplan", "roster"],
    accessGroups: anyRight("tt_office", "sm_create"),
    appTarget: { name: "shiftManagementPlan" },
    where: ["Dashboard", "Arbeitsplanung", "Planung erstellen"],
    duration: "7 Min.",
    featured: true,
    visuals: [
      previewScreen("Planungsübersicht öffnen", {
        device: "desktop",
        type: "overview",
        eyebrow: "Arbeitsplanung",
        icon: "mdi-calendar-edit",
        title: "Planung erstellen",
        metrics: [
          previewMetric("Besetzt", "173", "Beispiel", "success"),
          previewMetric("Gesamt", "186", "Schichten"),
          previewMetric("Offen", "13", "Noch zuweisen", "warning"),
        ],
        rows: [
          previewRow("September 2026", "Entwurf · Team sieht ihn nicht", {
            icon: "mdi-calendar-edit",
            status: "Entwurf",
            tone: "warning",
            spotlight: true,
            hint: "Monat öffnen",
          }),
          previewRow("August 2026", "Aktueller Monat · freigegeben", { icon: "mdi-calendar-check", status: "Freigegeben", tone: "success" }),
        ],
        actions: [previewAction("Neuen Monat planen", "primary", "mdi-plus", true, "Wizard starten")],
        stepLabel: "Monat wählen",
      }),
      previewScreen("Planungsmonat anlegen", {
        device: "desktop",
        type: "stepper",
        eyebrow: "Neuen Monat planen",
        icon: "mdi-calendar-plus",
        progress: { label: "Vorlage → Spezialtage → Mitarbeiter → Zuweisung → Einsatzplan", caption: "Schritt 2 von 5 · Spezialtage", value: 40 },
        fields: [
          previewField("Studio wählen", "Zürich · Beispiel", { icon: "mdi-map-marker-outline", select: true }),
          previewField("Monat", "September 2026", { icon: "mdi-calendar-month", select: true, spotlight: true, hint: "Monat wählen" }),
          previewField("Wochenvorlage", "Standardwoche Zürich", { icon: "mdi-calendar-week", select: true }),
          previewField("Spezialtage", "2 Feiertage erkannt", { icon: "mdi-calendar-star" }),
        ],
        actions: [
          previewAction("Zurück", "secondary", "mdi-chevron-left"),
          previewAction("Weiter", "primary", "mdi-chevron-right", true, "Mitarbeiter prüfen"),
        ],
        stepLabel: "Grundlage wählen",
      }),
      previewScreen("Mitarbeitende vorbereiten", {
        device: "desktop",
        type: "form",
        eyebrow: "Neuen Monat planen · Mitarbeiter",
        icon: "mdi-account-cog-outline",
        fields: [
          previewField("Planungspensum", "80 %", { icon: "mdi-percent-outline", spotlight: true, hint: "Pensum prüfen" }),
          previewField("Überstunden mit dem Soll verrechnen", "Aktiv", { icon: "mdi-swap-horizontal" }),
        ],
        sectionLabel: "Mitarbeiter-Einstellungen",
        rows: [
          previewRow("Tage in anderem Studio", "Keine hinterlegt", { icon: "mdi-map-marker-multiple-outline" }),
          previewRow("Fixe Schichten", "2 pro Woche", { icon: "mdi-calendar-lock-outline" }),
          previewRow("Abwesenheiten & Monatsereignisse", "1 Eintrag", { icon: "mdi-calendar-alert-outline", status: "Prüfen", tone: "warning" }),
        ],
        actions: [previewAction("Weiter zur Zuweisung", "primary", "mdi-arrow-right", true, "Assistent öffnen")],
        stepLabel: "Planungspensum prüfen",
      }),
    ],
    prerequisites: [
      "Planungsmonat und verantwortliches Studio sind festgelegt.",
      "Verfügbarkeiten und bekannte Abwesenheiten sind möglichst aktuell.",
    ],
    steps: [
      step("Planungsübersicht öffnen", "Öffne Arbeitsplanung und wähle „Neuen Monat planen“ beziehungsweise den gewünschten vorhandenen Monat."),
      step("Grundlagen kontrollieren", "Prüfe Mitarbeiter, Planungspensum, Verfügbarkeiten und hinterlegte Abwesenheiten."),
      step("Plan aufbauen", "Nutze Planungsassistent und offene Schichten als Unterstützung und teile Schichten den passenden Mitarbeitenden zu."),
      step("Konflikte prüfen", "Kontrolliere offene Schichten, Überschneidungen und auffällige Pensumsabweichungen."),
      step("Entwurf sichern", "Speichere den aktuellen Planungsstand zunächst als Entwurf und prüfe die Monatskarte."),
    ],
    resultCheck: "Der Monat erscheint als Entwurf mit nachvollziehbaren Werten für besetzte, gesamte und offene Schichten.",
    troubleshooting: [
      problem("Eine Person fehlt in der Planung", "Prüfe Mitarbeiterzuordnung, monatliche Einstellungen und den ausgewählten Studio-/Teamkontext."),
      problem(
        "Der Assistent kann nicht alles besetzen",
        "Bearbeite Konflikte und offene Schichten manuell; der Entwurf kann bis zur Freigabe weiter angepasst werden.",
      ),
    ],
    relatedGuides: ["arbeitsplan-freigeben", "schichten-verwalten", "zeiten-pruefen"],
  }),
  guide({
    slug: "arbeitsplan-freigeben",
    title: "Entwurf, Freigabe und Rücknahme eines Arbeitsplans verstehen",
    summary: "Veröffentliche einen geprüften Monatsplan und ziehe die Freigabe nur bewusst zurück.",
    category: "planning",
    aliases: ["plan freigeben", "plan veröffentlichen", "entwurf", "freigabe zurückziehen"],
    accessGroups: allGroups({ anyRights: ["tt_office", "sm_create"] }, { anyRights: ["g_employeemanager"] }),
    appTarget: { name: "shiftManagementPlan" },
    where: ["Dashboard", "Arbeitsplanung", "Monatsdetail"],
    duration: "4 Min.",
    visuals: [
      previewScreen("Plan freigeben", {
        device: "desktop",
        type: "overview",
        eyebrow: "Monatsplanung · September 2026",
        icon: "mdi-publish",
        status: { label: "Entwurf · fürs Team unsichtbar", tone: "warning" },
        metrics: [
          previewMetric("Besetzt", "184", "Beispiel", "success"),
          previewMetric("Gesamt", "186", "Schichten"),
          previewMetric("Offen", "2", "Noch zuweisen", "warning"),
        ],
        actions: [previewAction("Plan freigeben", "primary", "mdi-publish", true, "Bestätigung öffnen")],
        stepLabel: "Status prüfen",
      }),
      previewScreen("Einsatzplan fürs Team freigeben?", {
        type: "status-confirm",
        eyebrow: "Freigabe bestätigen",
        icon: "mdi-account-multiple-check-outline",
        notice: {
          tone: "warning",
          icon: "mdi-alert-outline",
          title: "2 offene Schichten",
          text: "Diese Schichten sind noch nicht zugewiesen und werden dem Team nicht angezeigt.",
        },
        actions: [previewAction("Noch nicht", "secondary"), previewAction("Jetzt aufschalten", "primary", "mdi-publish", true, "Plan freigeben")],
        stepLabel: "Aufschalten bestätigen",
      }),
      previewScreen("Freigabe zurückziehen?", {
        type: "status-confirm",
        eyebrow: "Freigegebener Einsatzplan",
        icon: "mdi-publish-off",
        status: { label: "Freigegeben", tone: "success" },
        notice: { tone: "info", icon: "mdi-information-outline", text: "Du kannst den Plan bearbeiten und jederzeit erneut freigeben." },
        actions: [
          previewAction("Freigegeben lassen", "secondary"),
          previewAction("Freigabe zurückziehen", "danger", "mdi-publish-off", true, "Als Entwurf verbergen"),
        ],
        stepLabel: "Rücknahme bewusst bestätigen",
      }),
    ],
    prerequisites: ["Alle kritischen offenen Schichten und Konflikte wurden geprüft."],
    steps: [
      step("Status erkennen", "Kontrolliere auf Monatskarte und Detailseite, ob der Plan Entwurf, Freigegeben oder Abgeschlossen ist."),
      step("Plan final prüfen", "Prüfe Besetzung, offene Schichten, Pensum und bekannte Abwesenheiten ein letztes Mal."),
      step("Plan freigeben", "Wähle „Plan freigeben“ und bestätige die Veröffentlichung."),
      step(
        "Sichtbarkeit kontrollieren",
        "Prüfe, dass zugewiesene Schichten nun im Arbeitskalender und in der persönlichen Monatsplanung sichtbar sind.",
      ),
      step(
        "Freigabe nur bei Bedarf zurückziehen",
        "Ziehe die Freigabe zurück, wenn der Plan erneut verborgen und grundlegend überarbeitet werden muss.",
      ),
    ],
    resultCheck: "Der Status lautet „Freigegeben“ und Mitarbeitende sehen ihre zugewiesenen Schichten im vorgesehenen Kalender.",
    troubleshooting: [
      problem("Mitarbeitende sehen den Plan nicht", "Prüfe den Freigabestatus, den richtigen Monat und die konkrete Mitarbeiterzuweisung."),
      problem(
        "Der Plan wurde zu früh freigegeben",
        "Ziehe die Freigabe bewusst zurück, korrigiere den Entwurf und veröffentliche ihn nach erneuter Kontrolle wieder.",
      ),
    ],
    relatedGuides: ["arbeitsplan-erstellen", "schichten-verwalten", "heutiger-einsatz"],
  }),
  guide({
    slug: "schichten-verwalten",
    title: "Schichten zuweisen, ändern oder übertragen",
    summary: "Bearbeite Schichten im Planungs- oder Kalenderkontext und kontrolliere Auswirkungen auf Mitarbeitende.",
    category: "planning",
    aliases: ["schicht ändern", "schicht übertragen", "schicht zuweisen", "dienstplan"],
    accessGroups: anyRight("tt_office", "sm_create"),
    appTarget: { name: "workCalendar" },
    where: ["Arbeitskalender", "Mitarbeiter-Spalte", "Drei-Punkte-Menü"],
    duration: "5 Min.",
    visuals: [
      previewScreen("Schichtaktion wählen", {
        device: "desktop",
        type: "navigation",
        eyebrow: "Arbeitskalender · Mitarbeiter-Menü",
        icon: "mdi-dots-vertical",
        title: "Dienstag, 15.09. · Beispiel",
        rows: [
          previewRow("Schichtzeiten anpassen", "Zeit und Pausen bearbeiten", {
            icon: "mdi-clock-edit-outline",
            spotlight: true,
            hint: "Zeitdialog öffnen",
          }),
          previewRow("Als krank markieren", "Abwesenheit erfassen", { icon: "mdi-account-alert-outline" }),
          previewRow("Schicht übertragen", "Einer anderen Person zuweisen", { icon: "mdi-account-arrow-right-outline" }),
          previewRow("Schicht tauschen", "Zwei Mitarbeitende tauschen", { icon: "mdi-swap-horizontal" }),
        ],
        stepLabel: "Aktion auswählen",
      }),
      previewScreen("Schichtzeiten anpassen", {
        device: "tablet",
        type: "form",
        eyebrow: "Zeiten & Pausen",
        icon: "mdi-clock-edit-outline",
        chips: [
          { label: "Arbeitszeit", active: true, tone: "success" },
          { label: "Pause", tone: "warning" },
          { label: "Centeröffnung" },
          { label: "Centerschliessung" },
        ],
        fields: [
          previewField("Von", "08:00", { icon: "mdi-clock-start" }),
          previewField("Bis", "17:00", { icon: "mdi-clock-end" }),
          previewField("Begründung für die Änderung (Pflicht)", "Grund auswählen", {
            icon: "mdi-message-text-outline",
            select: true,
            wide: true,
            spotlight: true,
            hint: "Grund wählen",
          }),
        ],
        actions: [previewAction("Abbrechen", "secondary"), previewAction("Speichern", "primary", "mdi-content-save", true, "Zeiten sichern")],
        stepLabel: "Zeitänderung begründen",
      }),
      previewScreen("Schicht übertragen", {
        type: "form",
        eyebrow: "Schicht übertragen",
        icon: "mdi-account-switch-outline",
        title: "Dienstag, 15.09. · 17:00–22:00",
        fields: [
          previewField("Ziel-Mitarbeiter wählen (Verfügbare oben sortiert)", "Lea Beispiel", {
            icon: "mdi-account-arrow-right-outline",
            select: true,
          }),
          previewField("Grund für Übertragung", "Normaler Tausch", {
            icon: "mdi-message-text-outline",
            select: true,
            wide: true,
            spotlight: true,
            hint: "Grund wählen",
          }),
        ],
        chips: [
          { label: "Normaler Tausch", active: true },
          { label: "Mitarbeiter ist krank", tone: "warning" },
        ],
        notice: {
          tone: "warning",
          icon: "mdi-calendar-alert",
          title: "Auswirkungen kontrollieren",
          text: "Nicht stornierte Termine und Aufgaben dieses Tages können mitübertragen werden.",
        },
        actions: [
          previewAction("Abbrechen", "secondary"),
          previewAction("Übertragen", "primary", "mdi-account-arrow-right-outline", true, "Wechsel bestätigen"),
          previewAction("Tauschen", "secondary", "mdi-swap-horizontal"),
        ],
        stepLabel: "Auswirkung bestätigen",
      }),
    ],
    prerequisites: ["Datum, Studio und betroffene Person sind eindeutig geklärt."],
    steps: [
      step("Schicht finden", "Öffne den Planungsmonat oder das betreffende Datum im Arbeitskalender und wähle die Schicht."),
      step("Änderungsart wählen", "Entscheide, ob Zeit, Mitarbeiter oder Zuordnung geändert beziehungsweise die Schicht übertragen werden soll."),
      step("Konflikte kontrollieren", "Prüfe Überschneidungen, Verfügbarkeit, Pensum und bestehende Abwesenheiten der Zielperson."),
      step("Änderung speichern", "Speichere die Schichtänderung und kontrolliere den aktualisierten Block."),
      step(
        "Prüfstatus beachten",
        "Wenn die Änderung einen bereits laufenden oder eingereichten Monat betrifft, kontrolliere den zugehörigen Review-Status in der Zeiterfassung.",
      ),
    ],
    resultCheck: "Die Schicht steht zur richtigen Zeit bei der richtigen Person und offene Review-Hinweise sind berücksichtigt.",
    troubleshooting: [
      problem("Die Zielperson kann nicht gewählt werden", "Prüfe Studio, Verfügbarkeit, Überschneidung und Berechtigung der Person für die Schicht."),
      problem("Die Änderung blockiert den Monatsabschluss", "Öffne Zeiterfassung → Übersicht und bearbeite die ausstehende Schichtänderungsprüfung."),
    ],
    relatedGuides: ["arbeitsplan-erstellen", "arbeitsplan-freigeben", "monatsabschluss-pruefen"],
  }),
  guide({
    slug: "zeiten-pruefen",
    title: "Wünsche, Abwesenheiten und Zeitkorrekturen prüfen",
    summary: "Bearbeite offene Mitarbeiteranträge strukturiert in der Management-Übersicht.",
    category: "time",
    aliases: ["anträge prüfen", "ferien genehmigen", "korrektur genehmigen", "abwesenheit prüfen", "management"],
    accessGroups: anyRight("tt_management", "g_studioadmin"),
    appTarget: { name: "timeTrackerManagement" },
    where: ["Dashboard", "Zeiterfassung", "Übersicht"],
    duration: "6 Min.",
    featured: true,
    visuals: [
      previewScreen("Offene Vorgänge finden", {
        device: "desktop",
        eyebrow: "Zeiterfassung · Übersicht",
        icon: "mdi-clipboard-search-outline",
        tabs: [
          { label: "Zeiterfassung" },
          { label: "Anträge & Wünsche", active: true, count: 6 },
          { label: "Abwesenheit", count: 2 },
          { label: "Übersicht" },
        ],
        fields: [
          previewField("Von", "01.08.2026", { icon: "mdi-calendar-start" }),
          previewField("Bis", "31.08.2026", { icon: "mdi-calendar-end" }),
          previewField("Mitarbeiter suchen", "", { icon: "mdi-account-search", placeholder: "Name eingeben", wide: true }),
        ],
        chips: [
          { label: "Offen · 6", icon: "mdi-inbox", active: true, tone: "warning" },
          { label: "Genehmigt", icon: "mdi-check", tone: "success" },
          { label: "Abgelehnt", icon: "mdi-close", tone: "danger" },
        ],
        rows: [
          previewRow("Ferienantrag · Lea Beispiel", "18.–22. August · 5 Tage", {
            icon: "mdi-palm-tree",
            status: "Offen",
            tone: "warning",
            spotlight: true,
            hint: "Antrag prüfen",
          }),
          previewRow("Freiwunsch · Marco Beispiel", "28. August · Nachmittag", { icon: "mdi-calendar-heart", status: "Offen", tone: "warning" }),
        ],
        stepLabel: "Vorgang auswählen",
      }),
      previewScreen("Ferienantrag prüfen", {
        device: "tablet",
        type: "status-confirm",
        eyebrow: "Anträge & Wünsche",
        icon: "mdi-palm-tree",
        title: "Ferienantrag · Lea Beispiel",
        rows: [
          previewRow("18.08.2026", "Ferien · 8.4 h", { icon: "mdi-calendar" }),
          previewRow("19.08.2026", "Ferien · 8.4 h", { icon: "mdi-calendar" }),
          previewRow("20.–22.08.2026", "Weitere 3 Tage · Beispiel", { icon: "mdi-calendar-multiple" }),
        ],
        actions: [previewAction("Ablehnen", "danger", "mdi-close"), previewAction("Genehmigen", "primary", "mdi-check", true, "Antrag genehmigen")],
        stepLabel: "Antrag entscheiden",
      }),
      previewScreen("Korrektur vergleichen", {
        device: "desktop",
        type: "status-confirm",
        eyebrow: "Korrektur für den 10.08.2026",
        icon: "mdi-compare-horizontal",
        title: "Check-Out · Beispielkorrektur",
        metrics: [previewMetric("Vor Korrektur", "16:06", "Check-Out", "danger"), previewMetric("Nachher", "17:06", "Check-Out", "success")],
        chips: [
          { label: "Vor Korrektur", icon: "mdi-close-circle-outline", tone: "danger" },
          { label: "Nachher", icon: "mdi-check-circle-outline", tone: "success", active: true },
        ],
        actions: [
          previewAction("Ablehnen", "danger", "mdi-close"),
          previewAction("Akzeptieren", "primary", "mdi-check", true, "Korrektur genehmigen"),
        ],
        stepLabel: "Entscheiden",
      }),
    ],
    prerequisites: ["Das richtige Studio und der zu prüfende Monat sind ausgewählt."],
    steps: [
      step("Management öffnen", "Öffne Zeiterfassung → Übersicht und kontrolliere Studio und Zeitraum."),
      step("Offene Bereiche prüfen", "Wechsle zwischen Zeiterfassung, Anträge & Wünsche, Abwesenheit und Übersicht."),
      step(
        "Antrag öffnen",
        "Wähle den betreffenden Mitarbeiter und öffne den konkreten Ferien-, Frei-, Schicht-, Abwesenheits- oder Korrekturvorgang.",
      ),
      step("Grundlage vergleichen", "Vergleiche Antrag mit Arbeitsplan, vorhandenen Zeiten, Kommentar und bekannten Abwesenheiten."),
      step("Entscheidung erfassen", "Akzeptiere oder lehne den Vorgang mit der vorgesehenen Aktion ab und kontrolliere den neuen Status."),
    ],
    resultCheck: "Der Vorgang hat einen eindeutigen Entscheidungsstatus und offene Hinweise beim Mitarbeiter wurden aktualisiert.",
    troubleshooting: [
      problem(
        "Ein Vorgang ist nicht im erwarteten Tab",
        "Prüfe, ob es sich um Wunsch, Abwesenheit, Korrektur oder Schichtänderung handelt, und kontrolliere den ausgewählten Monat.",
      ),
      problem(
        "Eine Entscheidung ist nicht möglich",
        "Prüfe, ob der Monat bereits abgeschlossen oder der Vorgang von einer anderen Freigabestufe abhängig ist.",
      ),
    ],
    relatedGuides: ["ferien-wuensche", "krankmeldung", "zeitkorrektur", "monatsabschluss-pruefen"],
  }),
  guide({
    slug: "monatsabschluss-pruefen",
    title: "Schichtänderungen prüfen und Monat offiziell abschließen",
    summary: "Trenne persönliche Einreichung von der offiziellen Prüfung und schließe erst ohne offene Reviews ab.",
    category: "time",
    aliases: ["monat genehmigen", "offizieller monatsabschluss", "abrechnung akzeptieren", "schichtänderung review"],
    accessGroups: anyRight("tt_management", "g_studioadmin", "tt_office"),
    appTarget: null,
    where: ["Dashboard", "Zeiterfassung", "Übersicht oder Abrechnung"],
    duration: "7 Min.",
    visuals: [
      previewScreen("Eingereichten Monat öffnen", {
        device: "desktop",
        type: "list-detail",
        accessGroups: anyRight("tt_management", "g_studioadmin"),
        eyebrow: "Zeiterfassung · Mitarbeiter",
        icon: "mdi-account-clock-outline",
        rows: [
          previewRow("Lea Beispiel", "Vom Mitarbeiter eingereicht", {
            icon: "mdi-check-decagram",
            status: "eingereicht",
            tone: "warning",
            spotlight: true,
            hint: "Monat prüfen",
          }),
          previewRow("Marco Beispiel", "Keine offenen Änderungen", { icon: "mdi-account-check-outline", status: "abgeschlossen", tone: "info" }),
          previewRow("Nina Beispiel", "1 Änderung prüfen", { icon: "mdi-history", status: "1 Änderung prüfen", tone: "danger" }),
        ],
        stepLabel: "Mitarbeiter auswählen",
      }),
      previewScreen("Schichtänderungen prüfen", {
        device: "desktop",
        type: "list-detail",
        accessGroups: anyRight("tt_management", "g_studioadmin"),
        eyebrow: "Schichtänderungen",
        icon: "mdi-history",
        status: { label: "2 offen", tone: "warning" },
        notice: {
          tone: "warning",
          icon: "mdi-alert-outline",
          text: "Änderungen mit Schicht-Abgleich und Stempelzeiten vergleichen, bevor der Monat abgeschlossen wird.",
        },
        rows: [
          previewRow("Dienstag, 11.08.", "Grund: kurzfristiger Schichttausch", {
            icon: "mdi-swap-horizontal",
            status: "ungeprüft",
            tone: "warning",
            spotlight: true,
            hint: "Vorher/Nachher prüfen",
          }),
          previewRow("Freitag, 21.08.", "Grund: verlängerte Öffnungszeit", { icon: "mdi-clock-edit-outline", status: "ungeprüft", tone: "warning" }),
        ],
        metrics: [previewMetric("Vorher", "08:00–17:00", "Schicht", "danger"), previewMetric("Nachher", "09:00–18:00", "Schicht", "success")],
        fields: [previewField("Prüfnotiz (optional)", "Mit Einsatzplan abgeglichen", { icon: "mdi-note-edit-outline", wide: true })],
        actions: [previewAction("Alle als geprüft bestätigen", "primary", "mdi-check-all", true, "Reviews abschliessen")],
        stepLabel: "Reviews erledigen",
      }),
      previewScreen("Monatsabschluss bestätigen", {
        type: "status-confirm",
        accessGroups: anyRight("g_studioadmin"),
        eyebrow: "Monatsabschluss bestätigen",
        icon: "mdi-shield-check-outline",
        title: "Lea Beispiel",
        metrics: [
          previewMetric("Geleistet", "174.7 h"),
          previewMetric("Differenz", "−1.3 h", "Beispiel", "warning"),
          previewMetric("Überstunden Saldo (Neu)", "+4.2 h", "Beispiel", "success"),
        ],
        notice: { tone: "success", icon: "mdi-check-circle-outline", text: "Schichtänderungen geprüft – keine offen." },
        actions: [
          previewAction("Abbrechen", "secondary"),
          previewAction("Bestätigen", "primary", "mdi-check-all", true, "Monat offiziell abschliessen"),
        ],
        stepLabel: "Offiziell abschliessen",
      }),
      previewScreen("Office-Abrechnung entscheiden", {
        type: "status-confirm",
        accessGroups: anyRight("tt_office"),
        eyebrow: "Zeiterfassung · Abrechnung",
        icon: "mdi-file-document-check-outline",
        title: "Lea Beispiel · eingereicht",
        metrics: [
          previewMetric("Geleistet", "174.7 h"),
          previewMetric("Soll", "176.0 h"),
          previewMetric("Differenz", "−1.3 h", "Beispiel", "warning"),
        ],
        notice: { tone: "warning", icon: "mdi-alert-decagram-outline", text: "Zeiten, Pausen und Korrekturen vor dem Abschluss vollständig prüfen." },
        actions: [
          previewAction("Ablehnen", "danger", "mdi-close"),
          previewAction("Akzeptieren", "primary", "mdi-check-all", true, "Abschluss bestätigen"),
        ],
        stepLabel: "Abrechnung abschliessen",
      }),
    ],
    prerequisites: ["Der Mitarbeiter hat den Monat persönlich eingereicht.", "Alle relevanten Anträge und Abwesenheiten sind bekannt."],
    steps: [
      step("Eingereichten Monat öffnen", "Wähle in der Management- oder Office-Übersicht den markierten Mitarbeiter und Monat."),
      step(
        "Offene Reviews prüfen",
        "Kontrolliere Zeitkorrekturen, Pausenhinweise und insbesondere ausstehende Schichtänderungen.",
        "",
        anyRight("tt_management", "g_studioadmin"),
      ),
      step(
        "Plan und Ist vergleichen",
        "Vergleiche geplante Schichten, erfasste Arbeitszeit, Abwesenheiten und verbleibende Differenzen.",
        "",
        anyRight("tt_management", "g_studioadmin"),
      ),
      step(
        "Offene Punkte entscheiden",
        "Bearbeite alle Review-Vorgänge, bevor du den offiziellen Abschluss fortsetzt.",
        "",
        anyRight("tt_management", "g_studioadmin"),
      ),
      step(
        "Monat abschließen",
        "Öffne mit Office-Freigabe die Abrechnung, prüfe Zeiten, Pausen, Korrekturen, Soll, Ist und Differenz und bestätige oder lehne den Monatsabschluss ab.",
        "Der aktuelle Export zeigt den Schichtänderungs-Review in der Management-Übersicht. Prüfe ihn deshalb verbindlich vor dem separaten Office-Abschluss.",
        anyRight("tt_office", "g_studioadmin"),
      ),
    ],
    resultCheck:
      "Schichtänderungen und Korrekturen sind geprüft; mit Office-Freigabe zeigt die Abrechnung anschließend den offiziellen Status abgeschlossen oder abgelehnt.",
    troubleshooting: [
      problem("Der Abschluss ist blockiert", "Prüfe markierte Schichtänderungen, offene Korrekturen und Pausenhinweise in der Übersicht."),
      problem(
        "Der Mitarbeiter sieht nur „Eingereicht“",
        "Das ist der erwartete Zwischenstand bis zur offiziellen Prüfung. Persönliche Einreichung und Managementabschluss sind getrennte Schritte.",
      ),
    ],
    relatedGuides: ["monat-einreichen", "zeiten-pruefen", "schichten-verwalten"],
  }),
  guide({
    slug: "studio-verwalten",
    title: "Mitarbeitende, Öffnungszeiten und Reservationen verwalten",
    summary: "Finde die zentralen Studio-Verwaltungsbereiche und öffne nur die für dein Profil freigegebenen Aufgaben.",
    category: "studio",
    aliases: ["mitarbeiter verwalten", "öffnungszeiten", "reservationen", "studio admin", "reservationstyp"],
    accessGroups: [{ anyRights: ["g_employeemanager", "reservations_fetch", "reservations_createType", "g_studioadmin"] }],
    appTarget: { name: "dashboardHome" },
    where: ["Dashboard", "Studio"],
    duration: "5 Min.",
    visuals: [
      previewScreen("Mitarbeitende verwalten", {
        device: "desktop",
        type: "overview",
        accessGroups: anyRight("g_employeemanager"),
        eyebrow: "Studio · Mitarbeitende",
        icon: "mdi-account-group-outline",
        title: "Mitarbeiter",
        sectionLabel: "Studios und Teams",
        metrics: [
          previewMetric("Anzahl Mitarbeiter", "24"),
          previewMetric("Anzahl Stellenprozent", "1'760%"),
          previewMetric("Soll Std. Aug 26", "704 h"),
        ],
        rows: [
          previewRow("Lea Beispiel", "Trainerin · 80% · Team Zürich", {
            icon: "mdi-account-circle-outline",
            status: "Aktiv",
            tone: "success",
            spotlight: true,
            hint: "Mitarbeiter-Editor",
          }),
        ],
        actions: [previewAction("Mitarbeiter hinzufügen", "primary", "mdi-account-plus-outline", true, "Neu erfassen")],
        stepLabel: "Mitarbeiter öffnen",
      }),
      previewScreen("Öffnungszeiten pflegen", {
        device: "tablet",
        type: "form",
        accessGroups: anyRight("g_employeemanager"),
        eyebrow: "Öffnungszeiten nach Studio",
        icon: "mdi-clock-outline",
        status: { label: "Zeitgesteuert", tone: "info" },
        fields: [
          previewField("Übertitel", "Betreuungszeiten", { icon: "mdi-format-title" }),
          previewField("Text", "Montag bis Freitag", { icon: "mdi-text" }),
          previewField("Von", "08:00", { icon: "mdi-clock-start" }),
          previewField("Bis", "21:00", { icon: "mdi-clock-end" }),
        ],
        chips: [
          { label: "24H Check-in", icon: "mdi-door-open" },
          { label: "Zeitangabe hinzufügen", icon: "mdi-plus" },
        ],
        actions: [
          previewAction("Neuen Abschnitt hinzufügen", "secondary", "mdi-plus-box-outline"),
          previewAction("Speichern", "primary", "mdi-content-save", true, "Zeiten sichern"),
        ],
        stepLabel: "Zeiten speichern",
      }),
      previewScreen("Reservation erfassen", {
        device: "tablet",
        type: "stepper",
        accessGroups: anyRight("reservations_fetch"),
        eyebrow: "Reservationen · Tagesübersicht",
        icon: "mdi-calendar-clock-outline",
        metrics: [
          previewMetric("Buchungen", "14"),
          previewMetric("Belegte Slots", "18 / 24"),
          previewMetric("Auslastung", "75%", "Heute", "success"),
        ],
        progress: { label: "Neue Buchung", caption: "Für wen? · Was & Wann? · Zeit · Kontrolle", value: 50 },
        fields: [
          previewField("Mitglied", "Anna Keller · Beispiel", { icon: "mdi-account-search-outline", select: true }),
          previewField("Leistung", "Massage · Beispiel", { icon: "mdi-calendar-star", select: true, spotlight: true, hint: "Leistung wählen" }),
        ],
        actions: [previewAction("Neue Buchung", "primary", "mdi-plus", true, "Wizard öffnen")],
        stepLabel: "Buchung starten",
      }),
    ],
    prerequisites: ["Das richtige Studio ist ausgewählt."],
    steps: [
      step(
        "Mitarbeitende öffnen",
        "Wähle Studio → Mitarbeitende, kontrolliere Studio beziehungsweise Team und öffne die gewünschte Person.",
        "",
        anyRight("g_employeemanager"),
      ),
      step(
        "Öffnungszeiten öffnen",
        "Wähle Studio → Öffnungszeiten und prüfe zuerst Studio sowie geltenden Zeitraum.",
        "",
        anyRight("g_employeemanager"),
      ),
      step(
        "Reservationen öffnen",
        "Wähle Studio → Reservationen und nutze die vorhandenen Filter für den gesuchten Vorgang.",
        "",
        anyRight("reservations_fetch"),
      ),
      step(
        "Reservationstypen konfigurieren",
        "Öffne die Verwaltung der Reservationstypen nur, wenn du Typen erstellen oder anpassen darfst.",
        "",
        anyRight("reservations_createType"),
      ),
      step("Änderung kontrollieren", "Prüfe nach dem Speichern den sichtbaren Status im jeweils bearbeiteten Studio-Bereich."),
    ],
    resultCheck: "Der richtige Studio-Bereich ist geöffnet und die Änderung ist im ausgewählten Studio sichtbar.",
    troubleshooting: [
      problem(
        "Der Bereich „Studio“ zeigt nicht alle Unterpunkte",
        "Mitarbeitende, Öffnungszeiten und Reservationen besitzen getrennte Funktionsrechte und werden einzeln gefiltert.",
      ),
      problem(
        "Eine Änderung erscheint im falschen Center",
        "Kontrolliere vor dem Speichern immer den Studio- beziehungsweise Teamfilter der Zielseite.",
      ),
    ],
    relatedGuides: ["arbeitsplan-erstellen", "dokumente-personal"],
  }),
  guide({
    slug: "dokumente-personal",
    title: "Dokumente und Personalprozesse bearbeiten",
    summary: "Finde Dokumentenablage, Timestops, Unfallmeldungen, Personalanliegen und weitere interne Vorgänge.",
    category: "documents",
    aliases: ["dokumentenablage", "visieren", "timestop", "unfallmeldung", "personalanliegen", "kk bestätigung"],
    accessGroups: anyRight(...DOCUMENT_RIGHTS),
    appTarget: null,
    where: ["Dashboard", "Dokumente"],
    duration: "6 Min.",
    visuals: [
      previewScreen("Dokument finden", {
        device: "desktop",
        type: "list-detail",
        accessGroups: anyRight("d_fetchAllDocs", "d_fetchDocs"),
        eyebrow: "Dokumente zentral im Griff",
        icon: "mdi-file-document-multiple-outline",
        title: "Dokumentenablage",
        actions: [
          previewAction("Ordner", "secondary", "mdi-folder-outline"),
          previewAction("Mails abrufen", "secondary", "mdi-email-sync-outline"),
          previewAction("Rechnungssteller", "secondary", "mdi-domain"),
          previewAction("Hochladen", "primary", "mdi-upload", true, "Dokument hinzufügen"),
        ],
        sectionLabel: "Globale Suche",
        sectionMeta: "Aktueller Bereich",
        search: { placeholder: "Dokumente durchsuchen", value: "Rechnung", spotlight: true, hint: "Dokument suchen" },
        rows: [
          previewRow("Rechnung August · Beispiel.pdf", "Eingang · 12.08.2026", {
            icon: "mdi-file-pdf-box",
            status: "Offen",
            tone: "warning",
            spotlight: true,
            hint: "Vorschau öffnen",
          }),
          previewRow("Teamprotokoll.pdf", "Personal · 10.08.2026", { icon: "mdi-file-document-outline", status: "Visiert", tone: "success" }),
        ],
        stepLabel: "Dokument auswählen",
      }),
      previewScreen("Dokument visieren", {
        device: "tablet",
        type: "list-detail",
        accessGroups: anyRight("d_fetchAllDocs", "d_fetchDocs"),
        eyebrow: "Dokumentvorschau",
        icon: "mdi-file-eye-outline",
        sectionLabel: "Vorschau",
        sectionMeta: "Dokumentinhalt",
        notice: {
          tone: "info",
          icon: "mdi-file-document-check-outline",
          title: "Rechnung August · Beispiel.pdf",
          text: "Bitte Vorschau, Zuordnung und Details vor der Visierung prüfen.",
        },
        actions: [previewAction("Visieren", "primary", "mdi-check-decagram-outline", true, "Bestätigung öffnen")],
        stepLabel: "Status setzen",
      }),
      previewScreen("Dokument visieren?", {
        type: "status-confirm",
        accessGroups: anyRight("d_fetchAllDocs", "d_fetchDocs"),
        eyebrow: "Dokumentvorschau",
        icon: "mdi-file-document-check-outline",
        notice: { tone: "warning", icon: "mdi-alert-outline", text: "Prüfe das Dokument nochmals, bevor du deine Visierung bestätigst." },
        actions: [
          previewAction("Abbrechen", "secondary"),
          previewAction("Jetzt visieren", "primary", "mdi-check-decagram-outline", true, "Visierung bestätigen"),
        ],
        stepLabel: "Visierung bestätigen",
      }),
      previewScreen("Personalanliegen erfassen", {
        device: "tablet",
        type: "form",
        accessGroups: anyRight("d_fetchPersonalRequests", "d_managePersonalRequests"),
        eyebrow: "Personalanliegen",
        icon: "mdi-account-question-outline",
        fields: [
          previewField("Studio *", "Zürich", { icon: "mdi-map-marker", select: true }),
          previewField("Priorität", "Normal", { icon: "mdi-flag-outline", select: true }),
          previewField("Betroffener Mitarbeiter *", "Lea Beispiel", { icon: "mdi-account-outline", select: true }),
          previewField("Kategorie *", "Arbeitsmittel", { icon: "mdi-shape-outline", select: true }),
          previewField("Titel *", "Ersatzgerät benötigt", { icon: "mdi-format-title", wide: true }),
        ],
        actions: [previewAction("Erfassen & an HR senden", "primary", "mdi-send", true, "Anliegen einreichen")],
        stepLabel: "Prozess starten",
      }),
    ],
    prerequisites: ["Du kennst Dokumenttyp oder betroffenen Personalprozess."],
    steps: [
      step(
        "Dokumentenablage öffnen",
        "Nutze Ordner, Statusbereich oder globale Suche und öffne Vorschau sowie Metadaten des richtigen Dokuments.",
        "",
        anyRight("d_fetchAllDocs", "d_fetchDocs"),
      ),
      step(
        "Bewerbung öffnen",
        "Wechsle zu Bewerbungen und wähle den vorhandenen Bewerbungsprozess oder die angebotene Erfassungsaktion.",
        "",
        anyRight("d_manageJobApplications", "d_fetchJobApplications"),
      ),
      step("KK-Bestätigung öffnen", "Öffne KK-Bestätigungen und kontrolliere Person sowie Dokumentdaten.", "", anyRight("d_v_healthcare")),
      step("Kreditkartenbeleg öffnen", "Öffne Kreditkartenbelege und kontrolliere Zuordnung und Belegstatus.", "", anyRight("cc_view", "cc_viewAll")),
      step(
        "Timestop bearbeiten",
        "Öffne Timestops oder den Timestop-Rechner und prüfe Zeitraum sowie Status.",
        "",
        anyRight("d_c_timestops", "d_v_timestops"),
      ),
      step(
        "Unfallmeldung bearbeiten",
        "Öffne Unfallmeldungen, filtere den Vorgang und führe nur die angebotenen Aktionen aus.",
        "",
        anyRight("d_fetchAccidentProtocols", "d_manageAccidentProtocols"),
      ),
      step(
        "Personalanliegen bearbeiten",
        "Öffne Personalanliegen, filtere nach Studio, Status oder Priorität und kontrolliere die nötigen Visierungen.",
        "",
        anyRight("d_fetchPersonalRequests", "d_managePersonalRequests"),
      ),
      step("Status kontrollieren", "Prüfe, ob der Vorgang anschließend im erwarteten Ordner beziehungsweise Statusbereich erscheint."),
    ],
    resultCheck: "Dokument oder Personalvorgang befindet sich mit korrektem Status und richtiger Zuweisung im vorgesehenen Bereich.",
    troubleshooting: [
      problem(
        "Ein Dokument lässt sich nur ansehen",
        "Einzelne Aktionen besitzen zusätzliche Rechte. Sichtbarkeit eines Dokuments bedeutet nicht automatisch Bearbeitungs- oder Visierrecht.",
      ),
      problem(
        "Der Vorgang ist nicht auffindbar",
        "Prüfe globale Suche, Statusbereich, eigenen Ordner und Studio-Kontext, bevor du einen neuen Vorgang anlegst.",
      ),
    ],
    relatedGuides: ["studio-verwalten", "krankmeldung"],
  }),
  guide({
    slug: "group-fitness",
    title: "GF-Kurse und Instruktor-Auswertungen verwalten",
    summary: "Pflege reguläre Group-Fitness-Kurse und exportiere die benötigten Instruktor-Kursdaten.",
    category: "group-fitness",
    aliases: ["gf", "groupfitness", "kurse", "instruktoren", "räume", "stundenlisten"],
    accessGroups: anyRight(...GROUP_FITNESS_RIGHTS),
    appTarget: null,
    where: ["Dashboard", "Group Fitness"],
    duration: "6 Min.",
    visuals: [
      previewScreen("GF-Kurs verwalten", {
        device: "desktop",
        type: "list-detail",
        accessGroups: anyRight("gf_c_create", "gf_c_edit", "gf_c_delete", "gf_c_plan"),
        eyebrow: "Group Fitness",
        icon: "mdi-account-group-outline",
        title: "Kurse",
        tabs: [{ label: "Kurse", active: true }, { label: "Kategorisieren" }],
        search: { placeholder: "Suche", value: "Yoga", spotlight: true, hint: "Kurs finden" },
        rows: [
          previewRow("Yoga Flow · Beispiel", "Kategorie Body & Mind · Aktiv", {
            icon: "mdi-yoga",
            status: "Aktiv",
            tone: "success",
            spotlight: true,
            hint: "Kurs bearbeiten",
          }),
        ],
        actions: [previewAction("Kurs hinzufügen", "primary", "mdi-plus", true, "Neuen Kurs")],
        stepLabel: "Regulären Kurs öffnen",
      }),
      previewScreen("Instruktor Export", {
        device: "tablet",
        type: "form",
        accessGroups: anyRight("gf_c_create", "gf_c_edit", "gf_c_delete"),
        eyebrow: "Group Fitness · Instr. Liste",
        icon: "mdi-file-download-outline",
        title: "Stundenlisten & Kursdaten herunterladen",
        fields: [
          previewField("Instruktor wählen", "Instruktor · Beispiel", {
            icon: "mdi-account-outline",
            select: true,
            spotlight: true,
            hint: "Instruktor wählen",
          }),
          previewField("Zeitraum wählen", "August 2026", { icon: "mdi-calendar-range", select: true }),
        ],
        notice: {
          tone: "warning",
          icon: "mdi-calendar-alert-outline",
          title: "Zeitraum nötig",
          text: "Wähle einen Zeitraum, bevor du den Export startest.",
        },
        actions: [previewAction("Export Starten", "primary", "mdi-download", true, "Daten exportieren")],
        stepLabel: "Instruktor-Daten exportieren",
      }),
    ],
    prerequisites: ["Du weißt, welchen regulären GF-Kurs oder welchen Instruktor-Zeitraum du bearbeiten möchtest."],
    steps: [
      step(
        "Kurse verwalten",
        "Öffne Kurse, wähle Kurs beziehungsweise Kategorie und nutze nur die angebotenen Erstellen- oder Bearbeiten-Aktionen.",
        "",
        anyRight("gf_c_create", "gf_c_edit", "gf_c_delete", "gf_c_plan"),
      ),
      step(
        "Instruktor-Auswertung öffnen",
        "Wähle in der Instruktoren-Liste Instruktor und Zeitraum und starte den Export. Diese Fläche ist im aktuellen Code ein Auswertungswerkzeug, keine allgemeine Stammdatenpflege.",
        "",
        anyRight("gf_c_create", "gf_c_edit", "gf_c_delete"),
      ),
      step("Räume pflegen", "Öffne Räume und kontrolliere den Datensatz vor jeder Änderung.", "", anyRight("r_edit")),
      step("Labels pflegen", "Öffne Labels und kontrolliere den Datensatz vor jeder Änderung.", "", anyRight("l_edit")),
      step(
        "Videos pflegen",
        "Öffne Videos und nutze die angebotenen Erstellen-, Bearbeiten- oder Löschen-Aktionen.",
        "",
        anyRight("a_add", "a_edit", "a_delete"),
      ),
      step(
        "GF-Events planen",
        "Öffne Events und bearbeite den gewünschten Kurs- oder Planungseintrag.",
        "",
        anyRight("gf_c_create", "gf_c_edit", "gf_c_delete", "gf_c_plan"),
      ),
      step(
        "Bewertungen prüfen",
        "Öffne Bewertungen und kontrolliere die freigegebenen Auswertungen.",
        "",
        anyRight("gf_r_viewAll", "gf_r_threestars"),
      ),
      step("Downloads öffnen", "Wechsle zu Downloads und wähle den angebotenen Export.", "", anyRight("gf_c_plan", "gf_cp_instructor")),
      step(
        "GF-Einstellungen öffnen",
        "Öffne Einstellungen und passe nur die für dein Profil sichtbaren Kursparameter an.",
        "",
        anyRight("gf_c_create", "gf_c_edit", "gf_c_delete", "gf_c_signupRange"),
      ),
      step("Ergebnis prüfen", "Kontrolliere, dass Änderung oder Export in genau dem bearbeiteten GF-Bereich sichtbar ist."),
    ],
    resultCheck: "Der richtige GF-Bereich ist geöffnet und die Änderung beziehungsweise der Export passt zum erwarteten Kurs oder Instruktor.",
    troubleshooting: [
      problem(
        "Der Unterpunkt fehlt trotz anderer GF-Rechte",
        "GF-Unterbereiche verwenden unterschiedliche Rechte. Beispielsweise sind Räume, Labels, Events und Bewertungen getrennt freigegeben.",
      ),
      problem("Der Export startet nicht", "Wähle zuerst einen Instruktor und einen gültigen Zeitraum und starte den Export anschließend erneut."),
    ],
    relatedGuides: ["fehlende-funktion", "statistik-wegweiser"],
  }),
  guide({
    slug: "statistik-wegweiser",
    title: "Welche Statistik beantwortet welche Centerfrage?",
    summary: "Starte bei der betrieblichen Frage und öffne anschließend nur die dafür passende Statistik.",
    category: "statistics",
    aliases: ["kennzahlen", "auslastung", "trainer statistik", "kunden statistik", "verträge", "leads", "ziele"],
    accessGroups: [{ anyRights: STATISTICS_RIGHTS, anyRoles: TRAINER_ROLES }],
    appTarget: null,
    where: ["Dashboard", "Statistiken"],
    duration: "5 Min.",
    visuals: [
      previewScreen("Frage der Statistik zuordnen", {
        device: "desktop",
        type: "navigation",
        fidelity: "didactic",
        eyebrow: "Statistik-Wegweiser",
        icon: "mdi-chart-box-outline",
        title: "Welche Frage möchtest du beantworten?",
        rows: [
          previewRow("Wie entwickelt sich unser Center?", "Mitglieder · Kündigungen · Ziele · Überstunden", {
            icon: "mdi-office-building-outline",
            status: "Monatsübersicht Center",
            tone: "info",
            spotlight: true,
            hint: "Statistik öffnen",
          }),
          previewRow("Wie laufen Termine und Aufgaben?", "Termine · Ämtli · Kundenzeit · No-shows", {
            icon: "mdi-calendar-clock-outline",
            status: "Center im Blick",
            tone: "success",
          }),
          previewRow("Wie performen unsere Leads?", "Quellen · Status · Conversion", {
            icon: "mdi-account-convert-outline",
            status: "Leads",
            tone: "warning",
          }),
          previewRow("Wie sind unsere Kurse ausgelastet?", "Kursauslastung · aktive GF-Mitglieder", {
            icon: "mdi-account-group-outline",
            status: "Group Fitness",
            tone: "neutral",
          }),
        ],
        stepLabel: "Auswertung wählen",
      }),
      previewScreen("Filter und Kennzahlen lesen", {
        device: "desktop",
        type: "analytics",
        accessGroups: anyRight("stat_viewstudio", "stat_admin"),
        eyebrow: "Statistiken · Checkins",
        icon: "mdi-login-variant",
        title: "Check-in-Statistik",
        fields: [
          previewField("Zeitraum", "August 2026", { icon: "mdi-calendar-range", select: true }),
          previewField("Studios", "Zürich · Beispiel", {
            icon: "mdi-map-marker-multiple-outline",
            select: true,
            spotlight: true,
            hint: "Studio prüfen",
          }),
        ],
        metrics: [
          previewMetric("Gesamt Check-ins", "3'842", "Beispiel", "success"),
          previewMetric("Einzigartige Personen", "1'126", "Beispiel"),
          previewMetric("Durchschnitt pro Tag", "124", "Beispiel"),
          previewMetric("Beliebteste Uhrzeit", "18:00", "Beispiel", "warning"),
        ],
        bars: [
          { label: "Check-in Verlauf", value: 82, display: "Beispiel", tone: "green" },
          { label: "Auslastung nach Uhrzeit", value: 68, display: "Beispiel", tone: "orange" },
          { label: "Besuche nach Wochentag", value: 74, display: "Beispiel", tone: "green" },
          { label: "Studio Verteilung (Top List)", value: 57, display: "Beispiel", tone: "green" },
        ],
        caption: "Beispielwerte – Zahlen nur innerhalb desselben Studios, Zeitraums und derselben Definition vergleichen.",
        stepLabel: "Kontext kontrollieren",
      }),
    ],
    prerequisites: ["Zeitraum und Studio der Fragestellung sind bekannt."],
    steps: [
      step("Frage formulieren", "Kläre zuerst, ob es um Kunden, Check-ins, Termine, Teamkapazität, Verträge, Leads, Group Fitness oder Ziele geht."),
      step(
        "Passende Statistik wählen",
        "Öffne Kunden/Check-ins für Besuche, Verträge/Pakete für Verkauf, Leads für Anfragen, GF für Kurse oder Trainer/Ziele für Teamfragen.",
      ),
      step("Filter kontrollieren", "Prüfe Zeitraum, Studio und gegebenenfalls Team, bevor du Zahlen interpretierst."),
      step(
        "Definition beachten",
        "Lies Bezeichnung und Kontext der Kennzahl. Geplante Kalenderauslastung ist beispielsweise nicht automatisch reale Anwesenheit oder Produktivität.",
      ),
      step(
        "In die operative Fläche wechseln",
        "Nutze Kalender, Kundenakte oder Teamansicht, wenn aus der Kennzahl eine konkrete Prüfung oder Aktion folgt.",
      ),
    ],
    resultCheck: "Die gewählte Statistik passt zur Ausgangsfrage und zeigt den richtigen Zeitraum sowie das richtige Studio.",
    troubleshooting: [
      problem(
        "Eine bekannte Statistik fehlt in der Navigation",
        "Einige routbare Statistikflächen sind nicht überall in der Sidebar verlinkt. Nutze nur freigegebene Direktziele und lasse die Navigation bei Bedarf prüfen.",
      ),
      problem(
        "Zahlen aus zwei Ansichten unterscheiden sich",
        "Kontrolliere Definition, Zeitraum, Studio und Statusfilter. Geplante und tatsächlich abgeschlossene Vorgänge sind unterschiedliche Grundlagen.",
      ),
    ],
    relatedGuides: ["heutiger-einsatz", "arbeitsplan-erstellen", "zeiten-pruefen"],
  }),
]
const HELP_FUNCTIONS = [
  functionEntry({
    slug: "dashboard",
    title: "Dashboard",
    summary: "Zentrale Übersicht und Einstieg in alle freigegebenen Mitarbeiterbereiche.",
    category: "orientation",
    aliases: ["menü", "sidebar", "start"],
    accessGroups: staffAccess,
    appTarget: { name: "dashboardHome" },
    where: ["App", "Dashboard"],
    guideSlug: "dashboard-navigation",
  }),
  functionEntry({
    slug: "eigene-zeiterfassung",
    title: "Eigene Zeiterfassung",
    summary: "Arbeitszeit, Pausen, Korrekturen, Wünsche und persönliche Monatseinreichung.",
    category: "time",
    aliases: ["stempeln", "tracker", "arbeitszeit"],
    accessGroups: anyRight("tt_employeeManage"),
    appTarget: { name: "timeTracker" },
    where: ["Dashboard", "Zeiterfassung", "Zeiterfassung"],
    guideSlug: "arbeitszeit-erfassen",
  }),
  functionEntry({
    slug: "zeitmanagement",
    title: "Zeiterfassung – Management",
    summary: "Anträge, Abwesenheiten, Korrekturen und eingereichte Monate prüfen.",
    category: "time",
    aliases: ["übersicht", "genehmigen", "management"],
    accessGroups: anyRight("tt_management", "g_studioadmin"),
    appTarget: { name: "timeTrackerManagement" },
    where: ["Dashboard", "Zeiterfassung", "Übersicht"],
    guideSlug: "zeiten-pruefen",
  }),
  functionEntry({
    slug: "zeitabrechnung",
    title: "Zeiterfassung – Abrechnung",
    summary: "Eingereichte Monate für den offiziellen Abschluss kontrollieren.",
    category: "time",
    aliases: ["office", "monatsabschluss", "abrechnung"],
    accessGroups: anyRight("tt_office", "g_studioadmin"),
    appTarget: { name: "timeTrackerOffice" },
    where: ["Dashboard", "Zeiterfassung", "Abrechnung"],
    guideSlug: "monatsabschluss-pruefen",
  }),
  functionEntry({
    slug: "arbeitsplanung",
    title: "Arbeitsplanung",
    summary: "Monatspläne, Mitarbeiterzuweisungen und Schichtvorlagen bearbeiten.",
    category: "planning",
    aliases: ["schichtplan", "einsatzplan", "monat planen"],
    accessGroups: anyRight("tt_office", "sm_create"),
    appTarget: { name: "shiftManagementPlan" },
    where: ["Dashboard", "Arbeitsplanung", "Planung erstellen"],
    guideSlug: "arbeitsplan-erstellen",
  }),
  functionEntry({
    slug: "arbeitsplanung-einstellungen",
    title: "Arbeitsplanung – Einstellungen",
    summary: "Planungsvorgaben und monatliche Grundlagen konfigurieren.",
    category: "planning",
    aliases: ["planung konfigurieren", "schichteinstellungen"],
    accessGroups: anyRight("tt_office", "sm_create"),
    appTarget: { name: "shiftManagementSettings" },
    where: ["Dashboard", "Arbeitsplanung", "Einstellungen"],
    guideSlug: "schichten-verwalten",
  }),
  functionEntry({
    slug: "arbeitskalender",
    title: "Arbeitskalender",
    summary: "Schichten, Termine, Aufgaben und Ämtli eines Arbeitstags sehen.",
    category: "calendar",
    aliases: ["kalender", "mein tag", "workcalendar"],
    accessGroups: [{ anyRights: ["tt_employeeManage", "sm_appointment_management", "sm_create", "g_employeemanager", "live_checkins"] }],
    appTarget: { name: "workCalendar" },
    where: ["App oder Dashboard", "Kalender"],
    guideSlug: "heutiger-einsatz",
  }),
  functionEntry({
    slug: "trainerpoint-live",
    title: "Trainerpoint Live",
    summary: "Aktive und heute abgeschlossene Center-Besuche sowie Kundensignale prüfen.",
    category: "calendar",
    aliases: ["checkin pool", "live fläche", "im center"],
    accessGroups: anyRight("live_checkins"),
    appTarget: { name: "workCalendar" },
    where: ["Arbeitskalender", "Trainerpoint Live"],
    guideSlug: "trainerpoint-live",
  }),
  functionEntry({
    slug: "kundensuche",
    title: "Benutzersuche und Kundenakte",
    summary: "Kunden suchen und Betreuung, Training, Stammdaten oder Verträge öffnen.",
    category: "customers",
    aliases: ["kundensuche", "mitglied", "kundenprofil"],
    accessGroups: anyRight("u_search"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Dashboard", "Benutzersuche"],
    guideSlug: "kunde-suchen",
  }),
  functionEntry({
    slug: "kundeninteraktionen",
    title: "Kundeninteraktionen",
    summary: "Betreuungskontakte mit Grund, Ort, Notiz und Ergebnis dokumentieren.",
    category: "customers",
    aliases: ["kontakt", "notiz", "betreuung"],
    accessGroups: customerAction("tp_create"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte", "Interaktionen"],
    guideSlug: "kundeninteraktion",
  }),
  functionEntry({
    slug: "anamnesen",
    title: "Kundenanamnese",
    summary: "Gesundheitsangaben eines Kunden ansehen oder neu erfassen.",
    category: "customers",
    aliases: ["anamnesebogen", "gesundheit"],
    accessGroups: customerAction("ab_create", "ab_edit"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte", "Anamnese"],
    guideSlug: "anamnese",
  }),
  functionEntry({
    slug: "trainingsplaene",
    title: "Kunden-Trainingspläne",
    summary: "Klassische und V2-Trainingspläne erstellen, bearbeiten und verwalten.",
    category: "training",
    aliases: ["tp", "split", "workout"],
    accessGroups: customerAction("tp_create"),
    appTarget: { name: "userDashboardSearch" },
    where: ["Kundenakte", "Training"],
    guideSlug: "trainingsplan-erstellen",
  }),
  functionEntry({
    slug: "trainingsplan-bewertungen",
    title: "Trainingsplan-Bewertungen",
    summary: "Feedback zu Trainingsplänen im Dashboard prüfen.",
    category: "training",
    aliases: ["feedback", "bewertung"],
    accessGroups: anyRight("tp_feedback"),
    appTarget: { name: "workoutsDashboardFeedback" },
    where: ["Dashboard", "Trainingsplan", "Bewertungen"],
    guideSlug: "trainingsplan-verwalten",
  }),
  functionEntry({
    slug: "vertraege",
    title: "Vertragsübersicht",
    summary: "Verfügbare sowie offene, temporäre oder abgelehnte Verträge verwalten.",
    category: "customers",
    aliases: ["vertrag", "mitgliedschaft", "tac"],
    accessGroups: anyRight("c_create_customer", "c_open_contracts", "c_create"),
    appTarget: { name: "contractList" },
    where: ["Dashboard", "Verträge"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "upgrades",
    title: "Vertrags-Upgrades",
    summary: "Akzeptierte, offene und abgelehnte Upgrade-Vorgänge prüfen.",
    category: "customers",
    aliases: ["upgrade", "paket lösen"],
    accessGroups: anyRight("c_create_customer", "c_open_contracts"),
    appTarget: { name: "contractUpgradesOpen" },
    where: ["Dashboard", "Upgrades"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "leads",
    title: "Weiterempfehlungen und Leads",
    summary: "Anfragen, Empfehlungen und Leadstatus suchen und bearbeiten.",
    category: "customers",
    aliases: ["anfrage", "referral", "werber"],
    accessGroups: anyRight("af_downloadList", "af_view", "af_edit", "af_settingsEdit", "af_leadSettings", "af_cost"),
    appTarget: { name: "leadsDashboardAllLeads" },
    where: ["Dashboard", "Weiterempfehlungen"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "dokumentenablage",
    title: "Dokumentenablage",
    summary: "Interne Dokumente suchen, hochladen, zuweisen und visieren.",
    category: "documents",
    aliases: ["ordner", "visieren", "upload"],
    accessGroups: anyRight("d_fetchAllDocs", "d_fetchDocs"),
    appTarget: null,
    where: ["Dashboard", "Dokumente", "Dokumentenablage"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "timestops",
    title: "Timestops",
    summary: "Timestop-Anfragen und zugehörige Berechnungen bearbeiten.",
    category: "documents",
    aliases: ["vertragsunterbruch", "pause"],
    accessGroups: anyRight("d_c_timestops", "d_v_timestops"),
    appTarget: { name: "timestopView" },
    where: ["Dashboard", "Dokumente", "Timestops"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "unfallmeldungen",
    title: "Unfallmeldungen",
    summary: "Unfallvorgänge erfassen, filtern und nachverfolgen.",
    category: "documents",
    aliases: ["unfall", "meldung"],
    accessGroups: anyRight("d_fetchAccidentProtocols", "d_manageAccidentProtocols"),
    appTarget: { name: "accidentReports" },
    where: ["Dashboard", "Dokumente", "Unfallmeldungen"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "personalanliegen",
    title: "Personalanliegen",
    summary: "Interne Anliegen an HR erfassen, visieren und bearbeiten.",
    category: "documents",
    aliases: ["hr", "personal", "anliegen"],
    accessGroups: anyRight("d_fetchPersonalRequests", "d_managePersonalRequests"),
    appTarget: { name: "personalRequests" },
    where: ["Dashboard", "Dokumente", "Personalanliegen"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "gf-kurse",
    title: "Group-Fitness-Kurse",
    summary: "GF-Kursstammdaten und Kategorien erstellen oder bearbeiten.",
    category: "group-fitness",
    aliases: ["kurs", "groupfitness"],
    accessGroups: anyRight("gf_c_create", "gf_c_edit", "gf_c_delete", "gf_c_plan"),
    appTarget: { name: "groupFitnessDashboardCourses" },
    where: ["Dashboard", "Group Fitness", "Kurse"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "gf-raeume",
    title: "Group-Fitness-Räume",
    summary: "Verfügbare Kursräume pflegen.",
    category: "group-fitness",
    aliases: ["raum", "kursraum"],
    accessGroups: anyRight("r_edit"),
    appTarget: { name: "groupFitnessDashboardRooms" },
    where: ["Dashboard", "Group Fitness", "Räume"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "studio-mitarbeitende",
    title: "Studio-Mitarbeitende",
    summary: "Mitarbeiter, Pensum, Teams und Zuordnungen eines Studios pflegen.",
    category: "studio",
    aliases: ["team", "mitarbeiter hinzufügen", "pensum"],
    accessGroups: anyRight("g_employeemanager"),
    appTarget: { name: "studioEmployee" },
    where: ["Dashboard", "Studio", "Mitarbeiter"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "oeffnungszeiten",
    title: "Öffnungszeiten",
    summary: "Betreuungszeiten und optionale Zeitfenster eines Studios pflegen.",
    category: "studio",
    aliases: ["öffnungszeit", "betreuungszeit", "24h"],
    accessGroups: anyRight("g_employeemanager"),
    appTarget: { name: "openingHours" },
    where: ["Dashboard", "Studio", "Öffnungszeiten"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "reservationen",
    title: "Reservationen",
    summary: "Buchungen und Auslastung nach Datum verwalten.",
    category: "studio",
    aliases: ["buchung", "slot", "leistung"],
    accessGroups: anyRight("reservations_fetch"),
    appTarget: { name: "ReservationsAdmin" },
    where: ["Dashboard", "Studio", "Reservationen"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "statistiken",
    title: "Center- und Mitarbeiterstatistiken",
    summary: "Kennzahlen zu Kunden, Check-ins, GF, Verträgen, Leads, Team und Zielen.",
    category: "statistics",
    aliases: ["kennzahlen", "auswertung", "kpi"],
    accessGroups: [{ anyRights: STATISTICS_RIGHTS, anyRoles: TRAINER_ROLES }],
    appTarget: null,
    where: ["Dashboard", "Statistiken"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "geraete",
    title: "Geräteverwaltung",
    summary: "Geräte erstellen, bearbeiten, löschen oder übertragen.",
    category: "tools",
    aliases: ["fitnessgerät", "transfer"],
    accessGroups: anyRight("ex_create", "ex_edit", "ex_delete", "ex_transfer"),
    appTarget: { name: "devicesDashboardList" },
    where: ["Dashboard", "Geräte"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "tac-zugangsgeraete",
    title: "TAC-Zugangsgeräte",
    summary: "Konfigurierte TAC-Zugangsgeräte verwalten.",
    category: "tools",
    aliases: ["tac", "zugang", "device"],
    accessGroups: anyRight("tac_accessDevices"),
    appTarget: { name: "tacAccessDevices" },
    where: ["Dashboard", "TAC", "Zugangsgeräte"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing",
    title: "Marketing-Werkzeuge",
    summary: "Pop-ups, QR-Codes, Kampagnen, Slider, Website und weitere Inhalte verwalten.",
    category: "marketing",
    aliases: ["popup", "qr code", "kampagne", "website", "slider"],
    accessGroups: anyRight(
      "mk_news",
      "mk_popup",
      "mk_qrcode",
      "mk_campaigns",
      "mk_slider",
      "g_websitecontent",
      "g_websitecontentstudioadmin",
      "mk_emailsignature",
    ),
    appTarget: null,
    where: ["Dashboard", "Marketing"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "zeitmanagement-centerleitung",
    title: "Zeiterfassung – Centerleiter-Übersicht",
    summary: "Die zusätzliche Centerleiter-Sicht in der Zeitprüfung verwenden.",
    category: "time",
    aliases: ["centerleiter", "sonderübersicht", "events admin"],
    accessGroups: allGroups({ anyRights: ["tt_management", "g_studioadmin"] }, { anyRights: ["tt_eventsAdmin"] }),
    appTarget: { name: "timeTrackerManagement" },
    where: ["Dashboard", "Zeiterfassung", "Übersicht", "Centerleiter"],
    guideSlug: "zeiten-pruefen",
  }),
  functionEntry({
    slug: "arbeitsplan-freigabe",
    title: "Arbeitsplan freigeben",
    summary: "Einen geprüften Monatsplan für das Team sichtbar schalten oder zurückziehen.",
    category: "planning",
    aliases: ["plan veröffentlichen", "aufschalten", "entwurf freigeben"],
    accessGroups: allGroups({ anyRights: ["tt_office", "sm_create"] }, { anyRights: ["g_employeemanager"] }),
    appTarget: { name: "shiftManagementPlan" },
    where: ["Dashboard", "Arbeitsplanung", "Monatsdetail"],
    guideSlug: "arbeitsplan-freigeben",
  }),
  functionEntry({
    slug: "reservationstypen",
    title: "Reservationsarten und Formulare",
    summary: "Buchbare Leistungen, Regeln und Formulare für Reservationen pflegen.",
    category: "studio",
    aliases: ["reservationstyp", "leistung", "formular"],
    accessGroups: allGroups({ anyRights: ["reservations_fetch"] }, { anyRights: ["reservations_createType"] }),
    appTarget: { name: "ReservationsAdmin" },
    where: ["Dashboard", "Studio", "Reservationen", "Formulare verwalten"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "zahlung-erfassen",
    title: "Zahlung erfassen",
    summary: "Eine neue Zahlung im vorgesehenen Zahlungsdialog aufnehmen.",
    category: "tools",
    aliases: ["zahlung", "bezahlen", "transaktion"],
    accessGroups: anyRight("payment_create"),
    appTarget: { name: "payment" },
    where: ["Dashboard", "Zahlungen", "Zahlung erfassen"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "zahlungsuebersicht",
    title: "Zahlungsübersicht",
    summary: "Bereits erfasste Zahlungen suchen und kontrollieren.",
    category: "tools",
    aliases: ["zahlungen", "transaktionen", "belege"],
    accessGroups: anyRight("payment_overview"),
    appTarget: { name: "paymentOverview" },
    where: ["Dashboard", "Zahlungen", "Übersicht"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "shop-produkte",
    title: "Shop-Produkte",
    summary: "Produkte des internen Shops verwalten.",
    category: "tools",
    aliases: ["shop", "produkt", "artikel"],
    accessGroups: anyRight("shop_manage"),
    appTarget: { name: "shopProducts" },
    where: ["Dashboard", "Shop", "Produkte"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "shop-einkaeufe",
    title: "Shop-Einkäufe",
    summary: "Einkäufe und Bestellungen im internen Shop prüfen.",
    category: "tools",
    aliases: ["shop", "einkauf", "bestellung"],
    accessGroups: anyRight("shop_manage", "shop_purchase_view"),
    appTarget: { name: "shopPurchases" },
    where: ["Dashboard", "Shop", "Einkäufe"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "ernaehrungsberatung-verkauf",
    title: "Ernährungsberatung verkaufen",
    summary: "Eine Ernährungsberatung im Vertragsbereich für Kunden lösen.",
    category: "customers",
    aliases: ["ernährung", "beratung", "verkauf"],
    accessGroups: anyRight("c_create_customer", "c_create"),
    appTarget: { name: "nutritionSales" },
    where: ["Dashboard", "Verträge", "Ernährungsberatung"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "ernaehrung-rezepte",
    title: "Ernährung – Rezepte durchsuchen",
    summary: "Vorhandene Ernährungsrezepte suchen und öffnen.",
    category: "tools",
    aliases: ["rezept", "nutrition", "essen"],
    accessGroups: allGroups({ anyRights: ["sebdomspecial"] }, { anyRights: ["nutrition_admin"] }),
    appTarget: { name: "nutritionSearch" },
    where: ["Dashboard", "Ernährung", "Rezepte durchsuchen"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "ernaehrung-kunden",
    title: "Ernährung – Kunden",
    summary: "Kundenbezogene Ernährungsbereiche verwalten.",
    category: "customers",
    aliases: ["nutrition kunden", "ernährungsplan"],
    accessGroups: allGroups({ anyRights: ["sebdomspecial"] }, { anyRights: ["nutrition_admin"] }),
    appTarget: { name: "nutritionCustomers" },
    where: ["Dashboard", "Ernährung", "Kunden"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "membercard",
    title: "Membercard-Verwaltung",
    summary: "Membercards und zugehörige Kundenaktionen verwalten.",
    category: "customers",
    aliases: ["11 tage", "mitgliedskarte", "member card"],
    accessGroups: anyRight("m_downloadList", "m_signupUser", "m_viewSubs", "m_createSubs", "m_createMembercard"),
    appTarget: { name: "membercardDashboardAdmin" },
    where: ["Dashboard", "Membercard"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "trainingsplan-verwaltung",
    title: "Trainingsplan-Verwaltung",
    summary: "Trainingspläne über die globale Trainingsplanfläche verwalten.",
    category: "training",
    aliases: ["workouts", "trainingspläne", "plan suchen"],
    accessGroups: anyRight("tp_create", "tp_viewAll"),
    appTarget: { name: "workouts" },
    where: ["Dashboard", "Trainingsplan"],
    guideSlug: "trainingsplan-verwalten",
  }),
  functionEntry({
    slug: "todos",
    title: "ToDos",
    summary: "Persönliche oder zugewiesene Aufgaben in der ToDo-Fläche öffnen.",
    category: "workday",
    aliases: ["todo", "aufgabenliste", "offene aufgaben"],
    accessGroups: anyRight("todo_fetchAndEdit"),
    appTarget: null,
    where: ["Dashboard", "ToDos"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "vertraege-offen",
    title: "Offene Verträge",
    summary: "Noch offene Vertragsvorgänge suchen und bearbeiten.",
    category: "customers",
    aliases: ["vertrag offen", "vertragsmanager"],
    accessGroups: anyRight("c_open_contracts", "c_create_customer"),
    appTarget: null,
    where: ["Dashboard", "Verträge", "Offen"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "vertraege-temporaer",
    title: "Temporäre Verträge",
    summary: "Temporär gespeicherte Vertragsvorgänge kontrollieren.",
    category: "customers",
    aliases: ["temporär", "vertragsentwurf"],
    accessGroups: anyRight("c_create_customer", "c_open_contracts"),
    appTarget: { name: "contractTemporary" },
    where: ["Dashboard", "Verträge", "Temporär"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "vertraege-abgelehnt",
    title: "Abgelehnte Verträge",
    summary: "Abgelehnte Vertragsvorgänge finden und ihren Status prüfen.",
    category: "customers",
    aliases: ["vertrag abgelehnt", "rejected contract"],
    accessGroups: anyRight("c_create_customer", "c_open_contracts"),
    appTarget: { name: "contractRejected" },
    where: ["Dashboard", "Verträge", "Abgelehnt"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "mtt-vertraege",
    title: "MTT-Verträge",
    summary: "MTT-Vertragsdokumente im Vertragsbereich öffnen.",
    category: "documents",
    aliases: ["mtt", "vertrag dokument"],
    accessGroups: anyRight("d_fetchAllDocs"),
    appTarget: { name: "mttContractsView" },
    where: ["Dashboard", "Verträge", "MTT Verträge"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "qr-rechnungen",
    title: "QR-Rechnungen",
    summary: "QR-Rechnungen aus dem Vertragsbereich öffnen und prüfen.",
    category: "documents",
    aliases: ["qr rechnung", "rechnung", "qr code"],
    accessGroups: anyRight("c_create"),
    appTarget: { name: "qrContractsView" },
    where: ["Dashboard", "Verträge", "QR Rechnungen"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "vertrags-einstellungen",
    title: "Vertrags-Einstellungen",
    summary: "Konfigurationen des Vertragsbereichs verwalten.",
    category: "customers",
    aliases: ["vertrag konfigurieren", "contract settings"],
    accessGroups: anyRight("c_create"),
    appTarget: null,
    where: ["Dashboard", "Verträge", "Einstellungen"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "vertragsstatistik-abgelehnt",
    title: "Statistik abgelehnter Verträge",
    summary: "Die separate Auswertung zu abgelehnten Verträgen öffnen.",
    category: "statistics",
    aliases: ["abgelehnte verträge statistik", "contract stats"],
    accessGroups: anyRight("c_create"),
    appTarget: { name: "contractStats" },
    where: ["Dashboard", "Verträge", "Statistik (abgelehnt)"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "upgrades-akzeptiert",
    title: "Akzeptierte Vertrags-Upgrades",
    summary: "Bereits akzeptierte Upgrade-Vorgänge prüfen.",
    category: "customers",
    aliases: ["upgrade akzeptiert", "paket upgrade"],
    accessGroups: anyRight("c_create_customer", "c_open_contracts"),
    appTarget: { name: "contractUpgradesAccepted" },
    where: ["Dashboard", "Upgrades", "Akzeptiert"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "upgrades-abgelehnt",
    title: "Abgelehnte Vertrags-Upgrades",
    summary: "Abgelehnte oder geschlossene Upgrade-Vorgänge prüfen.",
    category: "customers",
    aliases: ["upgrade abgelehnt", "upgrade geschlossen"],
    accessGroups: anyRight("c_create_customer", "c_open_contracts"),
    appTarget: { name: "contractUpgradesClosed" },
    where: ["Dashboard", "Upgrades", "Abgelehnt"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "bewerbungen",
    title: "Bewerbungen",
    summary: "Bewerbungsunterlagen und Bewerbungsprozesse verwalten.",
    category: "documents",
    aliases: ["job", "bewerber", "stellenbewerbung"],
    accessGroups: anyRight("d_manageJobApplications", "d_fetchJobApplications"),
    appTarget: { name: "jobApplications" },
    where: ["Dashboard", "Dokumente", "Bewerbungen"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "krankenkassen-bestaetigungen",
    title: "Krankenkassen-Bestätigungen",
    summary: "Bestätigungen für Krankenkassen öffnen und bearbeiten.",
    category: "documents",
    aliases: ["kk bestätigung", "krankenkasse", "healthcare"],
    accessGroups: anyRight("d_v_healthcare"),
    appTarget: { name: "healthcareView" },
    where: ["Dashboard", "Dokumente", "KK-Bestätigungen"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "kreditkartenbelege",
    title: "Kreditkartenbelege",
    summary: "Kreditkartenbelege einsehen und je nach Recht verwalten.",
    category: "documents",
    aliases: ["kreditkarte", "beleg", "credit card"],
    accessGroups: anyRight("cc_view", "cc_viewAll"),
    appTarget: { name: "creditCardBills" },
    where: ["Dashboard", "Dokumente", "Kreditkartenbelege"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "timestop-rechner",
    title: "Timestop-Rechner",
    summary: "Timestop-Zeiträume mit dem vorgesehenen Rechner vorbereiten.",
    category: "documents",
    aliases: ["timestop berechnen", "zeitstopp rechner"],
    accessGroups: anyRight("d_c_timestops", "d_v_timestops"),
    appTarget: { name: "timestopCalculator" },
    where: ["Dashboard", "Dokumente", "Timestop Rechner"],
    guideSlug: "dokumente-personal",
  }),
  functionEntry({
    slug: "lead-anfragen",
    title: "Lead-Anfragen",
    summary: "Eingegangene Anfragen in der Leadverwaltung prüfen.",
    category: "customers",
    aliases: ["anfrage liste", "lead anfrage"],
    accessGroups: anyRight("af_settingsEdit"),
    appTarget: { name: "leadsDashboardLeadsList" },
    where: ["Dashboard", "Weiterempfehlungen", "Anfragen Liste"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "referrals",
    title: "Referral-Übersicht",
    summary: "Empfehlungs- und Referral-Vorgänge verwalten.",
    category: "customers",
    aliases: ["empfehlung", "werber", "referral"],
    accessGroups: anyRight("af_settingsEdit"),
    appTarget: { name: "leadsDashboardReferralList" },
    where: ["Dashboard", "Weiterempfehlungen", "Referral"],
    guideSlug: "vertraege-pakete",
  }),
  functionEntry({
    slug: "referral-einstellungen",
    title: "Referral-Einstellungen",
    summary: "Einstellungen des Empfehlungsprogramms pflegen.",
    category: "customers",
    aliases: ["referral settings", "empfehlung einstellen"],
    accessGroups: anyRight("af_settingsEdit"),
    appTarget: { name: "leadsDashboardReferralSettings" },
    where: ["Dashboard", "Weiterempfehlungen", "Referral-Einstellungen"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "lead-einstellungen",
    title: "Lead-Einstellungen",
    summary: "Status und Konfigurationen der Leadverwaltung pflegen.",
    category: "customers",
    aliases: ["lead settings", "lead konfigurieren"],
    accessGroups: anyRight("af_leadSettings"),
    appTarget: { name: "leadsDashboardSettings" },
    where: ["Dashboard", "Weiterempfehlungen", "Leads"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "lead-kosten",
    title: "Lead-Kosten",
    summary: "Kosten der Leadkanäle erfassen und auswerten.",
    category: "customers",
    aliases: ["lead cost", "werbekosten", "akquisekosten"],
    accessGroups: anyRight("af_cost"),
    appTarget: { name: "leadsCostManager" },
    where: ["Dashboard", "Weiterempfehlungen", "Lead Kosten"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "planungs-controlling",
    title: "Planungs-Controlling",
    summary: "Planungskennzahlen und Controlling-Daten auswerten.",
    category: "planning",
    aliases: ["planung statistik", "controlling", "arbeitsplanung auswertung"],
    accessGroups: anyRight("stat_planningControlling"),
    appTarget: { name: "planningDashboard" },
    where: ["Dashboard", "Planung", "Controlling"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "gf-instruktorenliste",
    title: "GF-Instruktorenliste",
    summary: "Instruktor und Zeitraum wählen und die Auswertung exportieren.",
    category: "group-fitness",
    aliases: ["instruktor export", "instr liste", "gf trainer"],
    accessGroups: anyRight("gf_c_create", "gf_c_edit", "gf_c_delete"),
    appTarget: null,
    where: ["Dashboard", "Group Fitness", "Instr. Liste"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "gf-labels",
    title: "Group-Fitness-Labels",
    summary: "Labels für Group-Fitness-Inhalte pflegen.",
    category: "group-fitness",
    aliases: ["kurs labels", "gf kategorien"],
    accessGroups: anyRight("l_edit"),
    appTarget: { name: "groupFitnessDashboardLabels" },
    where: ["Dashboard", "Group Fitness", "Labels"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "gf-videos",
    title: "Group-Fitness-Videos",
    summary: "Videos für Group-Fitness-Inhalte verwalten.",
    category: "group-fitness",
    aliases: ["gf video", "kursvideo"],
    accessGroups: anyRight("a_add", "a_delete", "a_edit"),
    appTarget: { name: "groupFitnessDashboardVideos" },
    where: ["Dashboard", "Group Fitness", "Videos"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "gf-events",
    title: "Group-Fitness-Events",
    summary: "Events und zugehörige Kursplanungen verwalten.",
    category: "group-fitness",
    aliases: ["gf event", "kurs event"],
    accessGroups: anyRight("gf_c_create", "gf_c_edit", "gf_c_delete", "gf_c_plan"),
    appTarget: { name: "groupFitnessDashboardEventHome" },
    where: ["Dashboard", "Group Fitness", "Events"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "gf-bewertungen",
    title: "Group-Fitness-Bewertungen",
    summary: "Kursbewertungen und Drei-Sterne-Auswertungen prüfen.",
    category: "group-fitness",
    aliases: ["rating", "sterne", "kursbewertung"],
    accessGroups: anyRight("gf_r_viewAll", "gf_r_threestars"),
    appTarget: { name: "groupFitnessDashboardRatings" },
    where: ["Dashboard", "Group Fitness", "Bewertungen"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "gf-downloads",
    title: "Group-Fitness-Downloads",
    summary: "Planungs- und Instruktoren-Downloads erzeugen.",
    category: "group-fitness",
    aliases: ["gf export", "kursplan download"],
    accessGroups: anyRight("gf_c_plan", "gf_cp_instructor"),
    appTarget: { name: "groupFitnessDashboardDownloads" },
    where: ["Dashboard", "Group Fitness", "Downloads"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "gf-einstellungen",
    title: "Group-Fitness-Einstellungen",
    summary: "Einstellungen der regulären GF-Kursverwaltung pflegen.",
    category: "group-fitness",
    aliases: ["gf settings", "kurs einstellungen"],
    accessGroups: anyRight("gf_c_create", "gf_c_edit", "gf_c_delete"),
    appTarget: null,
    where: ["Dashboard", "Group Fitness", "Einstellungen"],
    guideSlug: "group-fitness",
  }),
  functionEntry({
    slug: "studio-admin",
    title: "Studio-Administration",
    summary: "Grunddaten und administrative Studio-Einstellungen verwalten.",
    category: "studio",
    aliases: ["studio admin", "center admin"],
    accessGroups: anyRight("g_studioadmin"),
    appTarget: { name: "studioEditor" },
    where: ["Dashboard", "Studio", "Admin"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "anamnese-einstellungen",
    title: "Anamnese-Einstellungen",
    summary: "Studioseitige Anamnese-Konfigurationen verwalten.",
    category: "studio",
    aliases: ["anamnese konfigurieren", "fragebogen settings"],
    accessGroups: anyRight("ab_view", "ab_edit", "ab_delete"),
    appTarget: { name: "anamneseDashboardSettings" },
    where: ["Dashboard", "Studio", "Anamnese Einstellungen"],
    guideSlug: "anamnese",
  }),
  functionEntry({
    slug: "kinderhort",
    title: "Kinderhort",
    summary: "Den Kinderhort des ausgewählten Studios verwalten.",
    category: "studio",
    aliases: ["daycare", "kinderbetreuung"],
    accessGroups: anyRight("g_daycare"),
    appTarget: { name: "studioDaycare" },
    where: ["Dashboard", "Studio", "Kinderhort"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "studio-webseite",
    title: "Studio-Webseite",
    summary: "Webseiteninhalte des Studios bearbeiten.",
    category: "marketing",
    aliases: ["website", "webseite inhalt", "center web"],
    accessGroups: anyRight("g_websitecontent", "g_websitecontentstudioadmin"),
    appTarget: { name: "websiteContent" },
    where: ["Dashboard", "Studio", "Webseite"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "marketing-popups",
    title: "Marketing-Pop-ups",
    summary: "Pop-ups für die App oder Website verwalten.",
    category: "marketing",
    aliases: ["popup", "hinweisfenster"],
    accessGroups: anyRight("mk_popup"),
    appTarget: { name: "communicationPopupsNew" },
    where: ["Dashboard", "Marketing", "PopUps"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-party-events",
    title: "Party und Events",
    summary: "Party- und Eventinhalte im Marketingbereich vorbereiten.",
    category: "marketing",
    aliases: ["party", "event marketing", "news"],
    accessGroups: anyRight("mk_news"),
    appTarget: null,
    where: ["Dashboard", "Marketing", "Party"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "hyrox-score",
    title: "Hyrox Score",
    summary: "Hyrox-PFT-Ergebnisse in der vorgesehenen Score-Fläche bearbeiten.",
    category: "marketing",
    aliases: ["hyrox", "pft", "score"],
    accessGroups: anyRight("mk_hyroxPFT"),
    appTarget: null,
    where: ["Dashboard", "Marketing", "Hyrox Score"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "hyrox-simulation",
    title: "Hyrox Simulation",
    summary: "Hyrox-Simulationen im freigegebenen Marketingbereich verwalten.",
    category: "marketing",
    aliases: ["hyrox", "simulation", "pft"],
    accessGroups: anyRight("mk_hyroxPFT"),
    appTarget: null,
    where: ["Dashboard", "Marketing", "Hyrox Simulation"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-qr-codes",
    title: "Marketing-QR-Codes",
    summary: "QR-Codes für Marketingzwecke erstellen.",
    category: "marketing",
    aliases: ["qr", "qr code generator"],
    accessGroups: anyRight("mk_qrcode"),
    appTarget: { name: "communicationQRCode" },
    where: ["Dashboard", "Marketing", "QR-Code"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-kampagnen",
    title: "Marketing-Kampagnen",
    summary: "Kampagnen im Kommunikationsbereich verwalten.",
    category: "marketing",
    aliases: ["campaign", "kampagne"],
    accessGroups: anyRight("mk_campaigns"),
    appTarget: { name: "communicationDashboardCampaign" },
    where: ["Dashboard", "Marketing", "Kampagnen"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-push",
    title: "Push-Nachrichten",
    summary: "Push-Nachrichten im freigegebenen Kommunikationsbereich vorbereiten.",
    category: "marketing",
    aliases: ["push", "notification", "nachricht"],
    accessGroups: anyRight("mk_push", "mk_push_deactivated"),
    appTarget: null,
    where: ["Dashboard", "Marketing", "Push"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-screen-rechnungen",
    title: "Marketing-Screen-Rechnungen",
    summary: "Rechnungen für Marketing-Screens verwalten.",
    category: "marketing",
    aliases: ["screen bill", "bildschirm rechnung"],
    accessGroups: anyRight("mk_screenBill"),
    appTarget: { name: "screenBill" },
    where: ["Dashboard", "Marketing", "Screen Rechnungen"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-lager",
    title: "Marketing-Lager",
    summary: "Bestände des Marketinglagers einsehen.",
    category: "marketing",
    aliases: ["lager", "marketing stock"],
    accessGroups: anyRight("d_fetchMarketingStock"),
    appTarget: { name: "marketingStock" },
    where: ["Dashboard", "Marketing", "Lager"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-shop",
    title: "Marketing-Shop",
    summary: "Artikel und Vorgänge im Marketing-Shop verwalten.",
    category: "marketing",
    aliases: ["werbemittel shop", "marketing artikel"],
    accessGroups: anyRight("d_fetchMarketingShop", "d_manageMarketingShop"),
    appTarget: { name: "marketingShop" },
    where: ["Dashboard", "Marketing", "Shop"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-slider",
    title: "Marketing-Slider",
    summary: "Slider-Inhalte verwalten.",
    category: "marketing",
    aliases: ["slide", "banner"],
    accessGroups: anyRight("mk_slider"),
    appTarget: { name: "communicationDashboardSlideManager" },
    where: ["Dashboard", "Marketing", "Slider"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "marketing-webseite",
    title: "Webseiten-Inhalte",
    summary: "Freigegebene Inhalte der Studio-Webseite bearbeiten.",
    category: "marketing",
    aliases: ["website", "web content", "webseite"],
    accessGroups: anyRight("g_websitecontent", "g_websitecontentstudioadmin"),
    appTarget: { name: "websiteContent" },
    where: ["Dashboard", "Marketing", "Webseite"],
    guideSlug: "studio-verwalten",
  }),
  functionEntry({
    slug: "mail-signatur",
    title: "Mail-Signatur",
    summary: "Eine freigegebene E-Mail-Signatur erzeugen.",
    category: "marketing",
    aliases: ["email signatur", "signature"],
    accessGroups: anyRight("mk_emailsignature"),
    appTarget: { name: "communicationDashboardSignatureGenerator" },
    where: ["Dashboard", "Marketing", "Mail Signatur"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "ausdruck-generator",
    title: "Ausdruck-Generator",
    summary: "Vorbereitete Ausdrucke im Marketingbereich erzeugen.",
    category: "marketing",
    aliases: ["print generator", "ausdruck"],
    accessGroups: anyRight("mk_emailsignature"),
    appTarget: { name: "communicationDashboardPrintGenerator" },
    where: ["Dashboard", "Marketing", "Ausdruck generator"],
    guideSlug: null,
  }),
  functionEntry({
    slug: "monatsuebersicht-center",
    title: "Monatsübersicht Center",
    summary: "Monatliche Center-Kennzahlen zu Mitgliedern, Zielen und Team prüfen.",
    category: "statistics",
    aliases: ["center monat", "monatsübersicht", "kennzahlen"],
    accessGroups: anyRight("stat_viewstudio", "stat_admin"),
    appTarget: { name: "homeDashboardCenterMonthlyStatistics" },
    where: ["Dashboard", "Statistiken", "Monatsübersicht Center"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "coo-dashboard",
    title: "COO-Dashboard",
    summary: "Die für COO freigegebenen übergreifenden Kennzahlen öffnen.",
    category: "statistics",
    aliases: ["coo", "geschäftsleitung", "übergreifend"],
    accessGroups: anyRight("stat_coo"),
    appTarget: { name: "cooDashboard" },
    where: ["Dashboard", "COO", "Dashboard"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "statistik-kennzahlen",
    title: "Kennzahlen",
    summary: "Die allgemeine Kennzahlenübersicht für das ausgewählte Studio öffnen.",
    category: "statistics",
    aliases: ["kpi", "figures", "zahlen"],
    accessGroups: anyRight("stat_viewstudio", "stat_admin"),
    appTarget: { name: "homeDashboardFigures" },
    where: ["Dashboard", "Statistiken", "Kennzahlen"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "black-friday-statistik",
    title: "Black Friday 2025",
    summary: "Die gesonderte Black-Friday-Auswertung öffnen.",
    category: "statistics",
    aliases: ["black friday", "aktion 2025"],
    accessGroups: allGroups(
      { anyRights: ["stat_viewstudio", "stat_admin", "stat_gf", "stat_gf_stud", "stat_leads"], anyRoles: [TRAINER_ROLES[0]] },
      { anyRights: ["stats_blackFriday"] },
    ),
    appTarget: null,
    where: ["Dashboard", "Statistiken", "Black Friday 2025"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "cl-statistiken",
    title: "CL-Statistiken",
    summary: "Die gesonderte Centerleiter-Auswertung öffnen.",
    category: "statistics",
    aliases: ["centerleiter statistik", "cl auswertung"],
    accessGroups: allGroups(
      { anyRights: ["stat_viewstudio", "stat_admin", "stat_gf", "stat_gf_stud", "stat_leads"], anyRoles: [TRAINER_ROLES[0]] },
      { anyRights: ["stats_cl_admin"] },
    ),
    appTarget: null,
    where: ["Dashboard", "Statistiken", "CL Statistiken"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "tac-transaktionen-statistik",
    title: "TAC-Transaktionen",
    summary: "Transaktionen aus TAC im vorgesehenen Statistikbereich prüfen.",
    category: "statistics",
    aliases: ["tac transaktion", "tac zahlung", "transactions"],
    accessGroups: allGroups(
      { anyRights: ["stat_viewstudio", "stat_admin", "stat_gf", "stat_gf_stud", "stat_leads"], anyRoles: [TRAINER_ROLES[0]] },
      { anyRights: ["stats_transactions"] },
    ),
    appTarget: null,
    where: ["Dashboard", "Statistiken", "Transaktionen TAC"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "kunden-statistik",
    title: "Kundenübersicht – Statistik",
    summary: "App-, TAC- und Trainerzuordnungen der Kunden auswerten.",
    category: "statistics",
    aliases: ["kunden übersicht", "tac abdeckung", "kunden je trainer"],
    accessGroups: anyRight("stat_viewstudio", "stat_admin"),
    appTarget: { name: "homeDashboardUserStatistics" },
    where: ["Dashboard", "Statistiken", "Kunden Übersicht"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "checkin-statistik",
    title: "Check-in-Statistik",
    summary: "Besuchstage und Besuchszeiten der Kunden auswerten.",
    category: "statistics",
    aliases: ["checkins", "besuche", "besuchszeit"],
    accessGroups: anyRight("stat_viewstudio", "stat_admin"),
    appTarget: { name: "homeDashboardCheckinStatistics" },
    where: ["Dashboard", "Statistiken", "Checkins"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "gf-statistik",
    title: "Group-Fitness-Statistik",
    summary: "Kursauslastung und aktive GF-Mitglieder auswerten.",
    category: "statistics",
    aliases: ["gf statistik", "kursauslastung"],
    accessGroups: anyRight("stat_gf", "stat_admin", "stat_gf_stud"),
    appTarget: { name: "homeDashboardGroupFitnessStatistics" },
    where: ["Dashboard", "Statistiken", "Group Fitness"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "vertragsstatistik",
    title: "Vertragsstatistik",
    summary: "Neue Verträge, Korrekturen, Kündigungen und Vertragswerte auswerten.",
    category: "statistics",
    aliases: ["verträge statistik", "vertragswert"],
    accessGroups: anyRight("stat_admin", "stat_viewstudio"),
    appTarget: { name: "homeDashboardContractStatistics" },
    where: ["Dashboard", "Statistiken", "Verträge"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "paketstatistik",
    title: "Paketstatistik",
    summary: "Zusatzleistungen und Pakete auswerten.",
    category: "statistics",
    aliases: ["pakete statistik", "zusatzleistung"],
    accessGroups: anyRight("stat_admin"),
    appTarget: { name: "homeDashboardContractPackagesStatistics" },
    where: ["Dashboard", "Statistiken", "Pakete"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "aboentwicklung",
    title: "Aboentwicklung",
    summary: "Die Entwicklung des Mitgliederbestands über mehrere Monate prüfen.",
    category: "statistics",
    aliases: ["abo entwicklung", "12 monate", "mitgliederbestand"],
    accessGroups: anyRight("stat_admin"),
    appTarget: { name: "homeDashboardContractDevelopmentStatistics" },
    where: ["Dashboard", "Statistiken", "Aboentwicklung"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "lead-statistik",
    title: "Lead-Statistik",
    summary: "Quellen, Status und Conversion von Leads auswerten.",
    category: "statistics",
    aliases: ["leads statistik", "conversion", "lead quelle"],
    accessGroups: anyRight("stat_admin", "stat_viewstudio", "stat_leads"),
    appTarget: { name: "homeDashboardLeadStatistics" },
    where: ["Dashboard", "Statistiken", "Leads"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "trainer-statistik",
    title: "Trainer-Statistik",
    summary: "Leistung und Kundenaktivität der Trainer auswerten.",
    category: "statistics",
    aliases: ["trainerleistung", "trainer auswertung"],
    accessGroups: anyRight("stat_admin", "stat_viewstudio"),
    appTarget: { name: "homeDashboardTrainerStatistics" },
    where: ["Dashboard", "Statistiken", "Trainer"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "trainer-monatsziele",
    title: "Trainer-Monatsziele",
    summary: "Persönliche beziehungsweise freigegebene Monatsziele prüfen.",
    category: "statistics",
    aliases: ["trainer ziele", "soll ist", "monatsziel"],
    accessGroups: [
      { anyRights: ["stat_viewstudio", "stat_admin", "stat_gf", "stat_gf_stud", "stat_leads"], anyRoles: [TRAINER_ROLES[0]] },
      { anyRights: ["stat_admin", "stat_viewstudio"], anyRoles: TRAINER_ROLES },
    ],
    appTarget: { name: "homeDashboardTrainerGoals" },
    where: ["Dashboard", "Statistiken", "Trainer Monatsziele"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "monatsziele-statistik",
    title: "Monatsziele-Statistik",
    summary: "Zielerreichung über Mitarbeitende und Monate vergleichen.",
    category: "statistics",
    aliases: ["ziele statistik", "zielerreichung"],
    accessGroups: anyRight("stat_admin"),
    appTarget: { name: "homeDashboardGoalsStatistics" },
    where: ["Dashboard", "Statistiken", "Monatsziele Statistiken"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "mitarbeiterzahlen",
    title: "Mitarbeiterzahlen",
    summary: "Personalbestand, Pensen und Auslastung auswerten.",
    category: "statistics",
    aliases: ["personalbestand", "pensen", "mitarbeiter statistik"],
    accessGroups: [
      { anyRights: ["stat_viewstudio", "stat_admin", "stat_gf", "stat_gf_stud", "stat_leads"], anyRoles: [TRAINER_ROLES[0]] },
      { anyRights: ["stat_employee", "stat_viewstudio"] },
    ],
    appTarget: { name: "homeDashboardEmployeeStatistics" },
    where: ["Dashboard", "Statistiken", "Mitarbeiterzahlen"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "membercard-statistik",
    title: "11-Tage-Kampagne – Statistik",
    summary: "Ergebnisse der 11-Tage-Kampagne auswerten.",
    category: "statistics",
    aliases: ["11 tage statistik", "membercard kampagne"],
    accessGroups: anyRight("stat_admin"),
    appTarget: { name: "homeDashboardMembercardStatistics" },
    where: ["Dashboard", "Statistiken", "11-Tage Kampagne"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "anamnese-statistik",
    title: "Anamnese-Statistik",
    summary: "Erfasste Anamnesen und zugehörige Kennzahlen auswerten.",
    category: "statistics",
    aliases: ["anamnesen statistik", "fragebogen auswertung"],
    accessGroups: anyRight("stat_admin", "stat_viewstudio"),
    appTarget: { name: "homeDashboardAnamneseStatistics" },
    where: ["Dashboard", "Statistiken", "Anamnese"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "kuendigungsentwicklung",
    title: "Kündigungsentwicklung",
    summary: "Kündigungsmuster und Downgrades im Zeitverlauf auswerten.",
    category: "statistics",
    aliases: ["kündigungen", "downgrade", "termination"],
    accessGroups: allGroups(
      { anyRights: ["stat_viewstudio", "stat_admin", "stat_gf", "stat_gf_stud", "stat_leads"], anyRoles: [TRAINER_ROLES[0]] },
      { anyRights: ["stats_transactions"] },
    ),
    appTarget: null,
    where: ["Dashboard", "Statistiken", "Kündigungen"],
    guideSlug: "statistik-wegweiser",
  }),
  functionEntry({
    slug: "center-im-blick",
    title: "Center im Blick",
    summary: "Termine, Ämtli, Kundenzeit, Teamkapazität und No-shows gemeinsam auswerten.",
    category: "statistics",
    aliases: ["workcalendar stats", "kalender statistik", "besetzungs fit"],
    accessGroups: anyRight("stat_viewstudio", "stat_admin"),
    appTarget: { name: "homeDashboardWorkCalendarStatistics" },
    where: ["Direktlink", "Center im Blick"],
    guideSlug: "statistik-wegweiser",
  }),
]

export default {
  name: "HelperTool",
  inheritAttrs: false,
  props: {
    guideSlug: {
      type: String,
      default: "",
    },
  },
  data() {
    const initialCategory = this.$route.query.category
    return {
      userService,
      searchQuery: typeof this.$route.query.q === "string" ? this.$route.query.q : "",
      selectedCategory: CATEGORY_DEFINITIONS.some((category) => category.id === initialCategory) ? initialCategory : "all",
      querySyncTimer: null,
      accessRevision: 0,
    }
  },
  computed: {
    ...mapState("userStore", {
      storeUserRights: (state) => state.userRights,
      storeUser: (state) => state.user,
    }),
    accessStateKey() {
      const rights = Array.isArray(this.storeUserRights) ? this.storeUserRights.join("|") : ""
      const roles = this.storeUser && Array.isArray(this.storeUser.userRoles) ? this.storeUser.userRoles.join("|") : ""
      return `${rights}::${roles}::${this.accessRevision}`
    },
    authorizedGuides() {
      void this.accessStateKey
      return HELP_GUIDES.filter((item) => this.isAccessAllowed(item.accessGroups))
    },
    authorizedFunctions() {
      void this.accessStateKey
      return HELP_FUNCTIONS.filter((item) => this.isAccessAllowed(item.accessGroups))
    },
    authorizedGuideSlugs() {
      return this.authorizedGuides.map((item) => item.slug)
    },
    hasAnyHelpAccess() {
      return this.authorizedGuides.length > 0 || this.authorizedFunctions.length > 0
    },
    requestedGuideRecord() {
      if (!this.guideSlug) return null
      return HELP_GUIDES.find((item) => item.slug === this.guideSlug) || null
    },
    activeGuide() {
      if (!this.requestedGuideRecord) return null
      return this.isAccessAllowed(this.requestedGuideRecord.accessGroups) ? this.requestedGuideRecord : null
    },
    isDeniedGuide() {
      return Boolean(this.guideSlug && this.requestedGuideRecord && !this.activeGuide)
    },
    isMissingGuide() {
      return Boolean(this.guideSlug && !this.requestedGuideRecord)
    },
    availableCategories() {
      return CATEGORY_DEFINITIONS.map((category) => ({
        ...category,
        count:
          this.authorizedGuides.filter((item) => item.category === category.id).length +
          this.authorizedFunctions.filter((item) => item.category === category.id).length,
      })).filter((category) => category.count > 0)
    },
    hasSearchOrCategoryFilter() {
      return Boolean((this.searchQuery || "").trim() || this.selectedCategory !== "all")
    },
    featuredGuides() {
      return this.authorizedGuides.filter((item) => item.featured).slice(0, 6)
    },
    filteredGuides() {
      return this.filterAndSortItems(this.authorizedGuides)
    },
    filteredFunctions() {
      return this.filterAndSortItems(this.authorizedFunctions)
    },
    relatedGuides() {
      if (!this.activeGuide) return []
      return this.activeGuide.relatedGuides
        .map((slug) => this.authorizedGuides.find((item) => item.slug === slug))
        .filter(Boolean)
        .slice(0, 4)
    },
  },
  watch: {
    searchQuery() {
      this.scheduleQuerySync()
    },
    selectedCategory() {
      this.scheduleQuerySync()
    },
    "$route.query.q"(value) {
      const nextValue = typeof value === "string" ? value : ""
      if (nextValue !== this.searchQuery) this.searchQuery = nextValue
    },
    "$route.query.category"(value) {
      const nextValue = CATEGORY_DEFINITIONS.some((category) => category.id === value) ? value : "all"
      if (nextValue !== this.selectedCategory) this.selectedCategory = nextValue
    },
    guideSlug() {
      if (this.querySyncTimer) {
        window.clearTimeout(this.querySyncTimer)
        this.querySyncTimer = null
      }
      this.handlePageChange()
    },
    "$route.query.section"() {
      if (this.guideSlug) this.handlePageChange()
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleGlobalKeydown)
    window.addEventListener("storage", this.handleAccessStorageChange)
    this.handlePageChange()
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleGlobalKeydown)
    window.removeEventListener("storage", this.handleAccessStorageChange)
    if (this.querySyncTimer) window.clearTimeout(this.querySyncTimer)
  },
  methods: {
    isAccessAllowed(accessGroups) {
      void this.accessStateKey
      if (!Array.isArray(accessGroups) || !accessGroups.length) return false

      return accessGroups.every((group) => {
        const rights = Array.isArray(group.anyRights) ? group.anyRights : []
        const roles = Array.isArray(group.anyRoles) ? group.anyRoles : []
        const hasRight = rights.length ? this.userService.hasRight(...rights) : false
        const hasRole = roles.length ? roles.some((role) => this.userService.hasRole(role)) : false
        return hasRight || hasRole
      })
    },
    visibleSteps(item) {
      return item.steps.filter((itemStep) => !itemStep.accessGroups || this.isAccessAllowed(itemStep.accessGroups))
    },
    visibleVisuals(item) {
      if (!item || !Array.isArray(item.visuals)) return []
      return item.visuals.filter((screen) => !screen.accessGroups || this.isAccessAllowed(screen.accessGroups))
    },
    visiblePreviewActions(screen) {
      if (!screen || !Array.isArray(screen.actions)) return []
      return screen.actions.filter((action) => !action.accessGroups || this.isAccessAllowed(action.accessGroups))
    },
    visibleTroubleshooting(item) {
      return item.troubleshooting.filter((entry) => !entry.accessGroups || this.isAccessAllowed(entry.accessGroups))
    },
    visibleGuideTarget(item) {
      if (!item || !item.appTarget) return null
      if (item.appTargetAccessGroups && !this.isAccessAllowed(item.appTargetAccessGroups)) return null
      return item.appTarget
    },
    categoryDefinition(categoryId) {
      return CATEGORY_DEFINITIONS.find((category) => category.id === categoryId) || CATEGORY_DEFINITIONS[0]
    },
    categoryLabel(categoryId) {
      return this.categoryDefinition(categoryId).label
    },
    categoryIcon(categoryId) {
      return this.categoryDefinition(categoryId).icon
    },
    normalizeSearch(value) {
      return String(value || "")
        .toLocaleLowerCase("de-CH")
        .replace(/ä/g, "a")
        .replace(/ae/g, "a")
        .replace(/ö/g, "o")
        .replace(/oe/g, "o")
        .replace(/ü/g, "u")
        .replace(/ue/g, "u")
        .replace(/ß/g, "ss")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
    },
    itemSearchText(item) {
      const steps = Array.isArray(item.steps)
        ? item.steps.map((itemStep) => `${itemStep.title} ${itemStep.text} ${itemStep.tip || ""}`).join(" ")
        : ""
      const troubleshooting = Array.isArray(item.troubleshooting)
        ? item.troubleshooting.map((entry) => `${entry.problem} ${entry.solution}`).join(" ")
        : ""
      return [
        item.title,
        item.summary,
        this.categoryLabel(item.category),
        Array.isArray(item.aliases) ? item.aliases.join(" ") : "",
        Array.isArray(item.where) ? item.where.join(" ") : "",
        steps,
        troubleshooting,
      ].join(" ")
    },
    filterAndSortItems(items) {
      const query = this.normalizeSearch(this.searchQuery)
      return items
        .filter((item) => this.selectedCategory === "all" || item.category === this.selectedCategory)
        .map((item) => ({ item, score: this.searchScore(item, query) }))
        .filter((entry) => !query || entry.score > 0)
        .sort((a, b) => (query ? b.score - a.score : a.item.title.localeCompare(b.item.title, "de-CH")))
        .map((entry) => entry.item)
    },
    searchScore(item, query) {
      if (!query) return 1
      const title = this.normalizeSearch(item.title)
      const aliases = this.normalizeSearch(Array.isArray(item.aliases) ? item.aliases.join(" ") : "")
      const corpus = this.normalizeSearch(this.itemSearchText(item))
      if (title === query) return 100
      if (title.startsWith(query)) return 70
      if (title.includes(query)) return 50
      if (aliases.includes(query)) return 35
      return corpus.includes(query) ? 10 : 0
    },
    openGuide(slug) {
      this.clearQuerySync()
      this.safePush({ name: "helperTool", params: { guideSlug: slug } })
    },
    goToOverview() {
      this.safePush({ name: "helperTool" })
    },
    goBackToDashboard() {
      this.clearQuerySync()
      this.safePush({ name: "dashboardHome" })
    },
    openAppTarget(target) {
      if (!target) return
      this.clearQuerySync()
      this.safePush(target)
    },
    safePush(location) {
      const result = this.$router.push(location)
      if (result && typeof result.catch === "function") result.catch(() => {})
    },
    scheduleQuerySync() {
      if (this.guideSlug) return
      this.clearQuerySync()
      this.querySyncTimer = window.setTimeout(this.syncOverviewQuery, 160)
    },
    clearQuerySync() {
      if (!this.querySyncTimer) return
      window.clearTimeout(this.querySyncTimer)
      this.querySyncTimer = null
    },
    syncOverviewQuery() {
      this.querySyncTimer = null
      if (this.guideSlug) return
      const query = {}
      const cleanSearch = (this.searchQuery || "").trim()
      if (cleanSearch) query.q = cleanSearch
      if (this.selectedCategory !== "all") query.category = this.selectedCategory

      const currentQuery = this.$route.query || {}
      const isSame = (currentQuery.q || "") === (query.q || "") && (currentQuery.category || "") === (query.category || "")
      if (isSame) return

      const result = this.$router.replace({ name: "helperTool", query })
      if (result && typeof result.catch === "function") result.catch(() => {})
    },
    resetFilters() {
      this.searchQuery = ""
      this.selectedCategory = "all"
      this.$nextTick(() => {
        if (this.$refs.helpSearch && typeof this.$refs.helpSearch.focus === "function") this.$refs.helpSearch.focus()
      })
    },
    handleGlobalKeydown(event) {
      if (this.guideSlug || event.defaultPrevented || event.key !== "/") return
      const target = event.target
      const tagName = target && target.tagName ? target.tagName.toLowerCase() : ""
      if (["input", "textarea", "select"].includes(tagName) || (target && target.isContentEditable)) return
      event.preventDefault()
      if (this.$refs.helpSearch && typeof this.$refs.helpSearch.focus === "function") this.$refs.helpSearch.focus()
    },
    handleAccessStorageChange(event) {
      if (!event || event.key === "user" || event.key === "userRights") this.accessRevision += 1
    },
    handlePageChange() {
      this.$nextTick(() => {
        const scrollingSheet = document.getElementById("scrolling-sheet")
        if (scrollingSheet && typeof scrollingSheet.scrollTo === "function") scrollingSheet.scrollTo({ top: 0, behavior: "auto" })
        else window.scrollTo(0, 0)

        const requestedSection = typeof this.$route.query.section === "string" ? this.$route.query.section : ""
        const sectionTarget = requestedSection ? document.getElementById(requestedSection) : null
        if (sectionTarget && typeof sectionTarget.scrollIntoView === "function") {
          sectionTarget.scrollIntoView({ behavior: "auto", block: "start" })
          if (typeof sectionTarget.focus === "function") sectionTarget.focus({ preventScroll: true })
        } else if (this.$refs.pageTitle && typeof this.$refs.pageTitle.focus === "function") {
          this.$refs.pageTitle.focus()
        }
      })
    },
    async copyGuideLink() {
      const value = window.location.href
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(value)
        } else {
          const input = document.createElement("textarea")
          input.value = value
          input.setAttribute("readonly", "readonly")
          input.style.position = "fixed"
          input.style.opacity = "0"
          document.body.appendChild(input)
          input.select()
          document.execCommand("copy")
          document.body.removeChild(input)
        }
        if (this.$toast && typeof this.$toast.success === "function") this.$toast.success("Link kopiert")
      } catch (error) {
        if (this.$toast && typeof this.$toast.error === "function") this.$toast.error("Link konnte nicht kopiert werden")
      }
    },
  },
}
</script>

<style scoped>
.helper-tool {
  --helper-bg: #111412;
  --helper-surface: #1a1e1b;
  --helper-surface-raised: #222722;
  --helper-border: rgba(255, 255, 255, 0.09);
  --helper-text: #f4f7f5;
  --helper-muted: #9aa39d;
  --helper-green: #009640;
  --helper-green-light: #7ee5a6;
  min-height: 100%;
  color: var(--helper-text);
  background: var(--helper-bg);
}

.helper-tool button {
  font: inherit;
}

.helper-tool__header {
  position: relative;
  overflow: hidden;
  padding: 42px clamp(20px, 5vw, 72px) 38px;
  border-bottom: 1px solid var(--helper-border);
  background: linear-gradient(135deg, #171c18 0%, #101311 60%, #0d100e 100%);
}

.helper-tool__header-content {
  position: relative;
  z-index: 1;
  max-width: 1380px;
  margin: 0 auto;
}

.helper-tool__header-glow {
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(2px);
}

.helper-tool__header-glow--one {
  top: -180px;
  right: 8%;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(0, 150, 64, 0.22), rgba(0, 150, 64, 0));
}

.helper-tool__header-glow--two {
  bottom: -240px;
  left: 18%;
  width: 360px;
  height: 360px;
  background: radial-gradient(circle, rgba(126, 229, 166, 0.09), rgba(126, 229, 166, 0));
}

.helper-tool__eyebrow,
.helper-tool__meta-row,
.helper-tool__meta-pill,
.helper-tool__path,
.helper-tool__guide-category,
.helper-tool__card-link {
  display: flex;
  align-items: center;
}

.helper-tool__eyebrow {
  gap: 9px;
  margin-bottom: 14px;
  color: var(--helper-green-light);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.helper-tool__header-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 30px;
}

.helper-tool__title {
  max-width: 900px;
  margin: 0;
  color: white;
  font-size: clamp(2rem, 4vw, 3.7rem);
  font-weight: 850;
  line-height: 1.04;
  letter-spacing: -0.045em;
  outline: none;
}

.helper-tool__subtitle {
  max-width: 760px;
  margin: 14px 0 0;
  color: #b2bbb5;
  font-size: clamp(1rem, 1.5vw, 1.18rem);
  line-height: 1.6;
}

.helper-tool__header-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.helper-tool__header-button {
  min-height: 44px;
}

.helper-tool__main {
  max-width: 1380px;
  margin: 0 auto;
  padding: 34px clamp(16px, 4vw, 56px) 80px;
}

.helper-tool__state-card {
  max-width: 640px;
  margin: 70px auto;
  padding: 48px clamp(24px, 5vw, 54px);
  color: var(--helper-text);
  text-align: center;
  border: 1px solid var(--helper-border);
  border-radius: 24px;
  background: var(--helper-surface) !important;
}

.helper-tool__state-card--inline {
  margin-top: 36px;
}

.helper-tool__state-card h2 {
  margin: 20px 0 10px;
  font-size: 1.55rem;
}

.helper-tool__state-card p {
  margin: 0 auto 24px;
  color: var(--helper-muted);
  line-height: 1.6;
}

.helper-tool__state-icon,
.helper-tool__section-icon,
.helper-tool__featured-icon,
.helper-tool__function-icon,
.helper-tool__screenshot-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 229, 166, 0.16);
  background: rgba(0, 150, 64, 0.11);
}

.helper-tool__state-icon {
  width: 66px;
  height: 66px;
  border-radius: 20px;
}

.helper-tool__search-panel {
  display: grid;
  grid-template-columns: minmax(230px, 0.75fr) minmax(360px, 1.35fr);
  align-items: end;
  gap: 28px 44px;
  padding: 28px;
  border: 1px solid var(--helper-border);
  border-radius: 24px;
  background: linear-gradient(145deg, #1d221e, #171b18);
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.18);
}

.helper-tool__section-kicker,
.helper-tool__section-label {
  margin: 0;
  color: var(--helper-green-light);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.helper-tool__search-copy h2,
.helper-tool__overview-heading h2,
.helper-tool__content-section h2 {
  margin: 5px 0 0;
  color: white;
  font-size: clamp(1.4rem, 2vw, 2rem);
  letter-spacing: -0.025em;
}

.helper-tool__search >>> .v-input__slot {
  min-height: 58px;
  margin: 0;
  color: white;
  background: #0f1210 !important;
  border: 1px solid rgba(126, 229, 166, 0.14);
  box-shadow: none !important;
}

.helper-tool__search >>> input,
.helper-tool__search >>> .v-icon {
  color: white !important;
}

.helper-tool__search-stats {
  grid-column: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-top: -17px;
  color: var(--helper-muted);
  font-size: 0.82rem;
}

.helper-tool__category-section {
  margin-top: 18px;
}

.helper-tool__category-chips {
  overflow-x: auto;
  padding-bottom: 4px;
}

.helper-tool__category-chips >>> .v-chip {
  min-height: 38px;
  color: #cbd2cd;
  border: 1px solid var(--helper-border);
  background: #191d1a;
}

.helper-tool__category-chips >>> .helper-tool__category-chip--active {
  color: white;
  border-color: rgba(126, 229, 166, 0.34);
  background: rgba(0, 150, 64, 0.24) !important;
}

.helper-tool__chip-count {
  margin-left: 8px;
  color: var(--helper-green-light);
  font-size: 0.75rem;
  font-weight: 800;
}

.helper-tool__overview-section {
  margin-top: 42px;
}

.helper-tool__overview-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
}

.helper-tool__overview-heading > span {
  color: var(--helper-muted);
  font-size: 0.88rem;
}

.helper-tool__featured-grid,
.helper-tool__guide-grid,
.helper-tool__related-grid {
  display: grid;
  gap: 16px;
}

.helper-tool__featured-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.helper-tool__guide-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.helper-tool__featured-card,
.helper-tool__guide-card,
.helper-tool__related-card {
  position: relative;
  min-width: 0;
  color: var(--helper-text);
  text-align: left;
  border: 1px solid var(--helper-border);
  background: var(--helper-surface);
  transition: transform 150ms ease, border-color 150ms ease, background 150ms ease;
}

.helper-tool__featured-card:hover,
.helper-tool__guide-card:hover,
.helper-tool__related-card:hover,
.helper-tool__featured-card:focus-visible,
.helper-tool__guide-card:focus-visible,
.helper-tool__related-card:focus-visible {
  transform: translateY(-3px);
  border-color: rgba(126, 229, 166, 0.35);
  background: var(--helper-surface-raised);
  outline: none;
}

.helper-tool__featured-card {
  min-height: 260px;
  padding: 24px;
  border-radius: 22px;
}

.helper-tool__featured-icon,
.helper-tool__function-icon {
  width: 46px;
  height: 46px;
  margin-bottom: 25px;
  border-radius: 14px;
}

.helper-tool__featured-card > span,
.helper-tool__function-copy > span {
  display: block;
  color: var(--helper-green-light);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.helper-tool__featured-card strong,
.helper-tool__guide-card strong {
  display: block;
  margin-top: 7px;
  color: white;
  font-size: 1.12rem;
  line-height: 1.35;
}

.helper-tool__featured-card p,
.helper-tool__guide-card p,
.helper-tool__function-copy p {
  color: var(--helper-muted);
  line-height: 1.52;
}

.helper-tool__featured-card p {
  margin: 9px 0 34px;
}

.helper-tool__card-link {
  position: absolute;
  right: 22px;
  bottom: 20px;
  gap: 8px;
  color: var(--helper-green-light);
  font-size: 0.84rem;
  font-weight: 750;
}

.helper-tool__guide-card {
  min-height: 225px;
  padding: 22px;
  border-radius: 18px;
}

.helper-tool__guide-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: var(--helper-muted);
  font-size: 0.74rem;
}

.helper-tool__guide-category {
  gap: 6px;
  color: var(--helper-green-light);
  font-weight: 750;
}

.helper-tool__guide-card p {
  margin: 10px 0 32px;
  font-size: 0.9rem;
}

.helper-tool__function-list {
  overflow: hidden;
  border: 1px solid var(--helper-border);
  border-radius: 20px;
  background: var(--helper-surface);
}

.helper-tool__function-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--helper-border);
}

.helper-tool__function-row:last-child {
  border-bottom: 0;
}

.helper-tool__function-icon {
  margin: 0;
}

.helper-tool__function-copy strong {
  display: block;
  margin-top: 3px;
  color: white;
  font-size: 1rem;
}

.helper-tool__function-copy p {
  margin: 3px 0 5px;
  font-size: 0.86rem;
}

.helper-tool__function-path {
  color: #78817b;
  font-size: 0.76rem;
}

.helper-tool__function-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.helper-tool__breadcrumb {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 22px;
  color: var(--helper-muted);
  font-size: 0.82rem;
}

.helper-tool__breadcrumb button {
  color: var(--helper-green-light);
  border: 0;
  background: transparent;
}

.helper-tool__article-layout {
  display: grid;
  grid-template-columns: minmax(0, 820px) minmax(250px, 320px);
  align-items: start;
  justify-content: center;
  gap: 38px;
}

.helper-tool__article-body {
  min-width: 0;
}

.helper-tool__meta-row {
  flex-wrap: wrap;
  gap: 9px;
  margin-bottom: 18px;
}

.helper-tool__meta-pill {
  gap: 6px;
  padding: 7px 11px;
  color: #b6beb8;
  font-size: 0.78rem;
  border: 1px solid var(--helper-border);
  border-radius: 999px;
  background: #171b18;
}

.helper-tool__meta-pill >>> .v-icon {
  color: var(--helper-green-light);
}

.helper-tool__where-card,
.helper-tool__result-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  border: 1px solid rgba(126, 229, 166, 0.16);
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.12), rgba(0, 150, 64, 0.04));
}

.helper-tool__section-icon {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 13px;
}

.helper-tool__path {
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
  color: white;
  font-size: 0.9rem;
  font-weight: 650;
}

.helper-tool__content-section {
  margin-top: 38px;
}

.helper-tool__check-list {
  display: grid;
  gap: 10px;
  margin: 15px 0 0;
  padding: 0;
  list-style: none;
}

.helper-tool__check-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #cbd1cd;
  line-height: 1.5;
}

.helper-tool__section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.helper-tool__step-count {
  color: var(--helper-muted);
  font-size: 0.8rem;
}

.helper-tool__steps {
  display: grid;
  gap: 14px;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
}

.helper-tool__steps li {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 16px;
  padding: 20px;
  border: 1px solid var(--helper-border);
  border-radius: 17px;
  background: var(--helper-surface);
}

.helper-tool__step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  color: white;
  font-weight: 850;
  border: 1px solid rgba(126, 229, 166, 0.25);
  border-radius: 12px;
  background: rgba(0, 150, 64, 0.2);
}

.helper-tool__step-copy h3 {
  margin: 2px 0 5px;
  color: white;
  font-size: 1rem;
}

.helper-tool__step-copy p {
  margin: 0;
  color: #b6beb8;
  line-height: 1.62;
}

.helper-tool__tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  color: #cbd3ce;
  font-size: 0.82rem;
  line-height: 1.45;
  border-radius: 10px;
  background: #131714;
}

.helper-tool__visual-section {
  margin-top: 42px;
  padding-top: 4px;
}

.helper-tool__visual-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 17px;
}

.helper-tool__visual-heading h2 {
  margin: 2px 0 0;
  color: white;
  font-size: clamp(1.35rem, 2.3vw, 1.85rem);
  letter-spacing: -0.025em;
}

.helper-tool__visual-heading > div > p:last-child {
  max-width: 650px;
  margin: 7px 0 0;
  color: var(--helper-muted);
  font-size: 0.86rem;
  line-height: 1.5;
}

.helper-tool__visual-safety {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 7px;
  min-height: 34px;
  padding: 7px 11px;
  color: #c8d5cd;
  font-size: 0.75rem;
  font-weight: 750;
  border: 1px solid rgba(126, 229, 166, 0.18);
  border-radius: 999px;
  background: rgba(0, 150, 64, 0.09);
}

.helper-tool__visual-track {
  display: flex;
  align-items: stretch;
  gap: 20px;
  margin: 0 -4px;
  padding: 5px 4px 17px;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scroll-padding-inline: 4px;
  scroll-snap-type: inline mandatory;
  scrollbar-color: rgba(126, 229, 166, 0.35) rgba(255, 255, 255, 0.04);
  scrollbar-width: thin;
}

.helper-tool__visual-track::-webkit-scrollbar {
  height: 7px;
}

.helper-tool__visual-track::-webkit-scrollbar-track {
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.04);
}

.helper-tool__visual-track::-webkit-scrollbar-thumb {
  border-radius: 99px;
  background: rgba(126, 229, 166, 0.35);
}

.helper-tool__mock-device {
  position: relative;
  display: flex;
  flex: 0 0 min(390px, calc(100vw - 72px));
  flex-direction: column;
  min-width: 0;
  min-height: 470px;
  overflow: hidden;
  color: #edf3ef;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 22px;
  background: #111512;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(0, 150, 64, 0.04) inset;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  user-select: none;
}

.helper-tool__mock-device::after {
  position: absolute;
  top: -90px;
  right: -100px;
  width: 260px;
  height: 190px;
  pointer-events: none;
  content: "";
  background: radial-gradient(circle, rgba(0, 150, 64, 0.11), transparent 68%);
}

.helper-tool__mock-device--tablet {
  flex-basis: min(520px, calc(100vw - 72px));
}

.helper-tool__mock-device--desktop {
  flex-basis: min(660px, calc(100vw - 72px));
}

.helper-tool__mock-device--didactic {
  border-color: rgba(112, 165, 255, 0.27);
  background: #11151b;
}

.helper-tool__mock-device--didactic::after {
  background: radial-gradient(circle, rgba(89, 137, 229, 0.13), transparent 68%);
}

.helper-tool__mock-device--status-confirm {
  flex-basis: min(410px, calc(100vw - 72px));
}

.helper-tool__mock-browserbar {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) 54px;
  align-items: center;
  min-height: 39px;
  padding: 0 12px;
  color: #829087;
  font-size: 0.66rem;
  font-weight: 750;
  letter-spacing: 0.03em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.025);
}

.helper-tool__mock-dots {
  display: flex;
  gap: 5px;
}

.helper-tool__mock-dots span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3a433d;
}

.helper-tool__mock-dots span:first-child {
  background: #ec6a5e;
}

.helper-tool__mock-dots span:nth-child(2) {
  background: #f3bf4f;
}

.helper-tool__mock-dots span:last-child {
  background: #61c454;
}

.helper-tool__mock-browser-title {
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.helper-tool__mock-step {
  justify-self: end;
  min-width: 30px;
  padding: 3px 7px;
  text-align: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.055);
}

.helper-tool__mock-app {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 13px;
  padding: 19px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.018), transparent 38%);
}

.helper-tool__mock-app * {
  pointer-events: none;
}

.helper-tool__mock-path {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  color: #7e8a83;
  font-size: 0.67rem;
  font-weight: 650;
}

.helper-tool__mock-titlebar {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
}

.helper-tool__mock-title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(126, 229, 166, 0.15);
  border-radius: 13px;
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.18), rgba(0, 150, 64, 0.05));
}

.helper-tool__mock-titlebar > div:nth-child(2) {
  min-width: 0;
}

.helper-tool__mock-titlebar span,
.helper-tool__mock-titlebar strong,
.helper-tool__mock-titlebar small {
  display: block;
}

.helper-tool__mock-titlebar > div > span {
  margin-bottom: 2px;
  color: #7ee5a6;
  font-size: 0.62rem;
  font-weight: 850;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.helper-tool__mock-titlebar strong {
  overflow: hidden;
  color: white;
  font-size: 0.96rem;
  line-height: 1.24;
  text-overflow: ellipsis;
}

.helper-tool__mock-titlebar small {
  margin-top: 3px;
  color: #909b94;
  font-size: 0.68rem;
  line-height: 1.35;
}

.helper-tool__mock-status,
[class^="helper-tool__mock-row-status--"] {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 4px 8px;
  font-size: 0.62rem;
  font-weight: 850;
  line-height: 1.2;
  border-radius: 999px;
}

[class^="helper-tool__mock-row-status--"] {
  max-width: 150px;
  text-align: center;
  white-space: normal;
}

.helper-tool__mock-status--success,
.helper-tool__mock-row-status--success {
  color: #9df0bd;
  background: rgba(0, 150, 64, 0.18);
}

.helper-tool__mock-status--warning,
.helper-tool__mock-row-status--warning {
  color: #ffc36b;
  background: rgba(255, 152, 0, 0.16);
}

.helper-tool__mock-status--danger,
.helper-tool__mock-row-status--danger {
  color: #ff9292;
  background: rgba(255, 82, 82, 0.15);
}

.helper-tool__mock-status--info,
.helper-tool__mock-row-status--info {
  color: #9ec4ff;
  background: rgba(76, 139, 245, 0.16);
}

.helper-tool__mock-status--neutral,
.helper-tool__mock-row-status--neutral {
  color: #bbc5bf;
  background: rgba(255, 255, 255, 0.07);
}

.helper-tool__mock-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.16);
}

.helper-tool__mock-tabs > span {
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  gap: 5px;
  min-width: 0;
  padding: 7px 9px;
  overflow: hidden;
  color: #89948d;
  font-size: 0.67rem;
  font-weight: 700;
  border-radius: 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.helper-tool__mock-tabs > .helper-tool__mock-tab--active {
  color: white;
  background: rgba(0, 150, 64, 0.22);
  box-shadow: 0 0 0 1px rgba(126, 229, 166, 0.11) inset;
}

.helper-tool__mock-tabs small {
  min-width: 17px;
  padding: 2px 5px;
  color: inherit;
  text-align: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.helper-tool__mock-search {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 12px;
  color: #e4eae6;
  font-size: 0.76rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.045);
}

.helper-tool__mock-placeholder {
  color: #7f8983 !important;
  font-weight: 500 !important;
}

.helper-tool__mock-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 11px 12px;
  color: #c5cdc8;
  font-size: 0.7rem;
  line-height: 1.42;
  border: 1px solid rgba(112, 165, 255, 0.16);
  border-radius: 11px;
  background: rgba(76, 139, 245, 0.08);
}

.helper-tool__mock-notice strong,
.helper-tool__mock-notice span {
  display: block;
}

.helper-tool__mock-notice strong {
  margin-bottom: 2px;
  color: white;
}

.helper-tool__mock-notice--success {
  border-color: rgba(126, 229, 166, 0.18);
  background: rgba(0, 150, 64, 0.11);
}

.helper-tool__mock-notice--warning {
  border-color: rgba(255, 174, 66, 0.2);
  background: rgba(255, 152, 0, 0.1);
}

.helper-tool__mock-notice--danger {
  border-color: rgba(255, 82, 82, 0.2);
  background: rgba(255, 82, 82, 0.09);
}

.helper-tool__mock-progress {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px 10px;
  padding: 11px 12px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.025);
}

.helper-tool__mock-progress > div:first-child strong,
.helper-tool__mock-progress > div:first-child span {
  display: block;
}

.helper-tool__mock-progress strong {
  color: #e8eee9;
  font-size: 0.72rem;
}

.helper-tool__mock-progress span,
.helper-tool__mock-progress small {
  color: #8f9993;
  font-size: 0.63rem;
}

.helper-tool__mock-progress-track {
  grid-column: 1 / -1;
  height: 4px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.07);
}

.helper-tool__mock-progress-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #009640, #7ee5a6);
}

.helper-tool__mock-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(94px, 1fr));
  gap: 7px;
}

.helper-tool__mock-metrics > div {
  min-width: 0;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.027);
}

.helper-tool__mock-metric--success {
  border-color: rgba(126, 229, 166, 0.15) !important;
  background: rgba(0, 150, 64, 0.07) !important;
}

.helper-tool__mock-metric--warning {
  border-color: rgba(255, 152, 0, 0.15) !important;
  background: rgba(255, 152, 0, 0.06) !important;
}

.helper-tool__mock-metric--danger {
  border-color: rgba(255, 82, 82, 0.15) !important;
  background: rgba(255, 82, 82, 0.06) !important;
}

.helper-tool__mock-metrics span,
.helper-tool__mock-metrics strong,
.helper-tool__mock-metrics small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.helper-tool__mock-metrics span {
  color: #8e9892;
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.helper-tool__mock-metrics strong {
  margin-top: 4px;
  color: white;
  font-size: 0.92rem;
}

.helper-tool__mock-metrics small {
  margin-top: 2px;
  color: #98a19b;
  font-size: 0.6rem;
}

.helper-tool__mock-section-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: -5px;
  color: #dbe3de;
  font-size: 0.66rem;
  font-weight: 850;
  letter-spacing: 0.065em;
  text-transform: uppercase;
}

.helper-tool__mock-section-label small {
  color: #7f8983;
  font-size: 0.59rem;
  font-weight: 650;
  letter-spacing: 0;
  text-transform: none;
}

.helper-tool__mock-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.helper-tool__mock-device--phone .helper-tool__mock-fields,
.helper-tool__mock-device--status-confirm .helper-tool__mock-fields {
  grid-template-columns: minmax(0, 1fr);
}

.helper-tool__mock-field {
  position: relative;
  min-width: 0;
  padding: 8px 10px 9px;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.034);
}

.helper-tool__mock-field--wide {
  grid-column: 1 / -1;
}

.helper-tool__mock-field > span:first-child {
  display: block;
  margin-bottom: 4px;
  color: #89938d;
  font-size: 0.59rem;
  font-weight: 680;
}

.helper-tool__mock-field > div {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
}

.helper-tool__mock-field strong {
  overflow: hidden;
  color: #edf2ef;
  font-size: 0.7rem;
  font-weight: 650;
  line-height: 1.32;
  text-overflow: ellipsis;
}

.helper-tool__mock-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.helper-tool__mock-chips > span {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 27px;
  padding: 5px 8px;
  color: #aab3ad;
  font-size: 0.63rem;
  font-weight: 700;
  line-height: 1.2;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.035);
}

.helper-tool__mock-chips > span small {
  min-width: 25px;
  padding: 2px 5px;
  color: white;
  text-align: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.09);
}

.helper-tool__mock-chip--active,
.helper-tool__mock-chip--success {
  color: #a3f1bf !important;
  border-color: rgba(126, 229, 166, 0.2) !important;
  background: rgba(0, 150, 64, 0.14) !important;
}

.helper-tool__mock-chip--warning {
  color: #ffc879 !important;
  border-color: rgba(255, 152, 0, 0.2) !important;
  background: rgba(255, 152, 0, 0.1) !important;
}

.helper-tool__mock-chip--danger {
  color: #ff9e9e !important;
  border-color: rgba(255, 82, 82, 0.2) !important;
  background: rgba(255, 82, 82, 0.1) !important;
}

.helper-tool__mock-chips > span.helper-tool__mock-chip--switch::after {
  width: 21px;
  height: 12px;
  margin-left: 2px;
  content: "";
  border: 3px solid #69736d;
  border-radius: 999px;
  background: #69736d;
  box-shadow: -5px 0 0 0 #e2e7e3 inset;
}

.helper-tool__mock-chips > span.helper-tool__mock-chip--switch.helper-tool__mock-chip--active::after {
  border-color: #009640;
  background: #009640;
  box-shadow: 5px 0 0 0 white inset;
}

.helper-tool__mock-rows {
  display: grid;
  gap: 7px;
}

.helper-tool__mock-row {
  position: relative;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.026);
}

.helper-tool__mock-row--checked {
  border-color: rgba(126, 229, 166, 0.13);
  background: rgba(0, 150, 64, 0.06);
}

.helper-tool__mock-row-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.05);
}

.helper-tool__mock-row > div:nth-child(2) {
  min-width: 0;
}

.helper-tool__mock-row strong,
.helper-tool__mock-row span,
.helper-tool__mock-row small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.helper-tool__mock-row strong {
  color: #eef3f0;
  font-size: 0.71rem;
  line-height: 1.32;
}

.helper-tool__mock-row > div:nth-child(2) > span {
  margin-top: 2px;
  color: #929c96;
  font-size: 0.63rem;
  line-height: 1.35;
}

.helper-tool__mock-row > div:nth-child(2) > small {
  margin-top: 2px;
  color: #78827c;
  font-size: 0.59rem;
}

.helper-tool__mock-row-actions {
  display: inline-flex !important;
  gap: 4px;
  overflow: visible !important;
}

.helper-tool__mock-row-actions .v-icon {
  padding: 5px;
  color: #b8c3bc;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.055);
}

.helper-tool__mock-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: auto;
  padding-top: 3px;
}

.helper-tool__mock-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 34px;
  padding: 7px 11px;
  color: #c9d1cc;
  font-size: 0.66rem;
  font-weight: 780;
  line-height: 1.2;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.045);
}

.helper-tool__mock-button--block {
  flex: 1 0 100%;
}

.helper-tool__mock-button--primary {
  color: white;
  border-color: rgba(126, 229, 166, 0.18);
  background: #008f3c;
  box-shadow: 0 7px 18px rgba(0, 150, 64, 0.17);
}

.helper-tool__mock-button--success {
  color: white;
  border-color: rgba(126, 229, 166, 0.18);
  background: #008f3c;
}

.helper-tool__mock-button--warning {
  color: #ffd091;
  border-color: rgba(255, 152, 0, 0.2);
  background: rgba(255, 152, 0, 0.13);
}

.helper-tool__mock-button--disabled {
  color: #707a74 !important;
  border-color: rgba(255, 255, 255, 0.045) !important;
  background: rgba(255, 255, 255, 0.025) !important;
  box-shadow: none !important;
}

.helper-tool__mock-button--danger {
  color: #ff9c9c;
  border-color: rgba(255, 82, 82, 0.18);
  background: rgba(255, 82, 82, 0.1);
}

.helper-tool__mock-spotlight {
  position: relative;
  z-index: 2;
  border-color: rgba(126, 229, 166, 0.43) !important;
  box-shadow: 0 0 0 1px rgba(0, 150, 64, 0.15), 0 0 24px rgba(0, 150, 64, 0.09);
}

.helper-tool__mock-pointer {
  position: absolute;
  top: -13px;
  right: 7px;
  z-index: 5;
  display: inline-flex !important;
  align-items: center;
  gap: 4px;
  max-width: calc(100% - 14px);
  padding: 3px 6px;
  overflow: hidden;
  color: #08170e !important;
  font-size: 0.54rem !important;
  font-weight: 850 !important;
  line-height: 1.2;
  border-radius: 6px;
  background: #7ee5a6;
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.25);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.helper-tool__mock-pointer .v-icon {
  color: #08170e !important;
}

.helper-tool__mock-bars {
  display: grid;
  gap: 9px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.024);
}

.helper-tool__mock-bars > div {
  display: grid;
  grid-template-columns: minmax(95px, 0.85fr) minmax(90px, 1.5fr) auto;
  align-items: center;
  gap: 9px;
  color: #9da69f;
  font-size: 0.62rem;
}

.helper-tool__mock-bars > div > div {
  height: 6px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.065);
}

.helper-tool__mock-bars i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #009640, #7ee5a6);
}

.helper-tool__mock-bars i.helper-tool__mock-bar--orange {
  background: linear-gradient(90deg, #d87800, #ffc36b);
}

.helper-tool__mock-bars strong {
  color: #dfe6e1;
  font-size: 0.62rem;
}

.helper-tool__mock-caption {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0;
  padding: 9px 10px;
  color: #9da8a1;
  font-size: 0.64rem;
  line-height: 1.43;
  border-radius: 9px;
  background: rgba(0, 0, 0, 0.17);
}

.helper-tool__mock-footer {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  min-height: 48px;
  padding: 10px 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.025);
}

.helper-tool__mock-footer strong {
  overflow: hidden;
  color: #dbe3de;
  font-size: 0.68rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.helper-tool__mock-footer small {
  color: #6f7a73;
  font-size: 0.57rem;
  font-weight: 700;
  text-transform: uppercase;
}

.helper-tool__mock-sequence {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #8d9891;
  font-size: 0.59rem;
}

.helper-tool__mock-sequence i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #009640;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.helper-tool__mock-device--didactic .helper-tool__mock-sequence i {
  background: #6f9fe8;
  box-shadow: 0 0 0 3px rgba(111, 159, 232, 0.13);
}

.helper-tool__visual-scroll-hint {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 5px 0 0;
  color: #77817b;
  font-size: 0.7rem;
}

.helper-tool__mock-calendar {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 11px;
}

.helper-tool__mock-calendar-days {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1px;
  background: rgba(255, 255, 255, 0.05);
}

.helper-tool__mock-calendar-days span {
  padding: 7px 4px;
  text-align: center;
  background: #171c18;
}

.helper-tool__mock-calendar-days small,
.helper-tool__mock-calendar-days strong {
  display: block;
}

.helper-tool__mock-calendar-days small {
  color: #7d8881;
  font-size: 0.55rem;
}

.helper-tool__mock-calendar-days strong {
  color: #dce3df;
  font-size: 0.68rem;
}

.helper-tool__mock-calendar-day--active {
  background: rgba(0, 150, 64, 0.17) !important;
}

.helper-tool__mock-calendar-body {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  min-height: 150px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.1);
}

.helper-tool__mock-time-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  color: #737e77;
  font-size: 0.55rem;
}

.helper-tool__mock-blocks {
  position: relative;
}

.helper-tool__mock-blocks > div {
  position: relative;
  margin: 7px 0;
  padding: 7px 9px;
  border-left: 3px solid #009640;
  border-radius: 7px;
  background: rgba(0, 150, 64, 0.13);
}

.helper-tool__mock-blocks small,
.helper-tool__mock-blocks strong,
.helper-tool__mock-blocks span {
  display: block;
  font-size: 0.6rem;
}

.helper-tool__mock-blocks strong {
  color: white;
}

.helper-tool__mock-block--orange {
  border-left-color: #ff9800 !important;
  background: rgba(255, 152, 0, 0.12) !important;
}

.helper-tool__screenshot-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  min-height: 190px;
  margin-top: 20px;
  padding: 28px;
  color: var(--helper-muted);
  border: 1px dashed rgba(126, 229, 166, 0.27);
  border-radius: 18px;
  background: repeating-linear-gradient(-45deg, #171b18, #171b18 12px, #151916 12px, #151916 24px);
}

.helper-tool__screenshot-icon {
  width: 54px;
  height: 54px;
  border-radius: 16px;
}

.helper-tool__screenshot-placeholder strong,
.helper-tool__screenshot-placeholder span {
  display: block;
}

.helper-tool__screenshot-placeholder strong {
  margin-bottom: 3px;
  color: white;
}

.helper-tool__result-card {
  margin-top: 22px;
}

.helper-tool__result-card > .v-icon {
  flex: 0 0 auto;
}

.helper-tool__result-card p:last-child {
  margin: 7px 0 0;
  color: #d5dbd7;
  line-height: 1.55;
}

.helper-tool__problems {
  margin-top: 16px;
  overflow: hidden;
  border: 1px solid var(--helper-border);
  border-radius: 16px;
}

.helper-tool__problems >>> .v-expansion-panel,
.helper-tool__problems >>> .v-expansion-panel-header,
.helper-tool__problems >>> .v-expansion-panel-content__wrap {
  color: var(--helper-text) !important;
  background: var(--helper-surface) !important;
}

.helper-tool__problems >>> .v-expansion-panel-header {
  min-height: 54px;
  font-weight: 650;
}

.helper-tool__problems >>> .v-expansion-panel-content__wrap {
  color: #b8c0ba !important;
  line-height: 1.58;
}

.helper-tool__related-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 16px;
}

.helper-tool__related-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 5px 12px;
  padding: 18px;
  border-radius: 15px;
}

.helper-tool__related-card span {
  grid-column: 1 / -1;
  color: var(--helper-green-light);
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
}

.helper-tool__related-card strong {
  color: white;
  line-height: 1.4;
}

.helper-tool__article-aside {
  position: sticky;
  top: 24px;
}

.helper-tool__action-card {
  padding: 20px;
  color: var(--helper-text);
  border: 1px solid var(--helper-border);
  border-radius: 18px;
  background: var(--helper-surface) !important;
}

.helper-tool__action-card .helper-tool__section-label {
  margin-bottom: 14px;
}

@media (max-width: 1100px) {
  .helper-tool__featured-grid,
  .helper-tool__guide-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .helper-tool__article-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .helper-tool__article-aside {
    position: static;
    grid-row: 1;
  }

  .helper-tool__action-card {
    display: grid;
    grid-template-columns: minmax(150px, auto) minmax(190px, 260px) minmax(150px, 220px);
    align-items: center;
    gap: 10px;
  }

  .helper-tool__action-card .helper-tool__section-label {
    margin: 0;
  }

  .helper-tool__action-card .mt-2 {
    margin-top: 0 !important;
  }
}

@media (max-width: 959px) {
  .helper-tool__header {
    padding-top: max(82px, calc(env(safe-area-inset-top, 0px) + 70px));
  }
}

@media (max-width: 760px) {
  .helper-tool__header {
    padding: max(82px, calc(env(safe-area-inset-top, 0px) + 70px)) 18px 26px;
  }

  .helper-tool__header-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 22px;
  }

  .helper-tool__header-actions {
    width: 100%;
    overflow-x: auto;
  }

  .helper-tool__header-button {
    flex: 0 0 auto;
  }

  .helper-tool__main {
    padding: 22px 14px 70px;
  }

  .helper-tool__search-panel {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
    padding: 20px;
    border-radius: 19px;
  }

  .helper-tool__search-stats {
    grid-column: 1;
    margin-top: -7px;
  }

  .helper-tool__featured-grid,
  .helper-tool__guide-grid,
  .helper-tool__related-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .helper-tool__featured-card {
    min-height: 236px;
  }

  .helper-tool__function-row {
    grid-template-columns: auto minmax(0, 1fr);
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
  }

  .helper-tool__function-icon {
    width: 40px;
    height: 40px;
  }

  .helper-tool__function-actions {
    grid-column: 2;
    justify-content: flex-end;
  }

  .helper-tool__where-card,
  .helper-tool__result-card {
    padding: 17px;
  }

  .helper-tool__steps li {
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 12px;
    padding: 16px;
  }

  .helper-tool__step-number {
    width: 32px;
    height: 32px;
  }

  .helper-tool__screenshot-placeholder {
    align-items: flex-start;
    min-height: 150px;
    padding: 22px 16px;
  }

  .helper-tool__visual-section {
    margin-top: 34px;
  }

  .helper-tool__visual-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .helper-tool__visual-safety {
    align-self: flex-start;
  }

  .helper-tool__visual-track {
    gap: 13px;
    margin-right: -14px;
    margin-left: -14px;
    padding-right: 14px;
    padding-bottom: 15px;
    padding-left: 14px;
    scroll-padding-inline: 14px;
  }

  .helper-tool__mock-device,
  .helper-tool__mock-device--phone,
  .helper-tool__mock-device--tablet,
  .helper-tool__mock-device--desktop,
  .helper-tool__mock-device--status-confirm {
    flex-basis: min(88vw, 410px);
    min-height: 450px;
    border-radius: 19px;
  }

  .helper-tool__mock-app {
    gap: 11px;
    padding: 15px;
  }

  .helper-tool__mock-fields {
    grid-template-columns: minmax(0, 1fr);
  }

  .helper-tool__mock-titlebar {
    grid-template-columns: 38px minmax(0, 1fr);
  }

  .helper-tool__mock-title-icon {
    width: 38px;
    height: 38px;
  }

  .helper-tool__mock-titlebar > .helper-tool__mock-status {
    grid-column: 2;
    justify-self: start;
  }

  .helper-tool__mock-tabs > span {
    padding-right: 7px;
    padding-left: 7px;
    font-size: 0.61rem;
  }

  .helper-tool__mock-actions {
    justify-content: flex-start;
  }

  .helper-tool__mock-button {
    min-height: 36px;
  }

  .helper-tool__mock-bars > div {
    grid-template-columns: minmax(80px, 0.9fr) minmax(70px, 1.3fr) auto;
  }

  .helper-tool__mock-footer {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .helper-tool__mock-footer small {
    display: none;
  }

  .helper-tool__visual-scroll-hint {
    margin-top: 4px;
  }

  .helper-tool__action-card {
    display: block;
  }

  .helper-tool__action-card .helper-tool__section-label {
    margin-bottom: 14px;
  }

  .helper-tool__action-card .mt-2 {
    margin-top: 8px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .helper-tool *,
  .helper-tool *::before,
  .helper-tool *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
